import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { View, Text, ScrollView, StyleSheet, ActivityIndicator, TouchableOpacity, RefreshControl, useWindowDimensions, Image } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import ListsManager from '../services/ListsManager';
import AnalyticsService from '../services/AnalyticsService';
import ReactionModal from '../components/ReactionModal';
import ShareModal from '../components/ShareModal';
import { FaFire, FaShieldAlt, FaExclamationTriangle, FaChartLine, FaArrowUp, FaClock, FaBrain, FaTwitter, FaTelegram, FaTiktok, FaInstagram, FaGlobe, FaNewspaper, FaChevronRight, FaSearch, FaCheckCircle, FaPlayCircle, FaLanguage, FaUndo, FaChevronDown, FaSort, FaSortUp, FaSortDown, FaUsers, FaUserPlus, FaBullhorn, FaHeart, FaComment, FaInfoCircle, FaTimes, FaReply, FaShareAlt, FaEye, FaBolt, FaFlag, FaRetweet, FaExternalLinkAlt, FaListUl, FaFileAlt, FaFacebookF, FaExchangeAlt, FaSmile, FaFrown, FaMeh, FaUser, FaTv } from 'react-icons/fa';
import ReportBuilderModal from '../components/ReportBuilderModal';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';
import OrgManager from '../services/OrgManager';
import { getAuth } from 'firebase/auth';

const EMOTION_COLORS = {
    outrage: '#ff453a',
    anger: '#ff453a',
    fear: '#bf5af2',
    mockery: '#ff9f0a',
    disgust: '#ff9f0a',
    hope: '#30d158',
    joy: '#30d158',
    trust: '#64d2ff',
    celebration: '#64d2ff',
    anticipation: '#0a84ff',
    surprise: '#ffd60a',
    concern: '#ffd60a',
    sadness: '#ac8e68',
    skepticism: '#ac8e68',
    neutral: '#8e8e93',
};

let THEME = {
    primary: '#0a84ff',
    success: '#34d399',
    warning: '#fbbf24',
    error: '#f87171',
    purple: '#a78bfa',
    cyan: '#67e8f9',
    bg: '#030305',
    card: 'rgba(15, 15, 20, 0.75)',
    cardBorder: 'rgba(255, 255, 255, 0.06)',
    cardHover: 'rgba(255, 255, 255, 0.04)',
    textMain: '#f0f0f5',
    textSecondary: 'rgba(235, 235, 245, 0.55)',
    textTertiary: 'rgba(235, 235, 245, 0.25)',
    gradientStart: '#0a84ff',
    gradientEnd: '#a78bfa',
    glow: 'rgba(10, 132, 255, 0.15)',
    surface: 'rgba(255, 255, 255, 0.025)',
    // Aliases (overwritten at runtime by buildDashboardTheme)
    bgSurface: 'rgba(255, 255, 255, 0.025)',
    bgCard: 'rgba(15, 15, 20, 0.75)',
    border: 'rgba(255, 255, 255, 0.06)',
    textPrimary: '#f0f0f5',
    textMuted: 'rgba(235, 235, 245, 0.25)',
    modalBg: '#1E1E23',
};

const buildDashboardTheme = (theme) => ({
    primary: theme.accentBlue,
    success: theme.accentGreen,
    warning: theme.accentYellow,
    error: theme.accentRed,
    purple: theme.accentPurple,
    cyan: '#67e8f9',
    bg: theme.bg,
    card: theme.bgCard,
    cardBorder: theme.border,
    cardHover: theme.bgSurface,
    textMain: theme.textPrimary,
    textSecondary: theme.textSecondary,
    textTertiary: theme.textMuted,
    gradientStart: theme.accentBlue,
    gradientEnd: theme.accentPurple,
    glow: 'rgba(10, 132, 255, 0.15)',
    surface: theme.bgSurface,
    // Aliases used in inline styles
    bgSurface: theme.bgSurface,
    bgCard: theme.bgCard,
    border: theme.border,
    textPrimary: theme.textPrimary,
    textMuted: theme.textMuted,
    modalBg: theme.modalBg,
});

const PLATFORM_COLORS = {
    twitter: '#1da1f2',
    telegram: '#0088cc',
    tiktok: '#ff0050',
    instagram: '#e1306c',
    facebook_group: '#1877F2',
    rss: '#22c55e',
    webz: '#10b981',
    news: '#8e8e93',
    tv: '#9f1239',
};

const PLATFORM_ICONS = {
    twitter: FaTwitter,
    telegram: FaTelegram,
    tiktok: FaTiktok,
    instagram: FaInstagram,
    facebook_group: FaFacebookF,
    rss: FaGlobe,
    webz: FaNewspaper,
    tv: FaTv,
};

const PLATFORM_LABELS = {
    rss: 'web',
    webz: 'news',
    facebook_group: 'Facebook',
    tv: 'TV',
};

function formatSentiment(score) {
  if (score == null) return { pct: '—', label: '', color: '#8e8e93' };
  const pct = Math.round(score * 100);
  let labelKey, color;
  if (pct <= -75) { labelKey = 'app.brandDashboard.sentimentLabel.terrible'; color = '#8b0000'; }
  else if (pct <= -50) { labelKey = 'app.brandDashboard.sentimentLabel.veryNegative'; color = '#d32f2f'; }
  else if (pct <= -25) { labelKey = 'app.brandDashboard.sentimentLabel.negative'; color = '#ff453a'; }
  else if (pct <= -10) { labelKey = 'app.brandDashboard.sentimentLabel.somewhatNegative'; color = '#ff6b6b'; }
  else if (pct < 10) { labelKey = 'app.brandDashboard.sentimentLabel.neutral'; color = '#8e8e93'; }
  else if (pct < 25) { labelKey = 'app.brandDashboard.sentimentLabel.somewhatPositive'; color = '#66bb6a'; }
  else if (pct < 50) { labelKey = 'app.brandDashboard.sentimentLabel.positive'; color = '#30d158'; }
  else if (pct < 75) { labelKey = 'app.brandDashboard.sentimentLabel.veryPositive'; color = '#1b8a2e'; }
  else { labelKey = 'app.brandDashboard.sentimentLabel.excellent'; color = '#006400'; }
  const sign = pct > 0 ? '+' : '';
  return { pct: `${sign}${pct}`, label: i18next.t(labelKey), color };
}

function formatVelocity(v) {
  if (v == null) return '';
  return `${v.toFixed(1)} posts/hr`;
}

function getThresholdLabel(value, type) {
  if (value == null) return { label: '--', color: '#8e8e93' };
  const pctVal = Math.round(value * 100);
  if (pctVal <= 30) return { label: 'Low', color: type === 'credibility' ? '#8e8e93' : '#8e8e93' };
  if (pctVal <= 60) return { label: 'Medium', color: type === 'credibility' ? '#fbbf24' : '#ff9f0a' };
  return { label: 'High', color: type === 'credibility' ? '#30d158' : '#ff453a' };
}

function formatDelta(current, previous, isPercent) {
  if (current == null || previous == null || previous === 0) return null;
  const diff = current - previous;
  const pctChange = Math.round((diff / Math.abs(previous)) * 100);
  const arrow = diff > 0 ? '↑' : diff < 0 ? '↓' : '';
  const color = diff > 0 ? '#30d158' : diff < 0 ? '#ff453a' : '#8e8e93';
  return { arrow, pctChange: `${Math.abs(pctChange)}%`, color, diff };
}

class DashboardErrorBoundary extends React.Component {
    state = { hasError: false };
    static getDerivedStateFromError() { return { hasError: true }; }
    componentDidCatch(err) { console.error('Dashboard render error:', err); }
    render() {
        if (this.state.hasError) {
            const { t } = this.props;
            return (
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: THEME.bg }}>
                    <Text style={{ color: '#ff453a', fontSize: 16, marginBottom: 12 }}>{t ? t('app.common.somethingWentWrong') : 'Something went wrong'}</Text>
                    <TouchableOpacity onPress={() => this.setState({ hasError: false })} style={{ padding: 12, borderRadius: 8, backgroundColor: 'rgba(10,132,255,0.2)' }}>
                        <Text style={{ color: '#0a84ff' }}>{t ? t('app.common.retry') : 'Retry'}</Text>
                    </TouchableOpacity>
                </View>
            );
        }
        return this.props.children;
    }
}

const ACTION_ICONS = {
    counter_narrative: FaShieldAlt,
    react: FaReply,
    report: FaFlag,
    amplify: FaBullhorn,
    share: FaShareAlt,
    monitor: FaEye,
    respond_to_comments: FaComment,
    boost_post: FaBolt,
    adjust_strategy: FaChartLine,
    ignore: FaTimes,
};


function _timeAgo(isoDate) {
    const diff = Date.now() - new Date(isoDate).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

const CHART_VIEWS = [
    { key: 'overview', labelKey: 'app.brandDashboard.charts.overview', icon: FaBrain, group: 'general' },
    { key: 'list_distribution', labelKey: 'app.brandDashboard.charts.list_distribution', icon: FaListUl, group: 'general' },
    { key: 'post_activity', labelKey: 'app.brandDashboard.charts.post_activity', icon: FaChartLine, group: 'general' },
    { key: 'platform_dist', labelKey: 'app.brandDashboard.charts.platform_dist', icon: FaChartLine, group: 'general' },
    { key: 'lifecycle', labelKey: 'app.brandDashboard.charts.lifecycle', icon: FaChartLine, group: 'general' },
    { key: 'tag_distribution', labelKey: 'app.brandDashboard.charts.tag_distribution', icon: FaChartLine, group: 'topics' },
    { key: 'posts_sentiment', labelKey: 'app.brandDashboard.charts.posts_sentiment', icon: FaChartLine, group: 'topics' },
    { key: 'posts_breakdown', labelKey: 'app.brandDashboard.charts.posts_breakdown', icon: FaChartLine, group: 'topics' },
    { key: 'hashtags', labelKey: 'app.brandDashboard.charts.hashtags', icon: FaTiktok, group: 'topics' },
    // NER-based entity extraction — temporarily disabled, data quality insufficient
    // { key: 'entities', labelKey: 'app.brandDashboard.charts.entities', icon: FaGlobe, group: 'topics' },
    { key: 'word_cloud', labelKey: 'app.brandDashboard.charts.word_cloud', icon: FaComment, group: 'topics' },
    { key: 'sentiment_timeline', labelKey: 'app.brandDashboard.charts.sentiment_timeline', icon: FaChartLine, group: 'sentiment' },
    { key: 'sentiment_emotion', labelKey: 'app.brandDashboard.charts.sentiment_emotion', icon: FaChartLine, group: 'sentiment' },
    { key: 'platform_sentiment', labelKey: 'app.brandDashboard.charts.platform_sentiment', icon: FaChartLine, group: 'sentiment' },
    { key: 'extreme_posts', labelKey: 'app.brandDashboard.charts.extreme_posts', icon: FaExclamationTriangle, group: 'sentiment' },
    { key: 'top_influencers', labelKey: 'app.brandDashboard.charts.top_influencers', icon: FaUsers, group: 'sources' },
    { key: 'top_sources', labelKey: 'app.brandDashboard.charts.top_sources', icon: FaUsers, group: 'sources' },
    { key: 'key_voices', labelKey: 'app.brandDashboard.charts.key_voices', icon: FaBullhorn, group: 'sources' },
    { key: 'most_engaging', labelKey: 'app.brandDashboard.charts.most_engaging', icon: FaBolt, group: 'general' },
    { key: 'sentiment_pie', labelKey: 'app.brandDashboard.charts.sentiment_pie', icon: FaChartLine, group: 'sentiment' },
    { key: 'competitive_benchmarking', labelKey: 'app.brandDashboard.charts.competitive_benchmarking', icon: FaExchangeAlt, group: 'competitors' },
    { key: 'share_of_voice', labelKey: 'app.brandDashboard.charts.share_of_voice', icon: FaChartLine, group: 'competitors' },
    { key: 'head_to_head_sentiment', labelKey: 'app.brandDashboard.charts.head_to_head_sentiment', icon: FaExchangeAlt, group: 'competitors' },
    { key: 'engagement_benchmarking', labelKey: 'app.brandDashboard.charts.engagement_benchmarking', icon: FaBolt, group: 'competitors' },
    { key: 'competitor_mentions', labelKey: 'app.brandDashboard.charts.competitor_mentions', icon: FaExclamationTriangle, group: 'competitors' },
    { key: 'platform_strongholds', labelKey: 'app.brandDashboard.charts.platform_strongholds', icon: FaUsers, group: 'competitors' },
    { key: 'emotion_fingerprint', labelKey: 'app.brandDashboard.charts.emotion_fingerprint', icon: FaChartLine, group: 'competitors' },
];

const TEMPLATE_TABS = [
    { key: 'topics', labelKey: 'app.brandDashboard.templates.topics', icon: FaListUl },
    { key: 'general', labelKey: 'app.brandDashboard.templates.general', icon: FaSearch },
    { key: 'sentiment', labelKey: 'app.brandDashboard.templates.sentiment', icon: FaChartLine },
    { key: 'competitors', labelKey: 'app.brandDashboard.templates.competitors', icon: FaExchangeAlt },
    { key: 'sources', labelKey: 'app.brandDashboard.templates.sources', icon: FaUsers },
];

const dashboardCSS = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
    @keyframes pulse-trending { 0% { transform: scale(1); opacity: 0.8; } 50% { transform: scale(1.15); opacity: 1; } 100% { transform: scale(1); opacity: 0.8; } }
    @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
    @keyframes shimmerSkeleton { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
    @keyframes borderGradient { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
    @keyframes sectionReveal { from { opacity: 0; transform: translateY(24px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
    @keyframes pathDraw { from { stroke-dashoffset: var(--path-length); } to { stroke-dashoffset: 0; } }
    @keyframes areaFadeIn { from { opacity: 0; } to { opacity: 1; } }
    .dash-entrance { animation: fadeSlideUp 0.5s ease-out both; }
    .dash-entrance-1 { animation-delay: 0.05s; } .dash-entrance-2 { animation-delay: 0.1s; }
    .section-reveal { opacity: 0; transform: translateY(24px) scale(0.98); }
    .section-reveal.visible { animation: sectionReveal 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
    .trending-pulsar { animation: pulse-trending 2s infinite ease-in-out; }
    .hover-arrow { transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1); opacity: 0.4; }
    .narrative-card-inner { transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); border-radius: 14px; }
    .narrative-card-inner:hover { background-color: rgba(255,255,255,0.035); }
    .narrative-card-inner:hover .hover-arrow { transform: translateX(5px); opacity: 1; }
    .stat-card-wrap { position: relative; border-radius: 20px; transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease; }
    .stat-card-wrap:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(10, 132, 255, 0.08); }
    .section-card-wrap { transition: box-shadow 0.3s ease; }
    .section-card-wrap:hover { box-shadow: 0 4px 24px rgba(0,0,0,0.3); }
    .table-row-hover { transition: background-color 0.15s ease; }
    .table-row-hover:hover { background-color: rgba(255,255,255,0.025) !important; }
    * { font-family: 'Sora', -apple-system, BlinkMacSystemFont, sans-serif; }
    .mono-value { font-family: 'DM Mono', 'SF Mono', monospace; }
    ::-webkit-scrollbar { width: 4px; height: 4px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.2); border-radius: 2px; }
`;

const BrandDashboardInner = () => {
    const { t, i18n } = useTranslation();
    const { theme: globalTheme } = useTheme();
    Object.assign(THEME, buildDashboardTheme(globalTheme));
    styles = getStyles();
    const { width } = useWindowDimensions();
    const isDesktop = width >= 1024;
    const isMobile = width < 768;
    const navigate = useNavigate();
    const { activeOrg } = useOrg();

    const authUser = getAuth().currentUser;
    const firstName = (authUser?.displayName || authUser?.email?.split('@')[0] || '').split(' ')[0];
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 12) return t('app.brandDashboard.greetingMorning');
        if (hour >= 12 && hour < 17) return t('app.brandDashboard.greetingAfternoon');
        if (hour >= 17 && hour < 21) return t('app.brandDashboard.greetingEvening');
        return t('app.brandDashboard.greetingNight');
    };

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [globalRange, setGlobalRange] = useState('7d');
    const [campaignsPreview, setCampaignsPreview] = useState([]);
    const [activeTemplate, setActiveTemplate] = useState('topics');
    const centerScrollRef = React.useRef(null);
    const [entityModal, setEntityModal] = useState(null);
    const [hashtagModal, setHashtagModal] = useState(null);
    const [narrativeModal, setNarrativeModal] = useState(null);
    const [engagementModal, setEngagementModal] = useState(null);
    const [emotionModal, setEmotionModal] = useState(null);
    const [tagModal, setTagModal] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);
    const [wordCloudModal, setWordCloudModal] = useState(null);
    const [competitorModal, setCompetitorModal] = useState(null);
    const [platformModal, setPlatformModal] = useState(null);
    const [sentimentModal, setSentimentModal] = useState(null);
    const [authorModal, setAuthorModal] = useState(null);
    const [wordCloudData, setWordCloudData] = useState(null);
    // Lazy-loaded dashboard tab sections (General/Topics/Competitive split out of core /dashboard)
    const [loadedSections, setLoadedSections] = useState({});
    const [loadingSection, setLoadingSection] = useState(null);
    const [rightW, setRightW] = useState(280);
    const [campaignComments, setCampaignComments] = useState(null);
    const rightDragRef = React.useRef(null);
    const rightDragStartRef = React.useRef(null);

    const fetchDashboard = useCallback(async () => {
        try {
            setError(null);
            // Core load — reset any lazily-loaded tab sections so they refetch for the new org/range.
            setLoadedSections({});
            setLoadingSection(null);
            inFlightSectionsRef.current.clear();
            const [result, campaigns, wordCloud] = await Promise.all([
                ListsManager.fetchBrandDashboard(activeOrg?.id, globalRange),
                ListsManager.fetchCampaignPreview(activeOrg?.id).catch(() => []),
                ListsManager.fetchWordCloud(activeOrg?.id, globalRange).catch(() => null),
            ]);
            if (result) {
                setData(result);
            } else {
                setError(t('app.brandDashboard.failedToLoad'));
            }
            if (campaigns) setCampaignsPreview(campaigns);
            if (wordCloud) setWordCloudData(wordCloud);
        } catch (e) {
            console.error('Dashboard fetch error:', e);
            setError(t('app.brandDashboard.failedToLoad'));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [activeOrg?.id, globalRange]);

    useEffect(() => { fetchDashboard(); AnalyticsService.logActivity('view_brand_dashboard'); }, [fetchDashboard]);

    // Map a template tab to its lazy backend section. Sentiment & Sources data ship in the core
    // /dashboard payload, so they have no separate fetch.
    const SECTION_FOR_TAB = { general: 'general', topics: 'topics', competitors: 'competitive' };
    // Tracks sections whose fetch is currently in flight, so rapid tab-switching can't fire
    // a duplicate request before the first one resolves (loadedSections is only set on completion).
    const inFlightSectionsRef = React.useRef(new Set());

    const loadSection = useCallback(async (tabKey) => {
        const section = SECTION_FOR_TAB[tabKey];
        if (!section || loadedSections[section] || inFlightSectionsRef.current.has(section)) return;
        inFlightSectionsRef.current.add(section);
        setLoadingSection(section);
        try {
            const res = await ListsManager.fetchDashboardSection(section, activeOrg?.id, globalRange);
            if (res) {
                setData(prev => ({ ...(prev || {}), ...res }));
                setLoadedSections(prev => ({ ...prev, [section]: true }));
            }
        } finally {
            inFlightSectionsRef.current.delete(section);
            setLoadingSection(s => (s === section ? null : s));
        }
    }, [activeOrg?.id, globalRange, loadedSections]);

    // When the active tab needs a lazy section, fetch it once the core data is present.
    useEffect(() => {
        if (!data) return;
        loadSection(activeTemplate);
    }, [activeTemplate, data, loadSection]);

    useEffect(() => {
        if (campaignsPreview && campaignsPreview.length > 0) {
            ListsManager.fetchCampaignCommentsOverview(campaignsPreview[0].id)
                .then(result => { if (result) setCampaignComments({ campaign: campaignsPreview[0], ...result }); })
                .catch(() => {});
        }
    }, [campaignsPreview]);

    const onRefresh = useCallback(() => {
        setRefreshing(true);
        fetchDashboard();
    }, [fetchDashboard]);

    const startRightDrag = (e) => {
        e.preventDefault();
        rightDragRef.current = true;
        rightDragStartRef.current = { x: e.clientX, rightW };
        const onMove = (mv) => {
            const rawDx = rightDragStartRef.current.x - mv.clientX;
            const dx = i18n.language === 'he' ? -rawDx : rawDx;
            setRightW(Math.max(220, Math.min(500, rightDragStartRef.current.rightW + dx)));
        };
        const onUp = () => {
            rightDragRef.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const clearAllModals = () => { setEntityModal(null); setHashtagModal(null); setNarrativeModal(null); setEngagementModal(null); setEmotionModal(null); setTagModal(null); setWordCloudModal(null); setCompetitorModal(null); setPlatformModal(null); setSentimentModal(null); setAuthorModal(null); };

    const detailPanelActive = !!(entityModal || hashtagModal || narrativeModal || engagementModal || emotionModal || tagModal || wordCloudModal || competitorModal || platformModal || sentimentModal || authorModal);

    const filteredCharts = CHART_VIEWS.filter(c => c.group === activeTemplate);

    if (loading) {
        const skBase = THEME.bgCard || 'rgba(28,28,30,0.7)';
        const skHighlight = THEME.bgSurface || 'rgba(58,58,60,0.8)';
        const skShine = THEME.primary ? THEME.primary + '18' : 'rgba(10,132,255,0.09)';
        const skeletonStyle = (w, h, radius = 8, mb = 0, delay = 0) => ({
            width: w, height: h, borderRadius: radius, marginBottom: mb,
            position: 'relative', overflow: 'hidden',
            backgroundColor: skBase,
        });
        const skeletonBlock = (w, h, radius = 8, mb = 0, delay = 0) => (
            <div style={skeletonStyle(w, h, radius, mb, delay)}>
                <div style={{
                    position: 'absolute', inset: 0,
                    background: `linear-gradient(90deg, transparent 0%, ${skHighlight} 20%, ${skShine} 50%, ${skHighlight} 80%, transparent 100%)`,
                    backgroundSize: '200% 100%',
                    animation: `shimmerSkeleton 2s ease-in-out infinite`,
                    animationDelay: `${delay}s`,
                }} />
            </div>
        );
        const skeletonCard = (i) => (
            <div key={i} style={{ padding: 20, borderRadius: 16, border: `1px solid ${THEME.cardBorder || 'rgba(255,255,255,0.06)'}`, background: THEME.bgCard || 'rgba(28,28,30,0.7)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    {skeletonBlock(14, 14, 4, 0, i * 0.15)}
                    {skeletonBlock(120, 14, 6, 0, i * 0.15 + 0.05)}
                </div>
                {i % 3 === 0 && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                        {skeletonBlock(160, 160, 80, 0, i * 0.15)}
                    </div>
                )}
                {i % 3 === 1 && (
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 100, paddingTop: 20 }}>
                        {[60, 85, 45, 70, 90, 55, 75].map((h, j) => (
                            <div key={j} style={{ ...skeletonStyle('100%', `${h}%`, 4, 0, j * 0.08), flex: 1 }}>
                                <div style={{
                                    position: 'absolute', inset: 0,
                                    background: `linear-gradient(90deg, transparent 0%, ${skHighlight} 20%, ${skShine} 50%, ${skHighlight} 80%, transparent 100%)`,
                                    backgroundSize: '200% 100%',
                                    animation: `shimmerSkeleton 2s ease-in-out infinite`,
                                    animationDelay: `${j * 0.1}s`,
                                }} />
                            </div>
                        ))}
                    </div>
                )}
                {i % 3 === 2 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {[100, 80, 60].map((w, j) => (
                            <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {skeletonBlock(28, 28, 14, 0, j * 0.12)}
                                {skeletonBlock(`${w}%`, 10, 5, 0, j * 0.12 + 0.05)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
        return (
            <View style={[styles.loadingContainer, { justifyContent: 'flex-start', paddingTop: 60, paddingHorizontal: 24 }]}>
                <style dangerouslySetInnerHTML={{ __html: `@keyframes shimmerSkeleton { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } } @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }` }} />
                <div style={{ width: '100%', maxWidth: 800 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
                        {skeletonBlock(200, 20, 8, 0, 0)}
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                        {[0,1,2].map(i => (
                            <div key={i} style={{ flex: 1, padding: 16, borderRadius: 14, border: `1px solid ${THEME.cardBorder || 'rgba(255,255,255,0.06)'}`, background: THEME.bgCard || 'rgba(28,28,30,0.7)' }}>
                                {skeletonBlock('60%', 10, 5, 10, i * 0.1)}
                                {skeletonBlock('40%', 22, 6, 0, i * 0.1 + 0.05)}
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {[0,1,2].map(i => skeletonCard(i))}
                    </div>
                </div>
                <div style={{ position: 'absolute', bottom: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <Text style={styles.loadingText}>{t('app.brandDashboard.loadingInsights')}</Text>
                    <div style={{ width: 120, height: 2, borderRadius: 1, overflow: 'hidden', background: 'rgba(255,255,255,0.06)' }}>
                        <div style={{ width: '40%', height: '100%', borderRadius: 1, background: `linear-gradient(90deg, ${THEME.primary}, ${THEME.purple})`, animation: 'shimmer 1.5s ease infinite', backgroundSize: '200% 100%' }} />
                    </div>
                </div>
            </View>
        );
    }

    if (error || !data) {
        return (
            <View style={styles.loadingContainer}>
                <Text style={styles.errorText}>{error || t('app.brandDashboard.noDataAvailable')}</Text>
                <TouchableOpacity style={styles.retryButton} onPress={fetchDashboard}>
                    <Text style={styles.retryText}>{t('app.brandDashboard.retry')}</Text>
                </TouchableOpacity>
            </View>
        );
    }

    // ── Render a single chart section by key ──
    const renderChartByKey = (chartKey) => {
        switch (chartKey) {
            case 'overview':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        {data.engagement_totals && <EngagementKPIStrip data={data} isDesktop={isDesktop} globalRange={globalRange} onStatClick={(sort) => { clearAllModals(); setEngagementModal(sort); }} />}
                        <NarrativesHub data={data} navigate={navigate} />
                    </div>
                );
            case 'posts_breakdown':
                return data.narrative_topic_distribution?.length > 0
                    ? <SectionCard title={t('app.brandDashboard.postsBreakdown')} info={t('app.brandDashboard.postsBreakdownInfo')}><TopicDonutChart distribution={data.narrative_topic_distribution} navigate={navigate} onNarrativeClick={(id, title) => { clearAllModals(); setNarrativeModal({ id, title }); }} /></SectionCard>
                    : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noTopicData')}</div>;
            case 'posts_sentiment':
                return data.narrative_sentiment_breakdown?.length > 0
                    ? <SectionCard title={t('app.brandDashboard.postsSentimentByTopic')} info={t('app.brandDashboard.postsSentimentInfo')}><SentimentStackedBar breakdown={data.narrative_sentiment_breakdown} onNarrativeClick={(id, title) => { clearAllModals(); setNarrativeModal({ id, title }); }} /></SectionCard>
                    : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noSentimentBreakdown')}</div>;
            case 'post_activity':
                return (data.hourly_activity && data.hourly_activity.length > 0) || (data.daily_activity_7d && data.daily_activity_7d.length > 0) ? (
                    <SectionCard title={t('app.brandDashboard.postActivity')} info={t('app.brandDashboard.postActivityInfo')}>
                        <HourlyChart hours={data.daily_activity_7d && data.daily_activity_7d.length > 0 ? data.daily_activity_7d : data.hourly_activity} mode={data.daily_activity_7d && data.daily_activity_7d.length > 0 ? '7d' : '24h'} sentimentOverlay={data.post_sentiment_timeline_7d || data.post_sentiment_timeline || null} />
                    </SectionCard>
                ) : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noActivityData')}</div>;
            case 'sentiment_timeline':
                return <SentimentTimelineSection data={data} />;
            case 'sentiment_emotion':
                return (
                    <SectionCard title={t('app.brandDashboard.sentimentAndEmotion')} info={t('app.brandDashboard.sentimentAndEmotionInfo')}>
                        {(data.post_emotion_distribution && Object.keys(data.post_emotion_distribution).length > 0) || (data.emotion_distribution && Object.keys(data.emotion_distribution).length > 0) ? (
                            <>
                                {data.post_emotion_distribution && Object.keys(data.post_emotion_distribution).length > 0
                                    ? <EmotionPieChart emotions={data.post_emotion_distribution} label={t('app.brandDashboard.postEmotionBreakdown')} onEmotionClick={(emotion, listId) => { clearAllModals(); setEmotionModal({ emotion, listId }); }} />
                                    : <EmotionPieChart emotions={data.emotion_distribution} onEmotionClick={(emotion, listId) => { clearAllModals(); setEmotionModal({ emotion, listId }); }} />}
                            </>
                        ) : <Text style={styles.noDataText}>{t('app.brandDashboard.enrichmentLoading')}</Text>}
                    </SectionCard>
                );
            case 'platform_sentiment':
                return <PlatformSentimentSection data={data} onPlatformClick={(platform) => { clearAllModals(); setPlatformModal({ platform }); }} />;
            case 'extreme_posts':
                return ((data.most_negative_posts && data.most_negative_posts.length > 0) || (data.most_positive_posts && data.most_positive_posts.length > 0))
                    ? <ExtremePostsList negativePosts={data.most_negative_posts || []} positivePosts={data.most_positive_posts || []} orgId={activeOrg?.id} />
                    : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noExtremePosts')}</div>;
            case 'platform_dist':
                return data.platform_distribution && Object.keys(data.platform_distribution).length > 0
                    ? <SectionCard title={t('app.brandDashboard.platformDistribution')} info={t('app.brandDashboard.platformDistributionInfo')}><PlatformBars platforms={data.platform_distribution} onPlatformClick={(platform) => { clearAllModals(); setPlatformModal({ platform }); }} /></SectionCard>
                    : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noPlatformData')}</div>;
            case 'tag_distribution':
                return data.tag_distribution && data.tag_distribution.length > 0
                    ? <SectionCard title={t('app.brandDashboard.tagDistribution')} info={t('app.brandDashboard.tagDistributionInfo')}><TagPieChart distribution={data.tag_distribution} onTagClick={(tag) => { clearAllModals(); setTagModal(tag); }} /></SectionCard>
                    : null;
            case 'hashtags':
                return data.top_hashtags && data.top_hashtags.length > 0
                    ? <SectionCard title={t('app.brandDashboard.trendingHashtags')} icon={<FaTiktok size={14} color="#ff0050" />} info={t('app.brandDashboard.trendingHashtagsInfo')}><HashtagCloud hashtags={data.top_hashtags} onHashtagClick={(tag) => { clearAllModals(); setHashtagModal(tag); }} /></SectionCard>
                    : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noHashtagData')}</div>;
            case 'top_sources':
                return ((data.top_sources && data.top_sources.length > 0) || (data.top_sources_7d && data.top_sources_7d.length > 0))
                    ? <SectionCard title={t('app.brandDashboard.topSources')} icon={<FaUsers size={16} color={THEME.primary} />} info={t('app.brandDashboard.topSourcesInfo')}><SourcesList sources24h={data.top_sources || []} sources7d={data.top_sources_7d || []} sourceTotals={data.source_totals} onSourceClick={(username, source) => { clearAllModals(); setAuthorModal({ username, source }); }} /></SectionCard>
                    : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noSourceData')}</div>;
            case 'key_voices':
                return data.top_voices && data.top_voices.length > 0
                    ? <SectionCard title={t('app.brandDashboard.keyVoices')} icon={<FaBullhorn size={14} color="#d9a5ff" />} info={t('app.brandDashboard.keyVoicesInfo')}><KeyVoices voices={data.top_voices} onVoiceClick={(username, source) => { clearAllModals(); setAuthorModal({ username, source }); }} /></SectionCard>
                    : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noVoiceData')}</div>;
            case 'entities':
                // NER-based entity extraction — temporarily disabled, data quality insufficient
                return null;
            case 'word_cloud':
                return wordCloudData?.words?.length > 0
                    ? <SectionCard title={t('app.brandDashboard.contextOfDiscussion')} info={t('app.brandDashboard.contextOfDiscussionInfo')}><DiscussionWordCloud words={wordCloudData.words} onWordClick={(word) => { clearAllModals(); setWordCloudModal(word); }} /></SectionCard>
                    : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noWordCloudData')}</div>;
            case 'list_distribution':
                return data.list_distribution && data.list_distribution.length > 0
                    ? <SectionCard title={t('app.brandDashboard.listDistribution')} info={t('app.brandDashboard.listDistributionInfo')}><ListDistributionChart distribution={data.list_distribution} timeline={data.list_distribution_timeline} /></SectionCard>
                    : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noListData')}</div>;
            case 'lifecycle':
                return data.stage_distribution
                    ? <SectionCard title={t('app.brandDashboard.narrativeLifecycle')} info={t('app.brandDashboard.narrativeLifecycleInfo')}><StageDistribution stages={data.stage_distribution} total={data.total_narratives} narratives={[...(data.hottest_narratives || []), ...(data.early_warnings || [])]} onNarrativeClick={(id) => navigate('/trends', { state: { highlightNarrativeId: id } })} /></SectionCard>
                    : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noLifecycleData')}</div>;
            case 'top_influencers':
                return (data.top_sources_7d && data.top_sources_7d.length > 0)
                    ? <SectionCard title={t('app.brandDashboard.topInfluencers')} icon={<FaUsers size={16} color={THEME.primary} />} info={t('app.brandDashboard.topInfluencersInfo')}>
                        <TopInfluencersTable sources={data.top_sources_7d} onSourceClick={(username, source) => { clearAllModals(); setAuthorModal({ username, source }); }} />
                      </SectionCard>
                    : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noSourceData')}</div>;
            case 'most_engaging':
                return <MostEngagingPostsSection orgId={activeOrg?.id} />;
            case 'sentiment_pie':
                return (data.sentiment_distribution || data.sentiment_distribution_7d)
                    ? <SectionCard title={t('app.brandDashboard.sentimentOverview')} info={t('app.brandDashboard.sentimentOverviewInfo')}>
                        <SentimentDonutChart dist24h={data.sentiment_distribution} dist7d={data.sentiment_distribution_7d} onSentimentClick={(sentiment) => { clearAllModals(); setSentimentModal(sentiment); }} />
                      </SectionCard>
                    : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noSentimentData')}</div>;
            case 'competitive_benchmarking':
                return null;
            case 'share_of_voice':
                return data.competitive_analysis?.per_list_comparison?.length > 0
                    ? <ShareOfVoiceSection data={data} />
                    : null;
            case 'head_to_head_sentiment':
                return data.competitive_analysis?.per_list_comparison?.length > 0
                    ? <HeadToHeadSentimentSection data={data} />
                    : null;
            case 'engagement_benchmarking':
                return data.competitive_analysis?.engagement_benchmarking?.length > 0
                    ? <EngagementBenchmarkingSection data={data} />
                    : null;
            case 'competitor_mentions':
                return data.competitive_analysis?.competitor_mentions?.length > 0
                    ? <CompetitorMentionsSection data={data} onCompetitorClick={(name, compId) => { clearAllModals(); setCompetitorModal({ name, competitor_id: compId }); }} />
                    : null;
            case 'platform_strongholds':
                return data.competitive_analysis?.platform_strongholds && Object.keys(data.competitive_analysis.platform_strongholds).length > 0
                    ? <PlatformStrongholdsSection data={data} onPlatformClick={(platform, listId) => { clearAllModals(); setPlatformModal({ platform, listId }); }} />
                    : null;
            case 'emotion_fingerprint':
                return data.competitive_analysis?.emotion_fingerprints && Object.keys(data.competitive_analysis.emotion_fingerprints).length > 0
                    ? <EmotionFingerprintSection data={data} onEmotionClick={(emotion, listId) => { clearAllModals(); setEmotionModal({ emotion, listId }); }} />
                    : null;
            default:
                return null;
        }
    };

    // ── Right sidebar context content ──
    const renderRightPanel = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Early Warnings */}
            {data.early_warnings && data.early_warnings.length > 0 && (
                <div style={{ padding: '0 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                        <FaExclamationTriangle size={12} color="#ffd60a" />
                        <span style={{ color: THEME.textMain, fontSize: 12, fontWeight: 700 }}>{t('app.brandDashboard.earlyWarnings')}</span>
                    </div>
                    {data.early_warnings.slice(0, 3).map(n => (
                        <WarningCard key={n.id} narrative={n} onPress={() => navigate('/trends', { state: { highlightNarrativeId: n.id } })} />
                    ))}
                </div>
            )}


            {/* Themes */}
            {data.top_themes && data.top_themes.length > 0 && (
                <div style={{ padding: '0 16px' }}>
                    <div style={{ color: THEME.textTertiary, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>{t('app.brandDashboard.trendingThemes')}</div>
                    {data.top_themes.slice(0, 5).map((th, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${THEME.cardBorder}` }}>
                            <span style={{ color: THEME.textMain, fontSize: 12, fontWeight: 500, flex: 1 }} title={th.theme}>{th.theme.length > 35 ? th.theme.slice(0, 33) + '…' : th.theme}</span>
                            <span className="mono-value" style={{ color: THEME.textMuted, fontSize: 11 }}>{th.count}</span>
                        </div>
                    ))}
                </div>
            )}

        </div>
    );

    // ── MOBILE: falls back to scrollable single column ──
    if (isMobile) {
        return (
            <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}
                refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#0a84ff" />}>
                <style dangerouslySetInnerHTML={{ __html: dashboardCSS }} />
                <svg style={{ height: 0, width: 0, position: 'absolute' }}><defs>
                    <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={THEME.primary} /><stop offset="100%" stopColor={THEME.cyan} /></linearGradient>
                    <linearGradient id="areaGrad" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor={THEME.primary} stopOpacity="0.2" /><stop offset="100%" stopColor={THEME.primary} stopOpacity="0" /></linearGradient>
                </defs></svg>
                {filteredCharts.map(chart => {
                    const content = renderChartByKey(chart.key);
                    if (!content) return null;
                    return <div key={chart.key} id={`section-${chart.key}`} style={{ marginBottom: 20 }}>{content}</div>;
                })}
            </ScrollView>
        );
    }

    // ── DESKTOP: 3-column layout ──
    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: dashboardCSS + `
                .bi-sidebar-item { transition: all 0.15s; cursor: pointer; }
                .bi-sidebar-item:hover { background: ${THEME.surface} !important; }
                .bi-divider { width: 5px; flex-shrink: 0; cursor: col-resize; background: transparent; position: relative; z-index: 10; transition: background 0.15s; }
                .bi-divider:hover, .bi-divider:active { background: ${THEME.primary}55; }
                .bi-divider::after { content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 1px; height: 32px; background: ${THEME.cardBorder}; border-radius: 1px; }
            `}} />
            <svg style={{ height: 0, width: 0, position: 'absolute' }}><defs>
                <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={THEME.primary} /><stop offset="100%" stopColor={THEME.cyan} /></linearGradient>
                <linearGradient id="areaGrad" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor={THEME.primary} stopOpacity="0.2" /><stop offset="100%" stopColor={THEME.primary} stopOpacity="0" /></linearGradient>
            </defs></svg>

            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: THEME.bg, overflow: 'hidden' }}>

                {/* ── Top Header ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 24px', borderBottom: `1px solid ${THEME.cardBorder}`, flexShrink: 0 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${THEME.primary}20, ${THEME.purple}20)`, border: `1px solid ${THEME.primary}30`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FaBrain size={16} color={THEME.primary} />
                    </div>
                    <div>
                        <div style={{ color: THEME.textMain, fontSize: 22, fontWeight: 700, letterSpacing: -0.5 }}>{getGreeting()}{firstName ? `, ${firstName}` : ''}</div>
                        <div style={{ color: THEME.textTertiary, fontSize: 11, marginTop: 1 }}>
                            {data.total_narratives || 0} {t('app.brandDashboard.narrativesCount')} · {data.trending_count || 0} {t('app.brandDashboard.trendingCount')}
                        </div>
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${THEME.cardBorder}` }}>
                            {['24h', '7d', '30d'].map(r => (
                                <button key={r} onClick={() => setGlobalRange(r)} style={{
                                    padding: '6px 12px', border: 'none', cursor: 'pointer',
                                    background: globalRange === r ? THEME.primary + '20' : 'transparent',
                                    color: globalRange === r ? THEME.primary : THEME.textSecondary,
                                    fontSize: 11, fontWeight: globalRange === r ? 700 : 500,
                                }}>{r.toUpperCase()}</button>
                            ))}
                        </div>
                        <button onClick={() => setShowReportModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.purple})`, border: 'none', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            <FaFileAlt size={11} />
                            <span>{t('app.brandDashboard.generateReport')}</span>
                        </button>
                        <button onClick={onRefresh} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, background: THEME.surface, border: `1px solid ${THEME.cardBorder}`, color: THEME.textSecondary, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            {refreshing ? <ActivityIndicator size="small" color={THEME.primary} /> : <FaChartLine size={11} color={THEME.textTertiary} />}
                            <span>{t('app.brandDashboard.refresh')}</span>
                        </button>
                    </div>
                </div>

                {/* ── Templates row ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 24px', borderBottom: `1px solid ${THEME.cardBorder}`, flexShrink: 0, overflowX: 'auto' }}>
                    {TEMPLATE_TABS.map(tab => {
                        const Icon = tab.icon;
                        const isActive = activeTemplate === tab.key;
                        return (
                            <div key={tab.key} className="bi-sidebar-item" onClick={() => {
                                setActiveTemplate(tab.key);
                                if (centerScrollRef.current) centerScrollRef.current.scrollTop = 0;
                            }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '7px 14px', borderRadius: 8,
                                    background: isActive ? THEME.surface : 'transparent',
                                    border: `1px solid ${isActive ? THEME.primary + '40' : THEME.cardBorder}`,
                                }}>
                                <Icon size={13} color={isActive ? THEME.primary : THEME.textTertiary} />
                                <span style={{ fontSize: 12, fontWeight: 600, color: isActive ? THEME.textMain : THEME.textSecondary, whiteSpace: 'nowrap' }}>{t(tab.labelKey)}</span>
                            </div>
                        );
                    })}
                </div>

                {/* ── Sub-nav pill row ── */}
                <div style={{ display: 'flex', gap: 6, padding: '6px 20px', borderBottom: `1px solid ${THEME.cardBorder}`, overflowX: 'auto', flexShrink: 0 }}>
                    {filteredCharts.map(chart => (
                        <TouchableOpacity key={chart.key} onPress={() => {
                            const el = document.getElementById(`section-${chart.key}`);
                            if (el && centerScrollRef.current) {
                                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                        }}
                            style={{
                                paddingHorizontal: 14, paddingVertical: 6, borderRadius: 20,
                                backgroundColor: THEME.surface,
                                border: `1px solid ${THEME.cardBorder}`,
                                flexShrink: 0,
                            }}>
                            <Text style={{ fontSize: 12, fontWeight: '600', color: THEME.textSecondary }}>
                                {t(chart.labelKey)}
                            </Text>
                        </TouchableOpacity>
                    ))}
                </div>

                {/* ── Body: center + right ── */}
                <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

                    {/* ── CENTER: main chart area ── */}
                    <div ref={centerScrollRef} style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
                        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
                            {loadingSection && loadingSection === SECTION_FOR_TAB[activeTemplate] && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '32px 0', color: THEME.textSecondary }}>
                                    <ActivityIndicator size="small" color={THEME.primary} />
                                    <span>{t('app.brandDashboard.loadingSection', 'Loading…')}</span>
                                </div>
                            )}
                            {filteredCharts.map(chart => {
                                const content = renderChartByKey(chart.key);
                                if (!content) return null;
                                return (
                                    <div key={chart.key} id={`section-${chart.key}`}>
                                        {content}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="bi-divider" onMouseDown={startRightDrag} />

                    {/* ── RIGHT sidebar: context panel or detail panel ── */}
                    <div style={{ width: rightW, flexShrink: 0, borderLeft: `1px solid ${THEME.cardBorder}`, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                        {detailPanelActive ? (
                            <RightDetailPanel
                                entityModal={entityModal}
                                hashtagModal={hashtagModal}
                                narrativeModal={narrativeModal}
                                engagementModal={engagementModal}
                                emotionModal={emotionModal}
                                tagModal={tagModal}
                                wordCloudModal={wordCloudModal}
                                competitorModal={competitorModal}
                                platformModal={platformModal}
                                sentimentModal={sentimentModal}
                                authorModal={authorModal}
                                orgId={activeOrg?.id}
                                range={globalRange}
                                onClose={clearAllModals}
                            />
                        ) : renderRightPanel()}
                    </div>
                </div>
            </div>
            <ReportBuilderModal
                visible={showReportModal}
                onClose={() => setShowReportModal(false)}
                orgId={activeOrg?.id}
                theme={THEME}
            />
        </>
    );
};

const BrandDashboardScreenWrapper = () => {
    const { t } = useTranslation();
    return (
        <DashboardErrorBoundary t={t}>
            <BrandDashboardInner />
        </DashboardErrorBoundary>
    );
};

const BrandDashboardScreen = BrandDashboardScreenWrapper;

// ============================================================================
// Sub-components
// ============================================================================

const InfoTooltip = ({ text }) => {
    const { t } = useTranslation();
    const [visible, setVisible] = React.useState(false);

    const modal = visible ? ReactDOM.createPortal(
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(8px)',
        }} onClick={() => setVisible(false)}>
            <div style={{
                backgroundColor: THEME.modalBg,
                borderRadius: 20,
                width: 320,
                border: `1px solid ${THEME.border}`,
                boxShadow: '0 24px 64px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.2)',
                overflow: 'hidden',
            }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '14px 18px',
                    backgroundColor: THEME.bgCard,
                    borderBottom: `1px solid ${THEME.border}`,
                }}>
                    <FaInfoCircle size={14} color={THEME.primary} />
                    <span style={{ color: THEME.textPrimary, fontSize: 13, fontWeight: '700', letterSpacing: 0.2 }}>
                        {t('app.brandDashboard.aboutThisMetric')}
                    </span>
                </div>
                {/* Body */}
                <div style={{ padding: '16px 18px' }}>
                    <span style={{ color: THEME.textSecondary, fontSize: 14, lineHeight: '22px', display: 'block' }}>{text}</span>
                </div>
            </div>
        </div>,
        document.body
    ) : null;

    return (
        <View style={{ position: 'relative' }}>
            <TouchableOpacity onPress={() => setVisible(!visible)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <FaInfoCircle size={12} color={visible ? THEME.primary : THEME.textTertiary} />
            </TouchableOpacity>
            {modal}
        </View>
    );
};

const StatCard = ({ label, value, suffix, color, icon, isDesktop, secondaryValue, secondarySuffix, info, secondaryColor }) => (
    <div className="stat-card-wrap" style={isDesktop ? { flex: 1, display: 'flex' } : { display: 'flex' }}>
        <View style={[styles.statCard, { flex: 1 }]}>
            <div style={{
                position: 'absolute', top: -60, right: -60, width: 140, height: 140, borderRadius: 70,
                background: `radial-gradient(circle, ${color}08 0%, transparent 70%)`,
                pointerEvents: 'none',
            }} />
            <View style={styles.statContent}>
                <View style={styles.statHeader}>
                    <View style={[styles.statIconCircle, { backgroundColor: color + '12', borderWidth: 1, borderColor: color + '20' }]}>
                        {icon || <FaChartLine size={13} color={color} />}
                    </View>
                    <Text style={[styles.statLabel, { flex: 1 }]}>{label}</Text>
                    {info ? <InfoTooltip text={info} /> : null}
                </View>
                <View style={styles.statValueContainer}>
                    <span className="mono-value" style={{ fontSize: 34, fontWeight: '700', color, letterSpacing: -1.5, lineHeight: '40px' }}>{value}</span>
                    {suffix ? <Text style={styles.statSuffix}>{suffix}</Text> : null}
                </View>
                {secondaryValue != null && (
                    <View style={styles.statSecondaryContainer}>
                        <span className="mono-value" style={{ fontSize: 14, fontWeight: '600', color: secondaryColor || THEME.textSecondary }}>{secondaryValue}</span>
                        {secondarySuffix ? <Text style={[styles.statSuffix, { opacity: 0.4 }]}>{secondarySuffix}</Text> : null}
                    </View>
                )}
            </View>
        </View>
    </div>
);

const SectionCard = ({ title, icon, children, borderColor, style, info, headerRight }) => {
    const ref = React.useRef(null);
    const [visible, setVisible] = React.useState(false);
    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.08 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return (
    <div ref={ref} className={`section-card-wrap section-reveal${visible ? ' visible' : ''}`}>
        <View style={[styles.sectionCard, borderColor && { borderTopColor: borderColor, borderTopWidth: 2 }, style]}>
            <View style={styles.sectionHeader}>
                <View style={[styles.sectionTitleRow, { flex: 1 }]}>
                    {icon}
                    <Text style={styles.sectionTitle}>{title}</Text>
                    {info ? <InfoTooltip text={info} /> : null}
                </View>
                {headerRight || <div style={{
                    width: 6, height: 6, borderRadius: 3,
                    background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.purple})`,
                    opacity: 0.6,
                }} />}
            </View>
            <View style={styles.sectionContent}>
                {children}
            </View>
        </View>
    </div>
    );
};

const NarrativeCard = ({ narrative, onPress, variant }) => {
    const { t } = useTranslation();
    const borderColor = variant === 'threat' ? THEME.error :
        variant === 'opportunity' ? THEME.success :
            (narrative.sentiment_score != null && narrative.sentiment_score < 0) ? THEME.error :
                (narrative.sentiment_score != null && narrative.sentiment_score > 0) ? THEME.success : THEME.cardBorder;

    return (
        <TouchableOpacity
            activeOpacity={0.8}
            onPress={onPress}
            style={styles.narrativeCard}
        >
            <div className="narrative-card-inner" style={{ display: 'flex', flex: 1, width: '100%' }}>
                <div style={{
                    width: 3, minHeight: '100%', borderRadius: 2,
                    background: `linear-gradient(180deg, ${borderColor}, ${borderColor}40)`,
                }} />
                <View style={styles.narrativeContent}>
                    <View style={styles.narrativeTop}>
                        <Text style={styles.narrativeTitle} numberOfLines={2}>{narrative.title}</Text>
                        <div className="hover-arrow">
                            <FaChevronRight size={11} color={THEME.textTertiary} />
                        </div>
                    </View>
                    <View style={styles.narrativeMeta}>
                        <View style={styles.metaLeft}>
                            {narrative.velocity_1h != null && (
                                <View style={styles.metaBadge}>
                                    <FaChartLine size={9} color={THEME.primary} />
                                    <span className="mono-value" style={{ color: THEME.textSecondary, fontSize: 11, fontWeight: '500' }}>{formatVelocity(narrative.velocity_1h)}</span>
                                </View>
                            )}
                            {narrative.message_count != null && (
                                <View style={styles.metaBadge}>
                                    <span className="mono-value" style={{ color: THEME.textSecondary, fontSize: 11, fontWeight: '500' }}>{narrative.message_count} {t('app.brandDashboard.posts')}</span>
                                </View>
                            )}
                        </View>
                        <View style={styles.metaRight}>
                            {narrative.dominant_emotion && (
                                <View style={[styles.emotionBadge, { backgroundColor: (EMOTION_COLORS[narrative.dominant_emotion] || '#8e8e93') + '10', borderWidth: 1, borderColor: (EMOTION_COLORS[narrative.dominant_emotion] || '#8e8e93') + '20' }]}>
                                    <Text style={[styles.emotionBadgeText, { color: EMOTION_COLORS[narrative.dominant_emotion] || '#8e8e93' }]}>
                                        {narrative.dominant_emotion}
                                    </Text>
                                </View>
                            )}
                            {narrative.is_trending && (
                                <div className="trending-pulsar">
                                    <FaFire size={12} color={THEME.warning} />
                                </div>
                            )}
                        </View>
                    </View>
                </View>
            </div>
        </TouchableOpacity>
    );
};

const WarningCard = ({ narrative, onPress }) => {
    const minutesAgo = narrative.first_seen_at
        ? Math.round((Date.now() - new Date(narrative.first_seen_at).getTime()) / 60000)
        : null;

    return (
        <TouchableOpacity style={styles.warningCard} onPress={onPress}>
            <View style={styles.narrativeHeader}>
                <Text style={styles.narrativeTitle} numberOfLines={2}>{narrative.title}</Text>
                <FaChevronRight size={12} color="#8e8e93" />
            </View>
            <View style={styles.narrativeMeta}>
                {narrative.velocity_10m != null && narrative.velocity_1h != null && (
                    <View style={[styles.badge, { backgroundColor: 'rgba(255,214,10,0.15)' }]}>
                        <FaArrowUp size={10} color="#ffd60a" />
                        <Text style={[styles.badgeText, { color: '#ffd60a' }]}>
                            {formatVelocity(narrative.velocity_10m)} vs {formatVelocity(narrative.velocity_1h)}
                        </Text>
                    </View>
                )}
                {minutesAgo != null && (
                    <View style={styles.badge}>
                        <FaClock size={10} color="#8e8e93" />
                        <Text style={styles.badgeText}>{minutesAgo < 60 ? `${minutesAgo}m ago` : `${Math.round(minutesAgo / 60)}h ago`}</Text>
                    </View>
                )}
            </View>
        </TouchableOpacity>
    );
};

const HourlyChart = ({ hours, mode = '24h', sentimentOverlay = null }) => {
    const { t } = useTranslation();
    const [animated, setAnimated] = React.useState(false);
    React.useEffect(() => { const t = setTimeout(() => setAnimated(true), 80); return () => clearTimeout(t); }, []);
    const CHART_HEIGHT = 140;
    const is7d = mode === '7d';
    const maxVal = Math.max(...hours.map(h => h.relevant || 0), 1);
    const totalRelevant = hours.reduce((sum, h) => sum + (h.relevant || 0), 0);

    const barCount = hours.length;
    const VB_W = Math.max(barCount * 20, 600);
    const barW = Math.min(14, Math.max(8, (VB_W / barCount) * 0.7));
    const barGap = (VB_W - barW * barCount) / barCount;

    return (
        <View style={{ marginTop: 8 }}>
            <View style={{ flexDirection: 'row', gap: 20, marginBottom: 14 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: THEME.primary, boxShadow: `0 0 8px ${THEME.primary}40` }} />
                    <Text style={{ color: THEME.textSecondary, fontSize: 12 }}>{t('app.brandDashboard.relevant')}: <span className="mono-value" style={{ color: THEME.primary, fontWeight: '500' }}>{totalRelevant}</span></Text>
                </View>
            </View>
            <div style={{ position: 'relative', height: CHART_HEIGHT }}>
                <svg width="100%" height={CHART_HEIGHT} viewBox={`0 0 ${VB_W} ${CHART_HEIGHT}`} preserveAspectRatio="none" style={{ display: 'block' }}>
                    <line x1="0" y1={CHART_HEIGHT - 1} x2={VB_W} y2={CHART_HEIGHT - 1} stroke="rgba(255,255,255,0.06)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                    <line x1="0" y1={CHART_HEIGHT / 2} x2={VB_W} y2={CHART_HEIGHT / 2} stroke="rgba(255,255,255,0.04)" strokeWidth="1" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
                    {hours.map((h, i) => {
                        const rel = h.relevant || 0;
                        const barH = (rel / maxVal) * (CHART_HEIGHT - 20);
                        const x = i * (barW + barGap) + barGap / 2;
                        const delay = (i / barCount) * 0.5;
                        return (
                            <g key={i}>
                                {barH > 0 && <rect x={x} y={animated ? CHART_HEIGHT - barH : CHART_HEIGHT} width={barW} height={animated ? barH : 0} fill={THEME.primary} rx="2" style={{ transition: `y 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s, height 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s` }} />}
                                {rel > 0 && <text x={x + barW / 2} y={CHART_HEIGHT - barH - 4} textAnchor="middle" fill={THEME.textMain} style={{ fontSize: `${Math.max(7, Math.min(10, 120 / barCount))}px`, fontWeight: '700', opacity: animated ? 1 : 0, transition: `opacity 0.3s ease ${delay + 0.4}s` }}>{rel}</text>}
                            </g>
                        );
                    })}
                </svg>
            </div>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 }}>
                {is7d ? (
                    hours.map((h, i) => (
                        <Text key={i} style={styles.hourlyLabel}>{h.day ? new Date(h.day).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' }) : ''}</Text>
                    ))
                ) : (
                    hours.filter((_, i) => i % Math.max(1, Math.floor(hours.length / 6)) === 0).map((h, i) => (
                        <Text key={i} style={styles.hourlyLabel}>{h.hour ? new Date(h.hour).getHours() + ':00' : ''}</Text>
                    ))
                )}
            </View>
        </View>
    );
};

const SentimentTimeline = ({ days }) => {
    const lineRef = React.useRef(null);
    const [pathLength, setPathLength] = React.useState(0);
    const [animated, setAnimated] = React.useState(false);
    React.useEffect(() => {
        if (lineRef.current) setPathLength(lineRef.current.getTotalLength());
        const t = setTimeout(() => setAnimated(true), 100);
        return () => clearTimeout(t);
    }, []);
    const GRAPH_HEIGHT = 100;
    const PADDING_TOP = 20;
    const points = days.map((d, i) => {
        const val = d.avg_sentiment || 0;
        const x = days.length === 1 ? 50 : (i / (days.length - 1)) * 100;
        const y = PADDING_TOP + ((1 - val) / 2) * (GRAPH_HEIGHT - PADDING_TOP * 2);
        return { val, x, y };
    });

    const VB_W = 1000;
    const toVx = (pct) => (pct / 100) * VB_W;
    let areaD = '', lineD = '';
    if (points.length > 1) {
        areaD = `M ${toVx(points[0].x)},${points[0].y}`;
        lineD = `M ${toVx(points[0].x)},${points[0].y}`;
        for (let i = 0; i < points.length - 1; i++) {
            const p0 = points[i], p1 = points[i + 1];
            const cpx = toVx(p0.x) + (toVx(p1.x) - toVx(p0.x)) / 2;
            const seg = ` C ${cpx},${p0.y} ${cpx},${p1.y} ${toVx(p1.x)},${p1.y}`;
            areaD += seg; lineD += seg;
        }
        areaD += ` L ${toVx(points[points.length - 1].x)},${GRAPH_HEIGHT} L ${toVx(points[0].x)},${GRAPH_HEIGHT} Z`;
    }

    const neutralY = PADDING_TOP + (GRAPH_HEIGHT - PADDING_TOP * 2) / 2;

    return (
        <View style={{ marginTop: 10 }}>
            <div style={{ position: 'relative', height: GRAPH_HEIGHT }}>
                {/* Path layer */}
                {points.length > 1 && (
                    <svg width="100%" height={GRAPH_HEIGHT} viewBox={`0 0 ${VB_W} ${GRAPH_HEIGHT}`} preserveAspectRatio="none" style={{ position: 'absolute', top: 0, left: 0 }}>
                        <defs>
                            <linearGradient id="sentLineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor={THEME.primary} />
                                <stop offset="100%" stopColor={THEME.cyan} />
                            </linearGradient>
                            <linearGradient id="sentAreaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor={THEME.primary} stopOpacity="0.15" />
                                <stop offset="100%" stopColor={THEME.primary} stopOpacity="0" />
                            </linearGradient>
                        </defs>
                        <path d={areaD} fill="url(#sentAreaGrad)" style={{ opacity: animated ? 1 : 0, transition: 'opacity 0.8s ease 0.6s' }} />
                        <path ref={lineRef} d={lineD} fill="none" stroke="url(#sentLineGrad)" strokeWidth="2.5" strokeLinecap="round" vectorEffect="non-scaling-stroke"
                            style={{ strokeDasharray: pathLength || 2000, strokeDashoffset: animated ? 0 : (pathLength || 2000), transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1) 0.1s' }} />
                    </svg>
                )}
                {/* Dots + labels layer */}
                <svg width="100%" height={GRAPH_HEIGHT} style={{ position: 'absolute', top: 0, left: 0, overflow: 'visible' }}>
                    <line x1="0" y1={neutralY} x2="100%" y2={neutralY} stroke="rgba(255,255,255,0.08)" strokeDasharray="4 4" />
                    <text x="4" y={PADDING_TOP - 4} fill={THEME.success} style={{ fontSize: '9px', fontWeight: '600' }}>+100%</text>
                    <text x="4" y={GRAPH_HEIGHT - 4} fill={THEME.error} style={{ fontSize: '9px', fontWeight: '600' }}>-100%</text>
                    {points.map((p, i) => {
                        const dotColor = p.val > 0.1 ? THEME.success : p.val < -0.1 ? THEME.error : THEME.textTertiary;
                        const dotDelay = 0.8 + (i / points.length) * 0.5;
                        return (
                            <React.Fragment key={i}>
                                <circle cx={`${p.x}%`} cy={p.y} r="5" fill={THEME.bg} stroke={dotColor} strokeWidth="2" style={{ opacity: animated ? 1 : 0, transform: animated ? 'scale(1)' : 'scale(0)', transformOrigin: `${p.x}% ${p.y}px`, transition: `opacity 0.3s ease ${dotDelay}s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) ${dotDelay}s` }} />
                                <text x={`${p.x}%`} y={p.y - 10} textAnchor="middle" fill={dotColor} style={{ fontSize: '10px', fontWeight: '700', opacity: animated ? 1 : 0, transition: `opacity 0.3s ease ${dotDelay + 0.1}s` }}>
                                    {formatSentiment(p.val).pct}
                                </text>
                            </React.Fragment>
                        );
                    })}
                </svg>
            </div>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 }}>
                {days.map((d, i) => (
                    <Text key={i} style={styles.sentimentDayLabel}>
                        {d.day ? new Date(d.day).toLocaleDateString('en', { weekday: 'short' }) : ''}
                    </Text>
                ))}
            </View>
        </View>
    );
};

const GaugesPanel = ({ sentiment, controversy, credibility }) => {
    const { t } = useTranslation();
    const sentFmt = formatSentiment(sentiment);
    const contrThresh = getThresholdLabel(controversy, 'controversy');
    const credThresh = getThresholdLabel(credibility, 'credibility');
    return (
        <View style={{ gap: 12, marginBottom: 16 }}>
            <GaugeBar label={t('app.brandDashboard.sentiment')} value={sentiment} min={-1} max={1}
                format={(v) => v != null ? `${sentFmt.pct} · ${sentFmt.label}` : '--'}
                color={sentFmt.color}
                pct={sentiment != null ? ((sentiment + 1) / 2) * 100 : 0} />
            <GaugeBar label={t('app.brandDashboard.controversy')} value={controversy} min={0} max={1}
                format={(v) => v != null ? `${(v * 100).toFixed(0)}% (${contrThresh.label})` : '--'}
                color={contrThresh.color} pct={controversy != null ? controversy * 100 : 0} />
            <GaugeBar label={t('app.brandDashboard.credibility')} value={credibility} min={0} max={1}
                format={(v) => v != null ? `${(v * 100).toFixed(0)}% (${credThresh.label})` : '--'}
                color={credThresh.color} pct={credibility != null ? credibility * 100 : 0} />
        </View>
    );
};

const GaugeBar = ({ label, format, value, color, pct }) => (
    <View>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
            <Text style={styles.gaugeLabel}>{label}</Text>
            <span className="mono-value" style={{ fontSize: 14, fontWeight: '500', color }}>{format(value)}</span>
        </View>
        <View style={styles.gaugeTrack}>
            <div style={{
                height: '100%', borderRadius: 4,
                width: `${Math.max(pct, 0)}%`,
                background: `linear-gradient(90deg, ${color}, ${color}90)`,
                boxShadow: `0 0 12px ${color}30`,
                transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            }} />
        </View>
    </View>
);

const PostSentimentTimeline = ({ hours, isDaily }) => {
    const { t } = useTranslation();
    const [normalized, setNormalized] = useState(true);
    const [animated, setAnimated] = React.useState(false);
    React.useEffect(() => { const t = setTimeout(() => setAnimated(true), 80); return () => clearTimeout(t); }, []);
    const CHART_HEIGHT = 130;
    const maxTotal = Math.max(...hours.map(h => {
        if (h.positive_count != null) return (h.positive_count || 0) + (h.neutral_count || 0) + (h.negative_count || 0);
        return h.post_count || 1;
    }), 1);

    const barCount = hours.length;
    const VB_W = Math.max(barCount * 20, 600);
    const barW = Math.min(14, Math.max(8, (VB_W / barCount) * 0.7));
    const barGap = (VB_W - barW * barCount) / barCount;

    return (
        <View style={{ marginTop: 10 }}>
            <View style={{ flexDirection: 'row', gap: 16, marginBottom: 10, alignItems: 'center' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: '#30d158' }} />
                    <Text style={{ color: THEME.textSecondary, fontSize: 11 }}>{t('app.brandDashboard.positive')}</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: '#fbbf24' }} />
                    <Text style={{ color: THEME.textSecondary, fontSize: 11 }}>{t('app.brandDashboard.neutral')}</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: '#ff453a' }} />
                    <Text style={{ color: THEME.textSecondary, fontSize: 11 }}>{t('app.brandDashboard.negative')}</Text>
                </View>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, background: THEME.surface, borderRadius: 8, padding: '4px 6px', border: `1px solid ${THEME.cardBorder}` }}>
                    <div onClick={() => setNormalized(true)} style={{ padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: 'pointer', background: normalized ? THEME.primary + '20' : 'transparent', color: normalized ? THEME.primary : THEME.textTertiary, border: normalized ? `1px solid ${THEME.primary}50` : '1px solid transparent' }}>
                        {t('app.brandDashboard.distribution') || 'Distribution'}
                    </div>
                    <div onClick={() => setNormalized(false)} style={{ padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: 'pointer', background: !normalized ? THEME.primary + '20' : 'transparent', color: !normalized ? THEME.primary : THEME.textTertiary, border: !normalized ? `1px solid ${THEME.primary}50` : '1px solid transparent' }}>
                        {t('app.brandDashboard.volume') || 'Volume'}
                    </div>
                </div>
            </View>
            <div style={{ position: 'relative', height: CHART_HEIGHT }}>
                <svg width="100%" height={CHART_HEIGHT} viewBox={`0 0 ${VB_W} ${CHART_HEIGHT}`} preserveAspectRatio="none" style={{ display: 'block' }}>
                    <line x1="0" y1={CHART_HEIGHT - 1} x2={VB_W} y2={CHART_HEIGHT - 1} stroke="rgba(255,255,255,0.06)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                    {hours.map((h, i) => {
                        let pos, neu, neg, total;
                        if (h.positive_count != null) {
                            pos = h.positive_count || 0;
                            neu = h.neutral_count || 0;
                            neg = h.negative_count || 0;
                            total = pos + neu + neg;
                        } else {
                            total = h.post_count || 0;
                            const sent = h.avg_sentiment || 0;
                            pos = sent > 0.15 ? Math.round(total * 0.6) : sent > -0.15 ? Math.round(total * 0.3) : Math.round(total * 0.1);
                            neg = sent < -0.15 ? Math.round(total * 0.6) : sent < 0.15 ? Math.round(total * 0.1) : Math.round(total * 0.1);
                            neu = Math.max(0, total - pos - neg);
                        }
                        if (total === 0) return null;
                        const height = normalized ? (CHART_HEIGHT - 16) : (total / maxTotal) * (CHART_HEIGHT - 16);
                        const x = i * (barW + barGap) + barGap / 2;
                        const posH = (pos / total) * height;
                        const neuH = (neu / total) * height;
                        const negH = height - posH - neuH;
                        const delay = (i / hours.length) * 0.5;
                        const barStyle = { transition: `y 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s, height 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s` };
                        return (
                            <g key={i}>
                                <rect x={x} y={animated ? CHART_HEIGHT - posH : CHART_HEIGHT} width={barW} height={animated ? posH : 0} fill="#30d158" rx="1" style={barStyle} />
                                <rect x={x} y={animated ? CHART_HEIGHT - posH - neuH : CHART_HEIGHT} width={barW} height={animated ? neuH : 0} fill="#fbbf24" rx="0" style={barStyle} />
                                <rect x={x} y={animated ? CHART_HEIGHT - height : CHART_HEIGHT} width={barW} height={animated ? negH : 0} fill="#ff453a" rx="1" style={barStyle} />
                            </g>
                        );
                    })}
                </svg>
            </div>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 }}>
                {hours.filter((_, i) => i % Math.max(1, Math.floor(hours.length / 6)) === 0).map((h, i) => (
                    <Text key={i} style={styles.sentimentDayLabel}>
                        {isDaily
                            ? (h.day ? new Date(h.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '')
                            : (h.hour ? new Date(h.hour).getHours() + ':00' : '')}
                    </Text>
                ))}
            </View>
        </View>
    );
};

const SentimentDistributionBar = ({ distribution }) => {
    const { t } = useTranslation();
    if (!distribution) return null;
    const total = (distribution.positive || 0) + (distribution.neutral || 0) + (distribution.negative || 0);
    if (total === 0) return null;
    const pos = Math.round((distribution.positive / total) * 100);
    const neu = Math.round((distribution.neutral / total) * 100);
    const neg = 100 - pos - neu;
    return (
        <View style={{ marginTop: 12, gap: 6 }}>
            <View style={{ flexDirection: 'row', height: 8, borderRadius: 4, overflow: 'hidden', gap: 1 }}>
                {pos > 0 && <div style={{ width: `${pos}%`, background: '#30d158', borderRadius: pos === 100 ? 4 : '4px 0 0 4px' }} />}
                {neu > 0 && <div style={{ width: `${neu}%`, background: '#fbbf24' }} />}
                {neg > 0 && <div style={{ width: `${neg}%`, background: '#ff453a', borderRadius: neg === 100 ? 4 : '0 4px 4px 0' }} />}
            </View>
            <View style={{ flexDirection: 'row', gap: 16 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#30d158' }} />
                    <Text style={{ color: '#30d158', fontSize: 11, fontWeight: '600' }}>{t('app.brandDashboard.positive')} {pos}%</Text>
                    <Text style={{ color: THEME.textTertiary, fontSize: 10 }}>({distribution.positive})</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#fbbf24' }} />
                    <Text style={{ color: '#fbbf24', fontSize: 11, fontWeight: '600' }}>{t('app.brandDashboard.neutral')} {neu}%</Text>
                    <Text style={{ color: THEME.textTertiary, fontSize: 10 }}>({distribution.neutral})</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#ff453a' }} />
                    <Text style={{ color: '#ff453a', fontSize: 11, fontWeight: '600' }}>{t('app.brandDashboard.negative')} {neg}%</Text>
                    <Text style={{ color: THEME.textTertiary, fontSize: 10 }}>({distribution.negative})</Text>
                </View>
            </View>
        </View>
    );
};

const SentimentTimelineSection = ({ data }) => {
    const { t } = useTranslation();
    const hasTimeline = data.post_sentiment_timeline && data.post_sentiment_timeline.length > 0;
    const hasDaily = data.post_sentiment_timeline_7d && data.post_sentiment_timeline_7d.length > 0;
    const hasNarrative = data.sentiment_timeline && data.sentiment_timeline.length > 0;
    if (!hasTimeline && !hasDaily && !hasNarrative) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noSentimentData')}</div>;

    const dist = data.sentiment_distribution || data.sentiment_distribution_7d;

    return (
        <SectionCard title={t('app.brandDashboard.postSentiment')} info={t('app.brandDashboard.postSentimentInfo')}>
            {hasDaily && <PostSentimentTimeline hours={data.post_sentiment_timeline_7d} isDaily />}
            {!hasDaily && hasTimeline && <PostSentimentTimeline hours={data.post_sentiment_timeline} />}
            {!hasTimeline && !hasDaily && hasNarrative && <SentimentTimeline days={data.sentiment_timeline} />}
            <SentimentDistributionBar distribution={dist} />
        </SectionCard>
    );
};

const EmotionPieChart = ({ emotions, label, onEmotionClick }) => {
    const { t } = useTranslation();
    const [hovered, setHovered] = React.useState(null);
    const [animated, setAnimated] = React.useState(false);
    React.useEffect(() => { const t = setTimeout(() => setAnimated(true), 50); return () => clearTimeout(t); }, []);
    if (!emotions || Object.keys(emotions).length === 0) return null;

    const entries = Object.entries(emotions).sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((s, [, c]) => s + c, 0);
    if (total === 0) return null;

    const segments = entries.map(([emotion, count], i) => ({
        emotion, count, pct: (count / total) * 100, color: EMOTION_COLORS[emotion] || '#8e8e93', idx: i
    }));

    const cx = 120, cy = 120, r = 90, strokeW = 35;
    const circumference = 2 * Math.PI * r;
    let cumAngle = -90;
    const arcs = segments.map((seg) => {
        const angle = (seg.pct / 100) * 360;
        const startAngle = cumAngle;
        cumAngle += angle;
        const dashLen = (seg.pct / 100) * circumference;
        return { ...seg, dashLen, startAngle };
    });

    return (
        <View style={{ gap: 8 }}>
            <Text style={styles.subLabel}>{label || t('app.brandDashboard.emotionBreakdown')}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                <svg viewBox="0 0 240 240" style={{ width: 200, height: 200, flexShrink: 0 }}>
                    {arcs.map((arc) => {
                        const finalOffset = -((arc.startAngle + 90) / 360) * circumference;
                        const delay = ((arc.startAngle + 90) / 360) * 0.6;
                        return (
                        <circle
                            key={arc.idx}
                            cx={cx} cy={cy} r={r}
                            fill="none"
                            stroke={hovered === arc.idx ? arc.color : arc.color + 'cc'}
                            strokeWidth={hovered === arc.idx ? strokeW + 6 : strokeW}
                            strokeDasharray={animated ? `${arc.dashLen} ${circumference - arc.dashLen}` : `0 ${circumference}`}
                            strokeDashoffset={finalOffset}
                            style={{ cursor: 'pointer', transition: `stroke-dasharray 0.7s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s, stroke-width 0.2s, stroke 0.2s` }}
                            onMouseEnter={() => setHovered(arc.idx)}
                            onMouseLeave={() => setHovered(null)}
                            onClick={() => onEmotionClick && onEmotionClick(arc.emotion)}
                        />
                        );
                    })}
                    <text x={cx} y={cy - 6} textAnchor="middle" fill={THEME.textMain} fontSize="22" fontWeight="700" fontFamily="'DM Mono', monospace" style={{ pointerEvents: 'none' }}>
                        {total}
                    </text>
                    <text x={cx} y={cy + 14} textAnchor="middle" fill={THEME.textTertiary} fontSize="10" fontWeight="500" style={{ pointerEvents: 'none' }}>
                        posts
                    </text>
                    {hovered != null && arcs[hovered] && (
                        <text x={cx} y={cy + 30} textAnchor="middle" fill={arcs[hovered].color} fontSize="10" fontWeight="600" style={{ pointerEvents: 'none' }}>
                            {arcs[hovered].emotion} · {arcs[hovered].pct.toFixed(1)}%
                        </text>
                    )}
                </svg>
                <View style={{ flex: 1, minWidth: 140, gap: 6 }}>
                    {arcs.map((arc) => (
                        <TouchableOpacity
                            key={arc.idx}
                            onPress={() => onEmotionClick && onEmotionClick(arc.emotion)}
                            onMouseEnter={() => setHovered(arc.idx)}
                            onMouseLeave={() => setHovered(null)}
                            style={{
                                flexDirection: 'row', alignItems: 'center', gap: 8,
                                paddingVertical: 4, paddingHorizontal: 8, borderRadius: 6,
                                backgroundColor: hovered === arc.idx ? arc.color + '12' : 'transparent',
                                opacity: hovered != null && hovered !== arc.idx ? 0.4 : 1,
                                cursor: onEmotionClick ? 'pointer' : 'default',
                            }}
                        >
                            <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: arc.color, flexShrink: 0 }} />
                            <Text style={{ color: hovered === arc.idx ? THEME.textMain : THEME.textSecondary, fontSize: 12, fontWeight: hovered === arc.idx ? '600' : '400', flex: 1 }}>
                                {arc.emotion}
                            </Text>
                            <Text style={{ color: arc.color, fontSize: 12, fontWeight: '700', fontFamily: "'DM Mono', monospace" }}>
                                {arc.pct.toFixed(1)}%
                            </Text>
                            <Text style={{ color: THEME.textTertiary, fontSize: 10, width: 30, textAlign: 'right' }}>
                                {arc.count}
                            </Text>
                        </TouchableOpacity>
                    ))}
                </View>
            </View>
            {onEmotionClick && (
                <Text style={{ color: THEME.textTertiary, fontSize: 10, marginTop: 4, textAlign: 'center' }}>
                    Click an emotion to see its posts →
                </Text>
            )}
        </View>
    );
};

// ============================================================================
// List Distribution Chart (Pie + Stacked Columns over time)
// ============================================================================

const LIST_COLORS = ['#0a84ff', '#30d158', '#ff9f0a', '#bf5af2', '#ff453a', '#64d2ff', '#ffd60a', '#ac8e68', '#ff375f', '#5e5ce6'];

const ListDistributionChart = ({ distribution, timeline }) => {
    const { t } = useTranslation();
    const [viewMode, setViewMode] = useState('both');
    const [animated, setAnimated] = React.useState(false);
    React.useEffect(() => { const t = setTimeout(() => setAnimated(true), 50); return () => clearTimeout(t); }, []);

    const total = distribution.reduce((s, d) => s + d.post_count, 0);
    const listColors = {};
    distribution.forEach((d, i) => { listColors[d.list_id] = LIST_COLORS[i % LIST_COLORS.length]; });

    // Pie chart
    const cx = 100, cy = 100, r = 75, strokeW = 30;
    const circumference = 2 * Math.PI * r;
    let cumAngle = -90;
    const arcs = distribution.map((item) => {
        const pct = (item.post_count / total) * 100;
        const angle = (pct / 100) * 360;
        const startAngle = cumAngle;
        cumAngle += angle;
        const midAngle = startAngle + angle / 2;
        return { ...item, pct, startAngle, angle, midAngle, color: listColors[item.list_id] };
    });

    // Timeline columns
    const days = {};
    (timeline || []).forEach(row => {
        if (!row.day) return;
        if (!days[row.day]) days[row.day] = {};
        days[row.day][row.list_id] = (days[row.day][row.list_id] || 0) + row.post_count;
    });
    const sortedDays = Object.keys(days).sort();
    const maxDayTotal = Math.max(...sortedDays.map(d => Object.values(days[d]).reduce((s, v) => s + v, 0)), 1);
    const CHART_HEIGHT = 130;
    const barCount = sortedDays.length;
    const VB_W = Math.max(barCount * 22, 500);
    const barW = Math.max(10, (VB_W / Math.max(barCount, 1)) * 0.7);
    const barGap = barCount > 1 ? (VB_W - barW * barCount) / barCount : 0;

    return (
        <View style={{ gap: 16 }}>
            {/* Toggle */}
            <View style={{ flexDirection: 'row', gap: 6 }}>
                {['both', 'pie', 'timeline'].map(mode => (
                    <TouchableOpacity key={mode} onPress={() => setViewMode(mode)}
                        style={{ paddingHorizontal: 12, paddingVertical: 5, borderRadius: 16, backgroundColor: viewMode === mode ? THEME.primary + '20' : THEME.surface, border: `1px solid ${viewMode === mode ? THEME.primary + '50' : THEME.cardBorder}` }}>
                        <Text style={{ fontSize: 11, fontWeight: '600', color: viewMode === mode ? THEME.primary : THEME.textSecondary }}>
                            {mode === 'both' ? t('app.brandDashboard.all') : mode === 'pie' ? t('app.brandDashboard.pieChart') : t('app.brandDashboard.timeline')}
                        </Text>
                    </TouchableOpacity>
                ))}
            </View>

            {/* Legend */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                {distribution.map((item) => (
                    <View key={item.list_id} style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: listColors[item.list_id] }} />
                        <Text style={{ color: THEME.textSecondary, fontSize: 11 }}>{item.list_name}</Text>
                        <Text style={{ color: THEME.textTertiary, fontSize: 10 }}>({item.post_count})</Text>
                    </View>
                ))}
            </View>

            {/* Pie Chart */}
            {(viewMode === 'both' || viewMode === 'pie') && (
                <View style={{ alignItems: 'center' }}>
                    <svg width={200} height={200} viewBox="0 0 200 200">
                        {arcs.map((arc, i) => {
                            const dashLen = (arc.pct / 100) * circumference;
                            const dashOffset = -((arc.startAngle + 90) / 360) * circumference;
                            const delay = ((arc.startAngle + 90) / 360) * 0.6;
                            return (
                                <circle key={i} cx={cx} cy={cy} r={r}
                                    fill="none" stroke={arc.color} strokeWidth={strokeW}
                                    strokeDasharray={animated ? `${dashLen} ${circumference - dashLen}` : `0 ${circumference}`}
                                    strokeDashoffset={dashOffset}
                                    style={{ transition: `stroke-dasharray 0.7s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s` }}
                                />
                            );
                        })}
                        <text x={cx} y={cy - 6} textAnchor="middle" fill={THEME.textMain} fontSize="18" fontWeight="700">{total}</text>
                        <text x={cx} y={cy + 12} textAnchor="middle" fill={THEME.textTertiary} fontSize="10">{t('app.brandDashboard.totalPosts')}</text>
                    </svg>
                </View>
            )}

            {/* Stacked Column Timeline */}
            {(viewMode === 'both' || viewMode === 'timeline') && sortedDays.length > 0 && (
                <View style={{ marginTop: 4 }}>
                    <div style={{ position: 'relative', height: CHART_HEIGHT }}>
                        <svg width="100%" height={CHART_HEIGHT} viewBox={`0 0 ${VB_W} ${CHART_HEIGHT}`} preserveAspectRatio="none" style={{ display: 'block' }}>
                            <line x1="0" y1={CHART_HEIGHT - 1} x2={VB_W} y2={CHART_HEIGHT - 1} stroke={THEME.cardBorder} strokeWidth="1" vectorEffect="non-scaling-stroke" />
                            {sortedDays.map((day, i) => {
                                const dayData = days[day];
                                const dayTotal = Object.values(dayData).reduce((s, v) => s + v, 0);
                                const height = (dayTotal / maxDayTotal) * (CHART_HEIGHT - 16);
                                const x = i * (barW + barGap) + barGap / 2;
                                let yOffset = 0;
                                const delay = (i / sortedDays.length) * 0.5;
                                return (
                                    <g key={i}>
                                        {distribution.map((item) => {
                                            const count = dayData[item.list_id] || 0;
                                            if (count === 0) return null;
                                            const segH = (count / dayTotal) * height;
                                            const y = CHART_HEIGHT - yOffset - segH;
                                            yOffset += segH;
                                            return <rect key={item.list_id} x={x} y={animated ? y : CHART_HEIGHT} width={barW} height={animated ? segH : 0} fill={listColors[item.list_id]} rx="1" style={{ transition: `y 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s, height 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s` }} />;
                                        })}
                                    </g>
                                );
                            })}
                        </svg>
                    </div>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 }}>
                        {sortedDays.filter((_, i) => i % Math.max(1, Math.floor(sortedDays.length / 6)) === 0).map((day, i) => (
                            <Text key={i} style={{ color: THEME.textTertiary, fontSize: 9 }}>
                                {new Date(day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            </Text>
                        ))}
                    </View>
                </View>
            )}
        </View>
    );
};

const TAG_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#e11d48'];

const TagPieChart = ({ distribution, onTagClick }) => {
    const [hovered, setHovered] = React.useState(null);
    const [animated, setAnimated] = React.useState(false);
    React.useEffect(() => { const t = setTimeout(() => setAnimated(true), 50); return () => clearTimeout(t); }, []);
    if (!distribution || distribution.length === 0) return null;

    const total = distribution.reduce((s, d) => s + d.count, 0);
    if (total === 0) return null;

    const segments = distribution.map((d, i) => ({
        tag: d.tag, count: d.count, pct: (d.count / total) * 100,
        color: d.tag === 'Other' ? '#6b7280' : TAG_COLORS[i % TAG_COLORS.length],
        idx: i, isOther: d.tag === 'Other',
    }));

    const cx = 200, cy = 200, r = 150, strokeW = 50;
    const circumference = 2 * Math.PI * r;
    let cumAngle = -90;
    const arcs = segments.map((seg) => {
        const angle = (seg.pct / 100) * 360;
        const startAngle = cumAngle;
        cumAngle += angle;
        const dashLen = (seg.pct / 100) * circumference;
        return { ...seg, dashLen, startAngle };
    });

    return (
        <View style={{ alignItems: 'center', paddingVertical: 8 }}>
            <svg viewBox="0 0 400 400" style={{ width: '100%', maxWidth: 380, height: 'auto' }}>
                {arcs.map((arc) => {
                    const finalOffset = -((arc.startAngle + 90) / 360) * circumference;
                    const delay = ((arc.startAngle + 90) / 360) * 0.6;
                    return (
                    <circle
                        key={arc.idx}
                        cx={cx} cy={cy} r={r}
                        fill="none"
                        stroke={hovered === arc.idx ? arc.color : arc.color + 'cc'}
                        strokeWidth={hovered === arc.idx ? strokeW + 8 : strokeW}
                        strokeDasharray={animated ? `${arc.dashLen} ${circumference - arc.dashLen}` : `0 ${circumference}`}
                        strokeDashoffset={finalOffset}
                        style={{ cursor: arc.isOther ? 'default' : 'pointer', transition: `stroke-dasharray 0.7s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s, stroke-width 0.2s, stroke 0.2s` }}
                        onMouseEnter={() => setHovered(arc.idx)}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => { if (!arc.isOther && onTagClick) onTagClick(arc.tag); }}
                    />
                    );
                })}
                <text x={cx} y={cy - 12} textAnchor="middle" fill={THEME.textMain} fontSize="36" fontWeight="700" fontFamily="'DM Mono', monospace" style={{ pointerEvents: 'none' }}>
                    {total}
                </text>
                <text x={cx} y={cy + 16} textAnchor="middle" fill={THEME.textTertiary} fontSize="13" fontWeight="500" letterSpacing="1" style={{ pointerEvents: 'none' }}>
                    posts
                </text>
                {hovered != null && arcs[hovered] && (
                    <text x={cx} y={cy + 40} textAnchor="middle" fill={arcs[hovered].color} fontSize="11" fontWeight="600" style={{ pointerEvents: 'none' }}>
                        {arcs[hovered].pct.toFixed(1)}%
                    </text>
                )}
            </svg>

            <View style={{ width: '100%', flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 12, paddingHorizontal: 4 }}>
                {arcs.map((arc) => (
                    <TouchableOpacity
                        key={arc.idx}
                        onPress={() => { if (!arc.isOther && onTagClick) onTagClick(arc.tag); }}
                        onMouseEnter={() => setHovered(arc.idx)}
                        onMouseLeave={() => setHovered(null)}
                        style={{
                            flexDirection: 'row', alignItems: 'center', gap: 6,
                            width: '48%',
                            opacity: hovered != null && hovered !== arc.idx ? 0.35 : 1,
                            paddingVertical: 4, paddingHorizontal: 6,
                            borderRadius: 6,
                            backgroundColor: hovered === arc.idx ? arc.color + '12' : 'transparent',
                            cursor: 'pointer',
                        }}
                    >
                        <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: arc.color, flexShrink: 0 }} />
                        <Text numberOfLines={1} style={{ color: hovered === arc.idx ? THEME.textMain : THEME.textSecondary, fontSize: 11, fontWeight: hovered === arc.idx ? '600' : '400', flex: 1 }}>
                            {arc.tag}
                        </Text>
                        <Text style={{ color: arc.color, fontSize: 11, fontWeight: '700', fontFamily: "'DM Mono', monospace" }}>
                            {arc.pct.toFixed(0)}%
                        </Text>
                    </TouchableOpacity>
                ))}
            </View>
        </View>
    );
};

const TagTimelineChart = ({ tag, orgId }) => {
    const { t } = useTranslation();
    const [range, setRange] = useState('7d');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hoveredBar, setHoveredBar] = useState(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const result = await ListsManager.fetchTagTimeline(tag, range, orgId);
            setData(result);
            setLoading(false);
        })();
    }, [tag, range, orgId]);

    const ranges = ['24h', '7d', '30d'];

    if (loading) {
        return (
            <div style={{ padding: '20px 0', display: 'flex', justifyContent: 'center' }}>
                <ActivityIndicator size="small" color={THEME.primary} />
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div style={{ padding: '16px 0', textAlign: 'center' }}>
                <span style={{ color: THEME.textTertiary, fontSize: 11 }}>{t('app.brandDashboard.noTimelineData')}</span>
            </div>
        );
    }

    const maxCount = Math.max(...data.map(d => d.count), 1);
    const total = data.reduce((s, d) => s + d.count, 0);
    const chartHeight = 120;
    const yAxisWidth = 32;

    const niceMax = (() => {
        if (maxCount <= 5) return maxCount;
        const magnitude = Math.pow(10, Math.floor(Math.log10(maxCount)));
        const normalized = maxCount / magnitude;
        const nice = normalized <= 1.5 ? 1.5 : normalized <= 2 ? 2 : normalized <= 3 ? 3 : normalized <= 5 ? 5 : 10;
        return Math.ceil(nice * magnitude);
    })();

    const yTicks = (() => {
        if (niceMax <= 4) return Array.from({ length: niceMax + 1 }, (_, i) => i);
        const step = niceMax <= 10 ? 2 : niceMax <= 20 ? 5 : niceMax <= 50 ? 10 : Math.ceil(niceMax / 5);
        const ticks = [];
        for (let v = 0; v <= niceMax; v += step) ticks.push(v);
        if (ticks[ticks.length - 1] < niceMax) ticks.push(niceMax);
        return ticks;
    })();

    const xLabels = (() => {
        if (!data.length) return [];
        const step = Math.max(1, Math.floor(data.length / 5));
        const labels = [];
        for (let i = 0; i < data.length; i += step) labels.push(i);
        if (labels[labels.length - 1] !== data.length - 1) labels.push(data.length - 1);
        return labels;
    })();

    const formatDate = (time) => range === '24h'
        ? new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : new Date(time).toLocaleDateString([], { month: 'short', day: 'numeric' });

    return (
        <div style={{
            padding: '16px 0 12px',
            borderBottom: `1px solid ${THEME.cardBorder}`,
        }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ color: THEME.textMain, fontSize: 12, fontWeight: 700 }}>
                        {t('app.brandDashboard.postsOverTime')}
                    </span>
                    <span style={{ color: THEME.textTertiary, fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
                        {total} {t('app.brandDashboard.posts')}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: 3, background: THEME.surface, borderRadius: 8, padding: 2 }}>
                    {ranges.map(r => (
                        <button
                            key={r}
                            onClick={() => setRange(r)}
                            style={{
                                padding: '4px 10px',
                                borderRadius: 6,
                                border: 'none',
                                fontSize: 10,
                                fontWeight: 700,
                                cursor: 'pointer',
                                backgroundColor: range === r ? THEME.primary : 'transparent',
                                color: range === r ? '#fff' : THEME.textSecondary,
                                transition: 'all 0.2s',
                            }}
                        >
                            {r}
                        </button>
                    ))}
                </div>
            </div>

            {/* Chart area with Y axis */}
            <div style={{ display: 'flex', position: 'relative' }}>
                {/* Y-axis labels */}
                <div style={{ width: yAxisWidth, height: chartHeight, position: 'relative', flexShrink: 0 }}>
                    {yTicks.map(tick => {
                        const bottom = (tick / niceMax) * chartHeight;
                        return (
                            <span key={tick} style={{
                                position: 'absolute',
                                right: 4,
                                bottom: bottom - 5,
                                fontSize: 9,
                                fontFamily: "'DM Mono', monospace",
                                color: THEME.textTertiary,
                                lineHeight: '10px',
                            }}>
                                {tick}
                            </span>
                        );
                    })}
                </div>

                {/* Chart body */}
                <div style={{ flex: 1, height: chartHeight, position: 'relative' }}>
                    {/* Grid lines */}
                    {yTicks.map(tick => {
                        const bottom = (tick / niceMax) * 100;
                        return (
                            <div key={tick} style={{
                                position: 'absolute',
                                left: 0, right: 0,
                                bottom: `${bottom}%`,
                                height: 1,
                                backgroundColor: tick === 0 ? THEME.cardBorder : `${THEME.cardBorder}80`,
                                pointerEvents: 'none',
                            }} />
                        );
                    })}

                    {/* Bars */}
                    <div style={{ display: 'flex', alignItems: 'flex-end', height: '100%', gap: 1, padding: '0 2px' }}>
                        {data.map((d, i) => {
                            const h = niceMax > 0 ? Math.max(1, (d.count / niceMax) * chartHeight) : 1;
                            const isHovered = hoveredBar === i;
                            return (
                                <div
                                    key={i}
                                    onMouseEnter={() => setHoveredBar(i)}
                                    onMouseLeave={() => setHoveredBar(null)}
                                    style={{
                                        flex: 1,
                                        height: h,
                                        backgroundColor: isHovered ? THEME.primary : `${THEME.primary}99`,
                                        borderRadius: '3px 3px 0 0',
                                        transition: 'height 0.3s ease, background-color 0.15s',
                                        cursor: 'default',
                                        position: 'relative',
                                    }}
                                >
                                    {isHovered && (
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '100%',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            marginBottom: 4,
                                            background: THEME.bgCard,
                                            border: `1px solid ${THEME.cardBorder}`,
                                            borderRadius: 6,
                                            padding: '4px 8px',
                                            whiteSpace: 'nowrap',
                                            zIndex: 10,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                                        }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: THEME.textMain, fontFamily: "'DM Mono', monospace" }}>
                                                {d.count}
                                            </div>
                                            <div style={{ fontSize: 9, color: THEME.textTertiary }}>
                                                {formatDate(d.time)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* X-axis labels */}
            <div style={{ display: 'flex', marginLeft: yAxisWidth, marginTop: 6, position: 'relative', height: 14 }}>
                {xLabels.map(idx => {
                    const left = data.length > 1 ? `${(idx / (data.length - 1)) * 100}%` : '0%';
                    return (
                        <span key={idx} style={{
                            position: 'absolute',
                            left,
                            transform: 'translateX(-50%)',
                            fontSize: 9,
                            fontFamily: "'DM Mono', monospace",
                            color: THEME.textTertiary,
                            whiteSpace: 'nowrap',
                        }}>
                            {formatDate(data[idx]?.time)}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

const EmotionBars = ({ emotions, label }) => {
    const { t } = useTranslation();
    const maxCount = Math.max(...Object.values(emotions), 1);
    return (
        <View style={{ gap: 8 }}>
            <Text style={styles.subLabel}>{label || t('app.brandDashboard.emotionBreakdown')}</Text>
            {Object.entries(emotions).map(([emotion, count]) => (
                <View key={emotion} style={styles.emotionRow}>
                    <Text style={[styles.emotionLabel, { color: EMOTION_COLORS[emotion] || '#8e8e93' }]}>{emotion}</Text>
                    <View style={styles.emotionBarTrack}>
                        <View style={[styles.emotionBarFill, {
                            width: `${(count / maxCount) * 100}%`,
                            backgroundColor: EMOTION_COLORS[emotion] || '#8e8e93'
                        }]} />
                    </View>
                    <Text style={styles.emotionCount}>{count}</Text>
                </View>
            ))}
        </View>
    );
};

const PlatformBars = ({ platforms, onPlatformClick }) => {
    const [animated, setAnimated] = React.useState(false);
    React.useEffect(() => { const t = setTimeout(() => setAnimated(true), 80); return () => clearTimeout(t); }, []);
    const maxPct = Math.max(...Object.values(platforms), 1);
    const sorted = Object.entries(platforms).sort((a, b) => b[1] - a[1]);
    return (
        <View style={{ gap: 10 }}>
            {sorted.map(([platform, pct], i) => {
                const Icon = PLATFORM_ICONS[platform];
                const delay = i * 0.08;
                return (
                    <div key={platform} onClick={() => onPlatformClick && onPlatformClick(platform)} style={{ cursor: onPlatformClick ? 'pointer' : 'default' }}>
                    <View style={styles.platformRow}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', width: 90, gap: 6 }}>
                            {Icon && <Icon size={14} color={PLATFORM_COLORS[platform] || '#8e8e93'} />}
                            <Text style={styles.platformLabel}>{PLATFORM_LABELS[platform] || platform}</Text>
                        </View>
                        <View style={styles.platformBarTrack}>
                            <View style={[styles.platformBarFill, {
                                width: animated ? `${(pct / maxPct) * 100}%` : '0%',
                                backgroundColor: PLATFORM_COLORS[platform] || '#8e8e93',
                                transition: `width 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
                            }]} />
                        </View>
                        <Text style={[styles.platformPct, { opacity: animated ? 1 : 0, transition: `opacity 0.3s ease ${delay + 0.4}s` }]}>{pct.toFixed(0)}%</Text>
                    </View>
                    </div>
                );
            })}
        </View>
    );
};

const SourcesList = ({ sources24h, sources7d, sourceTotals, onSourceClick }) => {
    const { t } = useTranslation();
    const sources = sources7d && sources7d.length > 0 ? sources7d : sources24h;

    // Group by platform
    const grouped = {};
    (sources || []).forEach(s => {
        if (!grouped[s.source]) grouped[s.source] = [];
        grouped[s.source].push(s);
    });
    const platformOrder = ['twitter', 'telegram', 'tiktok', 'instagram', 'facebook_group', 'rss'];
    const sortedPlatforms = Object.keys(grouped).sort((a, b) => {
        const ia = platformOrder.indexOf(a);
        const ib = platformOrder.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });

    return (
        <View style={{ gap: 12 }}>

            {sortedPlatforms.map(platform => {
                const Icon = PLATFORM_ICONS[platform];
                const total = window === '24h' && sourceTotals && sourceTotals[platform];
                return (
                    <View key={platform}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            {Icon && <Icon size={14} color={PLATFORM_COLORS[platform] || '#8e8e93'} />}
                            <Text style={styles.sourcePlatformLabel}>
                                {PLATFORM_LABELS[platform] || platform}{total ? ` (${total} ${t('app.brandDashboard.posts')})` : ''}
                            </Text>
                        </View>
                        {grouped[platform].map((s, i) => (
                            <div key={i} onClick={() => onSourceClick && onSourceClick(s.username, platform)} style={{ cursor: onSourceClick ? 'pointer' : 'default' }}>
                            <View style={styles.sourceRow}>
                                <Text style={styles.sourceUsername}>@{s.username}</Text>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                    {s.follower_count > 0 && (
                                        <Text style={styles.sourceFollowers}>{formatFollowers(s.follower_count)}</Text>
                                    )}
                                    <Text style={styles.sourceCount}>{s.post_count} {t('app.brandDashboard.posts')}</Text>
                                </View>
                            </View>
                            </div>
                        ))}
                    </View>
                );
            })}
        </View>
    );
};

const EntityChips = ({ entities }) => (
    <View style={{ gap: 12 }}>
        {['people', 'organizations', 'locations'].map(cat => {
            const items = entities[cat];
            if (!items || items.length === 0) return null;
            return (
                <View key={cat}>
                    <Text style={styles.entityCategoryLabel}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</Text>
                    <View style={styles.chipsContainer}>
                        {items.map((item, i) => (
                            <View key={i} style={styles.chip}>
                                <Text style={styles.chipText}>{item.name}</Text>
                                <Text style={styles.chipCount}>{item.count}</Text>
                            </View>
                        ))}
                    </View>
                </View>
            );
        })}
    </View>
);

const StageDistribution = ({ stages, total, narratives: rawNarratives = [], onNarrativeClick }) => {
    const { t } = useTranslation();
    const stageColors = { emerging: '#ffd60a', peaking: '#ff453a', stabilizing: '#30d158', decaying: '#8e8e93' };
    const stageBaseX = { decaying: 0.08, stabilizing: 0.35, emerging: 0.65, peaking: 0.92 };
    const narratives = rawNarratives.filter(n => (n.message_count || 0) > 1);

    if (!narratives || narratives.length === 0) {
        const stageOrder = ['emerging', 'peaking', 'stabilizing', 'decaying'];
        const activeStages = stageOrder.filter(s => (stages[s] || 0) > 0);
        if (activeStages.length === 0) return null;
        return (
            <View style={{ gap: 8 }}>
                <View style={{ position: 'relative', height: 60, justifyContent: 'center' }}>
                    <div style={{ position: 'absolute', bottom: 28, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${THEME.textMuted}40, ${THEME.primary})`, borderRadius: 1 }} />
                    <div style={{ position: 'absolute', bottom: 8, left: 0, fontSize: 10, color: THEME.textMuted }}>{t('app.brandDashboard.fading')}</div>
                    <div style={{ position: 'absolute', bottom: 8, right: 0, fontSize: 10, color: THEME.primary }}>{t('app.brandDashboard.trending')}</div>
                </View>
                <View style={styles.stageLegend}>
                    {stageOrder.filter(s => (stages[s] || 0) > 0).map(stage => (
                        <View key={stage} style={styles.stageLegendItem}>
                            <View style={[styles.stageDot, { backgroundColor: stageColors[stage] }]} />
                            <Text style={styles.stageLegendText}>{stage}: {stages[stage] || 0}</Text>
                        </View>
                    ))}
                </View>
            </View>
        );
    }

    const ROW_HEIGHT = 44;
    const buckets = { decaying: [], stabilizing: [], emerging: [], peaking: [] };
    narratives.forEach((n) => {
        const stage = n.narrative_stage || 'emerging';
        if (buckets[stage]) buckets[stage].push(n);
    });
    const rows = [];
    Object.entries(buckets).forEach(([stage, items]) => {
        items.forEach((n, idx) => {
            const baseX = stageBaseX[stage] || 0.5;
            const velAdjust = Math.min(0.08, Math.max(-0.08, (n.velocity_1h || 0) / 100));
            const xPct = Math.min(92, Math.max(5, (baseX + velAdjust) * 100));
            rows.push({ ...n, xPct, row: idx });
        });
    });
    const maxRow = Math.max(...rows.map(r => r.row), 0);
    const chartH = Math.max(180, (maxRow + 1) * ROW_HEIGHT + 60);

    return (
        <View style={{ gap: 8 }}>
            <div style={{ position: 'relative', height: chartH, padding: '8px 0 36px' }}>
                <div style={{ position: 'absolute', bottom: 28, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${THEME.textMuted}40, ${THEME.primary})`, borderRadius: 1 }} />
                <div style={{ position: 'absolute', bottom: 8, left: 0, fontSize: 10, color: THEME.textMuted }}>{t('app.brandDashboard.fading')}</div>
                <div style={{ position: 'absolute', bottom: 8, right: 0, fontSize: 10, color: THEME.primary }}>{t('app.brandDashboard.trending')}</div>
                {rows.slice(0, 12).map((n, i) => (
                    <div key={i} onClick={() => onNarrativeClick?.(n.id)} style={{ position: 'absolute', left: `${n.xPct}%`, top: `${16 + n.row * ROW_HEIGHT}px`, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, maxWidth: 130, cursor: 'pointer' }}>
                        <div style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: stageColors[n.narrative_stage] || THEME.textMuted, boxShadow: `0 0 6px ${stageColors[n.narrative_stage] || THEME.textMuted}60` }} />
                        <span style={{ fontSize: 10, color: THEME.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110, textAlign: 'center' }}>{(n.title || '').slice(0, 22)}</span>
                    </div>
                ))}
            </div>
            <View style={styles.stageLegend}>
                {Object.entries(stageColors).map(([stage, color]) => (
                    <View key={stage} style={styles.stageLegendItem}>
                        <View style={[styles.stageDot, { backgroundColor: color }]} />
                        <Text style={styles.stageLegendText}>{t(`app.brandDashboard.stage${stage.charAt(0).toUpperCase() + stage.slice(1)}`)}</Text>
                    </View>
                ))}
            </View>
        </View>
    );
};


// ============================================================================
// Engagement KPI Strip
// ============================================================================

const formatCompact = (n) => {
    if (n == null) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return n.toLocaleString();
};

const EngagementKPIStrip = ({ data, isDesktop, globalRange, onStatClick }) => {
    const { t } = useTranslation();
    const e = data.engagement_totals;
    if (!e || (e.total_views === 0 && e.total_likes === 0)) return null;

    const totalEngagement = (e.total_likes || 0) + (e.total_comments || 0) + (e.total_shares || 0);
    const engagementRate = e.total_views > 0 ? ((totalEngagement / e.total_views) * 100).toFixed(1) : '0';

    const ep = data.engagement_totals_prev;
    const reachDelta = ep && ep.total_views > 0 ? formatDelta(e.total_views, ep.total_views) : null;
    const prevTotalEng = ep ? (ep.total_likes || 0) + (ep.total_comments || 0) + (ep.total_shares || 0) : 0;
    const engagementDelta = ep && prevTotalEng > 0 ? formatDelta(totalEngagement, prevTotalEng) : null;
    const sharesDelta = ep && ep.total_shares > 0 ? formatDelta(e.total_shares, ep.total_shares) : null;
    const rateDeltaVal = data.engagement_rate_delta;

    const clickable = (sort, children) => onStatClick ? (
        <TouchableOpacity onPress={() => onStatClick(sort)} style={isDesktop ? { flex: 1, display: 'flex' } : { display: 'flex' }}>
            {children}
        </TouchableOpacity>
    ) : children;

    return (
        <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6, paddingHorizontal: 2 }}>
                <Text style={{ color: THEME.textTertiary, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    {globalRange === '24h' ? t('app.brandDashboard.last24h') : globalRange === '30d' ? t('app.brandDashboard.last30days') : t('app.brandDashboard.last7days')} — TikTok & Instagram
                </Text>
            </View>
            <View style={[styles.statsRow, !isDesktop && { flexWrap: 'wrap' }]}>
                {clickable('views', <StatCard label={t('app.brandDashboard.totalReach')} value={formatCompact(e.total_views)} color="#00d4ff" icon={<FaEye size={13} color="#00d4ff" />} isDesktop={!onStatClick && isDesktop} info={t('app.brandDashboard.totalReachInfo')} secondaryValue={reachDelta ? `${reachDelta.arrow}${reachDelta.pctChange}` : null} secondaryColor={reachDelta?.color} />)}
                {clickable('likes', <StatCard label={t('app.brandDashboard.engagement')} value={formatCompact(totalEngagement)} color="#ff6b9d" icon={<FaHeart size={13} color="#ff6b9d" />} isDesktop={!onStatClick && isDesktop} info={t('app.brandDashboard.engagementInfo')} secondaryValue={engagementDelta ? `${engagementDelta.arrow}${engagementDelta.pctChange}` : null} secondaryColor={engagementDelta?.color} />)}
                {clickable('shares', <StatCard label={t('app.brandDashboard.shares')} value={formatCompact(e.total_shares)} color="#30d158" icon={<FaShareAlt size={13} color="#30d158" />} isDesktop={!onStatClick && isDesktop} info={t('app.brandDashboard.sharesInfo')} secondaryValue={sharesDelta ? `${sharesDelta.arrow}${sharesDelta.pctChange}` : null} secondaryColor={sharesDelta?.color} />)}
                <StatCard label={t('app.brandDashboard.engagementRate')} value={engagementRate} suffix="%" color="#a78bfa" icon={<FaChartLine size={13} color="#a78bfa" />} isDesktop={isDesktop} info={t('app.brandDashboard.engagementRateInfo')} secondaryValue={rateDeltaVal != null ? `${rateDeltaVal > 0 ? '↑' : '↓'}${Math.abs(rateDeltaVal).toFixed(1)}% ${t('app.brandDashboard.vsPrevPeriod')}` : null} secondaryColor={rateDeltaVal != null ? (rateDeltaVal > 0 ? '#30d158' : '#ff453a') : null} />
            </View>
        </View>
    );
};

// ============================================================================
// Topic Distribution Donut Chart (Dig-style)
// ============================================================================

const DONUT_COLORS = ['#7eb5ff', '#ff9f7f', '#7fdfbf', '#ffcf7f', '#d9a5ff', '#ff7faf', '#7fefef', '#ffef7f', '#c0c0c0'];

const TopicDonutChart = ({ distribution, navigate, onNarrativeClick }) => {
    const [hovered, setHovered] = React.useState(null);
    const [animated, setAnimated] = React.useState(false);
    React.useEffect(() => { const t = setTimeout(() => setAnimated(true), 50); return () => clearTimeout(t); }, []);

    if (!distribution || distribution.length === 0) return null;

    const total = distribution.reduce((s, d) => s + d.post_count, 0);
    if (total === 0) return null;

    const top = distribution.slice(0, 10);
    const otherCount = distribution.slice(10).reduce((s, d) => s + d.post_count, 0);
    const segments = [...top.map((d, i) => ({ ...d, color: DONUT_COLORS[i], pct: (d.post_count / total) * 100 }))];
    if (otherCount > 0) segments.push({ id: 'other', title: 'Other', post_count: otherCount, color: DONUT_COLORS[10] || DONUT_COLORS[8], pct: (otherCount / total) * 100 });

    const cx = 200, cy = 200, r = 150, strokeW = 50;
    const circumference = 2 * Math.PI * r;
    let cumAngle = -90;

    const arcs = segments.map((seg, i) => {
        const angle = (seg.pct / 100) * 360;
        const startAngle = cumAngle;
        cumAngle += angle;
        const dashLen = (seg.pct / 100) * circumference;
        return { ...seg, dashLen, startAngle, idx: i };
    });

    return (
        <View style={{ alignItems: 'center', paddingVertical: 8 }}>
            {/* Donut SVG */}
            <svg viewBox="0 0 400 400" style={{ width: '100%', maxWidth: 380, height: 'auto' }}>
                {arcs.map((arc) => {
                    const finalOffset = -((arc.startAngle + 90) / 360) * circumference;
                    const delay = ((arc.startAngle + 90) / 360) * 0.6;
                    return (
                    <circle
                        key={arc.idx}
                        cx={cx} cy={cy} r={r}
                        fill="none"
                        stroke={hovered === arc.idx ? arc.color : arc.color + 'cc'}
                        strokeWidth={hovered === arc.idx ? strokeW + 8 : strokeW}
                        strokeDasharray={animated ? `${arc.dashLen} ${circumference - arc.dashLen}` : `0 ${circumference}`}
                        strokeDashoffset={finalOffset}
                        style={{ cursor: 'pointer', transition: `stroke-dasharray 0.7s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s, stroke-width 0.2s, stroke 0.2s` }}
                        onMouseEnter={() => setHovered(arc.idx)}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => {
                            if (arc.id === 'other') return;
                            if (onNarrativeClick) onNarrativeClick(arc.id, arc.title);
                            else navigate('/trends', { state: { highlightNarrativeId: arc.id } });
                        }}
                    />
                    );
                })}
                <text x={cx} y={cy - 12} textAnchor="middle" fill={THEME.textMain} fontSize="36" fontWeight="700" fontFamily="'DM Mono', monospace">
                    {formatCompact(total)}
                </text>
                <text x={cx} y={cy + 16} textAnchor="middle" fill={THEME.textTertiary} fontSize="13" fontWeight="500" letterSpacing="1">
                    posts
                </text>
                {hovered != null && arcs[hovered] && (
                    <text x={cx} y={cy + 40} textAnchor="middle" fill={arcs[hovered].color} fontSize="11" fontWeight="600">
                        {arcs[hovered].pct.toFixed(1)}%
                    </text>
                )}
            </svg>

            {/* Legend grid — 2 columns */}
            <View style={{ width: '100%', flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 12, paddingHorizontal: 4 }}>
                {arcs.map((arc) => (
                    <TouchableOpacity
                        key={arc.idx}
                        onPress={() => {
                            if (arc.id === 'other') return;
                            if (onNarrativeClick) onNarrativeClick(arc.id, arc.title);
                            else navigate('/trends', { state: { highlightNarrativeId: arc.id } });
                        }}
                        onMouseEnter={() => setHovered(arc.idx)}
                        onMouseLeave={() => setHovered(null)}
                        style={{
                            flexDirection: 'row', alignItems: 'center', gap: 6,
                            width: '48%',
                            opacity: hovered != null && hovered !== arc.idx ? 0.35 : 1,
                            paddingVertical: 4, paddingHorizontal: 6,
                            borderRadius: 6,
                            backgroundColor: hovered === arc.idx ? arc.color + '12' : 'transparent',
                            cursor: arc.id !== 'other' ? 'pointer' : 'default',
                        }}
                    >
                        <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: arc.color, flexShrink: 0 }} />
                        <Text style={{ flex: 1, color: hovered === arc.idx ? THEME.textMain : THEME.textSecondary, fontSize: 11, fontWeight: hovered === arc.idx ? '600' : '400' }} numberOfLines={1}>
                            {arc.title}
                        </Text>
                        <Text style={{ color: arc.color, fontSize: 11, fontWeight: '700', fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>
                            {arc.pct.toFixed(1)}%
                        </Text>
                    </TouchableOpacity>
                ))}
            </View>
            {onNarrativeClick && (
                <Text style={{ color: THEME.textTertiary, fontSize: 10, marginTop: 8 }}>
                    Click a segment or row to see its posts →
                </Text>
            )}
        </View>
    );
};

// ============================================================================
// Sentiment Stacked Bar Chart (Dig-style)
// ============================================================================

const SentimentStackedBar = ({ breakdown, onNarrativeClick }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'he';
    const [hoveredBar, setHoveredBar] = React.useState(null);
    const [animated, setAnimated] = React.useState(false);
    React.useEffect(() => { const t = setTimeout(() => setAnimated(true), 80); return () => clearTimeout(t); }, []);
    if (!breakdown || breakdown.length === 0) return null;

    const maxTotal = Math.max(...breakdown.map(b => b.total), 1);
    const labelH = 18;
    const barH = 24;
    const rowH = labelH + barH + 16;
    const barAreaW = 560;
    const totalW = barAreaW + 50;
    const totalH = breakdown.length * rowH;

    return (
        <View style={{ marginTop: 8 }}>
            <svg viewBox={`0 0 ${totalW} ${totalH}`} style={{ width: '100%', height: 'auto' }}>
                {breakdown.map((b, i) => {
                    const rowY = i * rowH;
                    const barY = rowY + labelH;
                    const totalW_bar = (b.total / maxTotal) * barAreaW;
                    const posW = b.total > 0 ? (b.positive / b.total) * totalW_bar : 0;
                    const neuW = b.total > 0 ? (b.neutral / b.total) * totalW_bar : 0;
                    const negW = b.total > 0 ? (b.negative / b.total) * totalW_bar : 0;

                    const posX = 0;
                    const neuX = posX + posW;
                    const negX = neuX + neuW;

                    return (
                        <g key={b.id}
                            onClick={() => onNarrativeClick && onNarrativeClick(b.id, b.title)}
                            onMouseEnter={() => setHoveredBar(b.id)}
                            onMouseLeave={() => setHoveredBar(null)}
                            style={{ cursor: onNarrativeClick ? 'pointer' : 'default', opacity: hoveredBar && hoveredBar !== b.id ? 0.45 : 1, transition: 'opacity 0.15s' }}
                        >
                            {/* Title above bar */}
                            <text
                                x={barAreaW / 2}
                                y={rowY + 13}
                                textAnchor="middle"
                                fill={THEME.textSecondary}
                                fontSize="11"
                                fontWeight="500"
                            >
                                {(b.title || '').length > 55 ? (b.title || '').slice(0, 53) + '…' : (b.title || '')}
                            </text>
                            {/* Bar track */}
                            <rect x={0} y={barY} width={barAreaW} height={barH} rx="5" fill={THEME.surface} opacity="0.5" />
                            {/* Positive */}
                            {posW > 1 && <rect x={posX} y={barY} width={animated ? posW : 0} height={barH} rx="5" fill="#4ade80" opacity="0.85" style={{ transition: `width 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${i * 0.08}s` }} />}
                            {/* Neutral */}
                            {neuW > 1 && <rect x={animated ? neuX : 0} y={barY} width={animated ? neuW : 0} height={barH} fill="#fbbf24" opacity="0.85" style={{ transition: `width 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${i * 0.08 + 0.05}s, x 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${i * 0.08}s` }} />}
                            {/* Negative */}
                            {negW > 1 && <rect x={animated ? negX : 0} y={barY} width={animated ? negW : 0} height={barH} rx="5" fill="#f87171" opacity="0.85" style={{ transition: `width 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${i * 0.08 + 0.1}s, x 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${i * 0.08}s` }} />}
                            {/* Count labels inside bars */}
                            {posW > 28 && <text x={posX + posW / 2} y={barY + barH / 2 + 4} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="600">{formatCompact(b.positive)}</text>}
                            {neuW > 28 && <text x={neuX + neuW / 2} y={barY + barH / 2 + 4} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="600">{formatCompact(b.neutral)}</text>}
                            {negW > 28 && <text x={negX + negW / 2} y={barY + barH / 2 + 4} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="600">{formatCompact(b.negative)}</text>}
                            {/* Total after bar */}
                            <text x={totalW_bar + 8} y={barY + barH / 2 + 4} fill={THEME.textTertiary} fontSize="10" fontFamily="'DM Mono', monospace">{b.total}</text>
                        </g>
                    );
                })}
            </svg>

            {/* Legend */}
            <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 20, marginTop: 12 }}>
                {[{ label: t('app.brandDashboard.positive'), color: '#4ade80' }, { label: t('app.brandDashboard.neutral'), color: '#fbbf24' }, { label: t('app.brandDashboard.negative'), color: '#f87171' }].map(l => (
                    <View key={l.label} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: l.color }} />
                        <Text style={{ color: THEME.textSecondary, fontSize: 11, fontWeight: '500' }}>{l.label}</Text>
                    </View>
                ))}
            </View>
            {onNarrativeClick && (
                <Text style={{ color: THEME.textTertiary, fontSize: 10, textAlign: 'center', marginTop: 8 }}>
                    Click a bar to see its posts →
                </Text>
            )}
        </View>
    );
};

// ============================================================================
// Discussion Word Cloud
// ============================================================================

const WORD_CLOUD_COLORS = [
    '#0a84ff', '#30d158', '#ff453a', '#bf5af2', '#ff9f0a',
    '#64d2ff', '#ffd60a', '#ac8e68', '#ff375f', '#5e5ce6',
    '#1da1f2', '#ff0050', '#0088cc', '#e1306c', '#34d399',
];

const DiscussionWordCloud = ({ words, onWordClick }) => {
    const { t } = useTranslation();
    const items = useMemo(() => {
        if (!words || words.length === 0) return [];
        const sorted = [...words].sort((a, b) => (b.trending_score || b.weight) - (a.trending_score || a.weight));
        return sorted.slice(0, 30);
    }, [words]);

    if (!items.length) return null;

    return (
        <div style={{ padding: '12px 4px' }}>
            {/* Legend */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14, paddingLeft: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, color: THEME.textMuted, fontWeight: 500 }}>Aa</span>
                    <span style={{ fontSize: 16, color: THEME.textMuted, fontWeight: 700 }}>Aa</span>
                    <span style={{ fontSize: 11, color: THEME.textMuted }}>{t('app.brandDashboard.wordCloudLegendSize')}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: THEME.textMuted, opacity: 0.4 }} />
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: THEME.textMuted, opacity: 1 }} />
                    <span style={{ fontSize: 11, color: THEME.textMuted }}>{t('app.brandDashboard.wordCloudLegendTrending')}</span>
                </div>
            </div>
            {/* Pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
                {items.map((word, i) => {
                    const score = word.trending_score != null ? word.trending_score : word.weight;
                    const size = Math.max(12, Math.min(20, 12 + word.weight * 10));
                    const color = WORD_CLOUD_COLORS[i % WORD_CLOUD_COLORS.length];
                    const opacity = 0.6 + score * 0.4;
                    return (
                        <span
                            key={word.text}
                            title={`${word.text}: ${word.count}`}
                            onClick={() => onWordClick?.(word.text)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: `${Math.round(4 + word.weight * 4)}px ${Math.round(10 + word.weight * 6)}px`,
                                borderRadius: 20,
                                fontSize: size,
                                fontWeight: word.weight > 0.6 ? 700 : 500,
                                color,
                                opacity,
                                background: `${color}12`,
                                border: `1px solid ${color}25`,
                                cursor: onWordClick ? 'pointer' : 'default',
                                transition: 'transform 0.2s ease, opacity 0.2s ease, background 0.2s ease',
                                whiteSpace: 'nowrap',
                                lineHeight: 1,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = `${color}22`; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.opacity = String(opacity); e.currentTarget.style.background = `${color}12`; }}
                        >
                            {word.text}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

// ============================================================================
// Hashtag Cloud
// ============================================================================

const HashtagCloud = ({ hashtags, onHashtagClick }) => {
    if (!hashtags || hashtags.length === 0) return null;

    const sorted = [...hashtags].sort((a, b) => b.count - a.count);
    const maxCount = Math.max(...sorted.map(h => h.count), 1);

    const allEqual = sorted.every(h => h.count === sorted[0].count);
    const allLow = sorted.every(h => h.count <= 2);

    if (allEqual || allLow) {
        return (
            <View style={{ gap: 8, marginTop: 4 }}>
                <Text style={{ color: THEME.textTertiary, fontSize: 11, fontStyle: 'italic', marginBottom: 4 }}>Not enough data for trend ranking</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                    {sorted.map((h, i) => (
                        <TouchableOpacity
                            key={i}
                            onPress={() => onHashtagClick?.(h.tag)}
                            style={{ paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20, backgroundColor: THEME.surface, borderWidth: 1, borderColor: THEME.cardBorder }}
                        >
                            <Text style={{ color: THEME.textMain, fontSize: 12, fontWeight: '500' }}>#{h.tag} <Text style={{ color: THEME.textTertiary }}>{h.count}</Text></Text>
                        </TouchableOpacity>
                    ))}
                </View>
            </View>
        );
    }

    return (
        <View style={{ gap: 6, marginTop: 4 }}>
            {sorted.map((h, i) => {
                const pct = (h.count / maxCount) * 100;
                const isTop3 = i < 3;
                return (
                    <TouchableOpacity
                        key={i}
                        onPress={() => onHashtagClick?.(h.tag)}
                        activeOpacity={0.7}
                        style={{ flexDirection: 'row', alignItems: 'center', gap: 10, cursor: 'pointer', paddingVertical: 3 }}
                    >
                        <span className="mono-value" style={{
                            color: THEME.textTertiary, fontSize: 11, fontWeight: 600,
                            width: 20, textAlign: 'right', fontFamily: "'DM Mono', monospace",
                        }}>{i + 1}</span>
                        <Text style={{
                            color: isTop3 ? '#ff0050' : THEME.textMain,
                            fontSize: 13, fontWeight: isTop3 ? '700' : '500',
                            width: 160,
                        }} numberOfLines={1}>#{h.tag}</Text>
                        <View style={{ flex: 1, height: 18, borderRadius: 6, backgroundColor: THEME.surface, overflow: 'hidden' }}>
                            <View style={{
                                width: `${pct}%`, height: '100%', borderRadius: 6,
                                backgroundColor: isTop3 ? 'rgba(255, 0, 80, 0.5)' : 'rgba(255, 0, 80, 0.25)',
                                ...(i === 0 ? { boxShadow: '0 0 12px rgba(255, 0, 80, 0.25)' } : {}),
                                transition: 'width 0.3s ease',
                            }} />
                        </View>
                        <span className="mono-value" style={{
                            color: isTop3 ? '#ff0050' : THEME.textSecondary,
                            fontSize: 12, fontWeight: 700, minWidth: 32, textAlign: 'right',
                            fontFamily: "'DM Mono', monospace",
                        }}>{h.count}</span>
                    </TouchableOpacity>
                );
            })}
        </View>
    );
};

// ============================================================================
// Trending Themes
// ============================================================================

const TrendingThemes = ({ themes }) => {
    if (!themes || themes.length === 0) return null;

    const categoryColors = ['#7eb5ff', '#ff9f7f', '#7fdfbf', '#ffcf7f', '#d9a5ff', '#ff7faf', '#7fefef', '#ffef7f'];

    return (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
            {themes.slice(0, 10).map((t, i) => {
                const color = categoryColors[i % categoryColors.length];
                return (
                    <View key={i} style={{
                        flex: 1, minWidth: 240,
                        flexDirection: 'row', alignItems: 'center', gap: 12,
                        padding: 14, borderRadius: 14,
                        backgroundColor: THEME.surface,
                        borderWidth: 1, borderColor: THEME.cardBorder,
                        borderLeftWidth: 3, borderLeftColor: color,
                    }}>
                        <View style={{ flex: 1 }}>
                            <Text style={{ color: THEME.textMain, fontSize: 13, fontWeight: '600', lineHeight: 18 }} numberOfLines={2}>{t.theme}</Text>
                        </View>
                        <View style={{
                            backgroundColor: color + '18', paddingHorizontal: 10, paddingVertical: 4,
                            borderRadius: 10, borderWidth: 1, borderColor: color + '30',
                        }}>
                            <Text style={{ color, fontSize: 12, fontWeight: '700', fontFamily: "'DM Mono', monospace" }}>{t.count}</Text>
                        </View>
                    </View>
                );
            })}
        </View>
    );
};

// ============================================================================
// Key Voices
// ============================================================================

const KeyVoices = ({ voices, onVoiceClick }) => {
    const { t } = useTranslation();
    if (!voices || voices.length === 0) return null;

    return (
        <View style={{ gap: 8 }}>
            {voices.slice(0, 10).map((v, i) => {
                const Icon = PLATFORM_ICONS[v.platform] || FaGlobe;
                const pColor = PLATFORM_COLORS[v.platform] || '#8e8e93';
                return (
                    <div key={i} onClick={() => onVoiceClick && onVoiceClick(v.name, v.platform)} style={{ cursor: onVoiceClick ? 'pointer' : 'default' }}>
                    <View style={{
                        flexDirection: 'row', alignItems: 'center', gap: 12,
                        padding: 12, borderRadius: 12,
                        backgroundColor: i === 0 ? pColor + '08' : 'transparent',
                        borderWidth: 1, borderColor: i === 0 ? pColor + '20' : THEME.cardBorder,
                    }}>
                        <View style={{
                            width: 36, height: 36, borderRadius: 12,
                            backgroundColor: pColor + '15', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Icon size={16} color={pColor} />
                        </View>
                        <View style={{ flex: 1 }}>
                            <Text style={{ color: THEME.textMain, fontSize: 14, fontWeight: '600' }}>{v.name}</Text>
                            <Text style={{ color: THEME.textTertiary, fontSize: 11, marginTop: 2 }}>
                                {v.platform} {v.stance ? `· ${v.stance}` : ''}
                            </Text>
                        </View>
                        <View style={{
                            backgroundColor: THEME.surface, paddingHorizontal: 10, paddingVertical: 4,
                            borderRadius: 8, borderWidth: 1, borderColor: THEME.cardBorder,
                        }}>
                            <Text style={{ color: THEME.textSecondary, fontSize: 12, fontWeight: '600', fontFamily: "'DM Mono', monospace" }}>
                                {v.narrative_count} {v.narrative_count === 1 ? t('app.brandDashboard.narrative') : t('app.brandDashboard.narrativesPlural')}
                            </Text>
                        </View>
                    </View>
                    </div>
                );
            })}
        </View>
    );
};

// ============================================================================
// Enhanced Entity Bars
// ============================================================================

const ENTITY_CATEGORY_COLORS = { people: '#7eb5ff', organizations: '#d9a5ff', locations: '#7fdfbf' };

const EntityBars = ({ entities, onEntityClick }) => {
    if (!entities) return null;

    return (
        <View style={{ gap: 16 }}>
            {['people', 'organizations', 'locations'].map(cat => {
                const items = entities[cat];
                if (!items || items.length === 0) return null;
                const maxCount = Math.max(...items.map(i => i.count), 1);
                const color = ENTITY_CATEGORY_COLORS[cat];
                return (
                    <View key={cat}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color }} />
                            <Text style={{ color: THEME.textSecondary, fontSize: 12, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 1 }}>
                                {cat}
                            </Text>
                        </View>
                        {items.slice(0, 8).map((item, i) => {
                            const pct = (item.count / maxCount) * 100;
                            return (
                                <TouchableOpacity
                                    key={i}
                                    onPress={() => onEntityClick?.(item.name, cat)}
                                    activeOpacity={0.7}
                                    style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 6, cursor: 'pointer' }}
                                >
                                    <Text style={{ color: THEME.textMain, fontSize: 13, fontWeight: '500', width: 140 }} numberOfLines={1}>{item.name}</Text>
                                    <View style={{ flex: 1, height: 20, borderRadius: 6, backgroundColor: THEME.surface, overflow: 'hidden' }}>
                                        <View style={{
                                            width: `${pct}%`, height: '100%', borderRadius: 6,
                                            backgroundColor: color + (i === 0 ? 'cc' : '66'),
                                            ...(i === 0 ? { boxShadow: `0 0 12px ${color}40` } : {}),
                                            transition: 'width 0.3s ease',
                                        }} />
                                    </View>
                                    <span className="mono-value" style={{ color: THEME.textSecondary, fontSize: 12, fontWeight: '600', minWidth: 28, textAlign: 'right' }}>{item.count}</span>
                                </TouchableOpacity>
                            );
                        })}
                    </View>
                );
            })}
        </View>
    );
};

// ============================================================================
// Right Detail Panel (inline entity / hashtag posts)
// ============================================================================

const ENGAGEMENT_SORT_META = {
    views: { label: 'Top Reach', icon: FaEye, color: '#00d4ff' },
    likes: { label: 'Top Engagement', icon: FaHeart, color: '#ff6b9d' },
    comments: { label: 'Most Discussed', icon: FaComment, color: '#fbbf24' },
    shares: { label: 'Most Shared', icon: FaShareAlt, color: '#30d158' },
};

const SENTIMENT_META = {
    positive: { label: 'Positive', color: '#30d158', icon: FaSmile },
    neutral: { label: 'Neutral', color: '#fbbf24', icon: FaMeh },
    negative: { label: 'Negative', color: '#ff453a', icon: FaFrown },
};

const RightDetailPanel = ({ entityModal, hashtagModal, narrativeModal, engagementModal, emotionModal, tagModal, wordCloudModal, competitorModal, platformModal, sentimentModal, authorModal, orgId, range, onClose }) => {
    const { t } = useTranslation();
    const [posts, setPosts] = useState([]);
    const [totalCount, setTotalCount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const PAGE_SIZE = 20;

    const isPlatform = !!platformModal;
    const isSentiment = !isPlatform && !!sentimentModal;
    const isAuthor = !isPlatform && !isSentiment && !!authorModal;
    const isCompetitor = !isPlatform && !isSentiment && !isAuthor && !!competitorModal;
    const isWordCloud = !isPlatform && !isSentiment && !isAuthor && !isCompetitor && !!wordCloudModal;
    const isTag = !isPlatform && !isSentiment && !isAuthor && !isCompetitor && !isWordCloud && !!tagModal;
    const isEmotion = !isPlatform && !isSentiment && !isAuthor && !isCompetitor && !isWordCloud && !isTag && !!emotionModal;
    const isEngagement = !isPlatform && !isSentiment && !isAuthor && !isCompetitor && !isWordCloud && !isTag && !isEmotion && !!engagementModal;
    const isNarrative = !isPlatform && !isSentiment && !isAuthor && !isCompetitor && !isWordCloud && !isTag && !isEngagement && !isEmotion && !!narrativeModal;
    const isEntity = !isPlatform && !isSentiment && !isAuthor && !isCompetitor && !isWordCloud && !isTag && !isNarrative && !isEngagement && !isEmotion && !!entityModal;
    const engMeta = isEngagement ? ENGAGEMENT_SORT_META[engagementModal] : null;
    const sentMeta = isSentiment ? SENTIMENT_META[sentimentModal] : null;
    const label = isPlatform ? platformModal.platform : isSentiment ? sentMeta.label : isAuthor ? (authorModal.username || 'Author') : isCompetitor ? (competitorModal?.name || competitorModal) : isWordCloud ? wordCloudModal : isTag ? tagModal : isEmotion ? emotionModal.emotion : isEngagement ? engMeta.label : isNarrative ? narrativeModal.title : (isEntity ? entityModal.name : `#${hashtagModal}`);
    const subLabel = isPlatform
        ? t('app.brandDashboard.platformDistribution') || 'Platform Posts'
        : isSentiment
            ? t('app.brandDashboard.sentimentOverview') || 'Sentiment'
            : isAuthor
                ? (authorModal.source || 'Posts')
                : isCompetitor
                    ? t('app.brandDashboard.competitorMentions') || 'Competitor Mentions'
                    : isWordCloud
                        ? t('app.brandDashboard.contextOfDiscussion')
                        : isTag
                            ? t('app.brandDashboard.tagDistribution') || 'Tag'
                            : isEmotion
                                ? t('app.brandDashboard.emotionBreakdown')
                                : isEngagement
                                    ? t('app.brandDashboard.last7days') || 'Last 7 days'
                                    : isNarrative
                                        ? t('app.brandDashboard.narrative') || 'Narrative'
                                        : isEntity
                                            ? entityModal.category.charAt(0).toUpperCase() + entityModal.category.slice(1)
                                            : t('app.brandDashboard.tiktokHashtag');
    const accentColor = isPlatform ? (PLATFORM_COLORS[platformModal.platform] || '#8e8e93') : isSentiment ? sentMeta.color : isAuthor ? (PLATFORM_COLORS[authorModal.source] || '#8e8e93') : isCompetitor ? '#ff453a' : isWordCloud ? '#64d2ff' : isTag ? '#3b82f6' : isEmotion ? (EMOTION_COLORS[emotionModal.emotion] || '#8e8e93') : isEngagement ? engMeta.color : isNarrative ? '#0a84ff' : isEntity
        ? (ENTITY_CATEGORY_COLORS[entityModal.category] || '#7eb5ff')
        : '#ff0050';
    const IconComp = isPlatform ? FaGlobe : isSentiment ? sentMeta.icon : isAuthor ? FaUser : isCompetitor ? FaExclamationTriangle : isWordCloud ? FaComment : isTag ? FaListUl : isEmotion ? FaChartLine : isEngagement ? engMeta.icon : isNarrative ? FaChartLine : (isEntity ? FaSearch : FaTiktok);

    const fetchKey = isPlatform
        ? `platform:${platformModal.platform}:${platformModal.listId || ''}`
        : isSentiment
            ? `sentiment:${sentimentModal}`
            : isAuthor
                ? `author:${authorModal.username}:${authorModal.source}`
                : isCompetitor
                    ? `competitor:${competitorModal?.competitor_id || competitorModal?.name || competitorModal}`
                    : isWordCloud
                        ? `word:${wordCloudModal}`
                        : isTag
                            ? `tag:${tagModal}`
                            : isEmotion
                                ? `emotion:${emotionModal.emotion}:${emotionModal.listId || ''}`
                                : isEngagement
                                    ? `engagement:${engagementModal}`
                                    : isNarrative
                                        ? `narrative:${narrativeModal.id}`
                                        : isEntity ? `entity:${entityModal.name}:${entityModal.category}` : `hashtag:${hashtagModal}`;

    useEffect(() => {
        (async () => {
            setLoading(true);
            setPosts([]);
            setTotalCount(null);
            setHasMore(true);
            let result;
            if (isPlatform) {
                result = await ListsManager.fetchPlatformPosts(platformModal.platform, PAGE_SIZE, 0, orgId, platformModal.listId, range) || [];
            } else if (isSentiment) {
                result = await ListsManager.fetchSentimentPosts(sentimentModal, PAGE_SIZE, 0, orgId, range) || [];
            } else if (isAuthor) {
                result = await ListsManager.fetchAuthorPosts(authorModal.username, authorModal.source, PAGE_SIZE, 0, orgId, range) || [];
            } else if (isCompetitor) {
                const compName = competitorModal?.name || competitorModal;
                const compId = competitorModal?.competitor_id || null;
                result = await ListsManager.fetchCompetitorPosts(compName, PAGE_SIZE, 0, orgId, range, compId) || [];
            } else if (isWordCloud) {
                result = await ListsManager.fetchWordPosts(wordCloudModal, PAGE_SIZE, 0, orgId, range) || [];
            } else if (isTag) {
                result = await ListsManager.fetchTagPosts(tagModal, PAGE_SIZE, 0, orgId, range) || [];
            } else if (isEmotion) {
                result = await ListsManager.fetchEmotionPosts(emotionModal.emotion, PAGE_SIZE, 0, orgId, emotionModal.listId, range) || [];
            } else if (isEngagement) {
                result = await ListsManager.fetchEngagementPosts(engagementModal, PAGE_SIZE, 0, orgId, range) || [];
            } else if (isNarrative) {
                const res = await ListsManager.fetchNarrativePosts(narrativeModal.id, PAGE_SIZE, 0);
                result = res?.posts || res || [];
            } else if (isEntity) {
                result = await ListsManager.fetchEntityPosts(entityModal.name, entityModal.category, PAGE_SIZE, 0, orgId, range) || [];
            } else {
                result = await ListsManager.fetchHashtagPosts(hashtagModal, PAGE_SIZE, 0, orgId, range) || [];
            }
            setPosts(result);
            // Server-side total (all matching rows) when available; null → fall back to loaded count.
            setTotalCount(typeof result.totalCount === 'number' ? result.totalCount : null);
            setHasMore(result.length >= PAGE_SIZE);
            setLoading(false);
        })();
    }, [fetchKey, orgId, range]); // eslint-disable-line

    const loadMore = async () => {
        setLoadingMore(true);
        let result;
        if (isPlatform) {
            result = await ListsManager.fetchPlatformPosts(platformModal.platform, PAGE_SIZE, posts.length, orgId, platformModal.listId, range) || [];
        } else if (isSentiment) {
            result = await ListsManager.fetchSentimentPosts(sentimentModal, PAGE_SIZE, posts.length, orgId, range) || [];
        } else if (isAuthor) {
            result = await ListsManager.fetchAuthorPosts(authorModal.username, authorModal.source, PAGE_SIZE, posts.length, orgId, range) || [];
        } else if (isCompetitor) {
            const compName = competitorModal?.name || competitorModal;
            const compId = competitorModal?.competitor_id || null;
            result = await ListsManager.fetchCompetitorPosts(compName, PAGE_SIZE, posts.length, orgId, range, compId) || [];
        } else if (isWordCloud) {
            result = await ListsManager.fetchWordPosts(wordCloudModal, PAGE_SIZE, posts.length, orgId, range) || [];
        } else if (isTag) {
            result = await ListsManager.fetchTagPosts(tagModal, PAGE_SIZE, posts.length, orgId, range) || [];
        } else if (isEmotion) {
            result = await ListsManager.fetchEmotionPosts(emotionModal.emotion, PAGE_SIZE, posts.length, orgId, emotionModal.listId, range) || [];
        } else if (isEngagement) {
            result = await ListsManager.fetchEngagementPosts(engagementModal, PAGE_SIZE, posts.length, orgId, range) || [];
        } else if (isNarrative) {
            const res = await ListsManager.fetchNarrativePosts(narrativeModal.id, PAGE_SIZE, posts.length);
            result = res?.posts || res || [];
        } else if (isEntity) {
            result = await ListsManager.fetchEntityPosts(entityModal.name, entityModal.category, PAGE_SIZE, posts.length, orgId, range) || [];
        } else {
            result = await ListsManager.fetchHashtagPosts(hashtagModal, PAGE_SIZE, posts.length, orgId, range) || [];
        }
        if (result.length > 0) {
            setPosts(prev => [...prev, ...result]);
            if (result.length < PAGE_SIZE) setHasMore(false);
        } else {
            setHasMore(false);
        }
        setLoadingMore(false);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <div style={{
                padding: '14px 16px 12px',
                borderBottom: `1px solid ${THEME.cardBorder}`,
                flexShrink: 0,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                            background: `${accentColor}18`,
                            border: `1px solid ${accentColor}30`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <IconComp size={13} color={accentColor} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ color: THEME.textMain, fontSize: 14, fontWeight: 700, letterSpacing: -0.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {label}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 1 }}>
                                <div style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: accentColor }} />
                                <span style={{ color: THEME.textSecondary, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                    {subLabel}
                                </span>
                                {!loading && (
                                    <span style={{ color: THEME.textTertiary, fontSize: 10 }}>
                                        · {t('app.brandDashboard.postCount', { count: totalCount != null ? totalCount : posts.length })}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <TouchableOpacity onPress={onClose} style={{
                        width: 28, height: 28, borderRadius: 7,
                        backgroundColor: THEME.surface,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', flexShrink: 0,
                    }}>
                        <FaTimes size={12} color={THEME.textSecondary} />
                    </TouchableOpacity>
                </div>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
                {isTag && <TagTimelineChart tag={tagModal} orgId={orgId} />}
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 12 }}>
                        <ActivityIndicator size="small" color={accentColor} />
                        <span style={{ color: THEME.textSecondary, fontSize: 12 }}>{t('app.brandDashboard.searchingPosts')}</span>
                    </div>
                ) : posts.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 10 }}>
                        <IconComp size={20} color={THEME.textTertiary} />
                        <span style={{ color: THEME.textSecondary, fontSize: 13, fontWeight: 600 }}>{t('app.brandDashboard.noPostsFound')}</span>
                        <span style={{ color: THEME.textTertiary, fontSize: 11, textAlign: 'center' }}>{t('app.brandDashboard.noRecentPostsFor', { label })}</span>
                    </div>
                ) : (
                    <div style={{ paddingTop: 8, paddingBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {posts.map((post, i) => (
                            <PlatformPostCard key={post.id || i} post={post} />
                        ))}

                        {hasMore && (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 8px' }}>
                                <TouchableOpacity
                                    onPress={loadMore}
                                    disabled={loadingMore}
                                    style={{
                                        paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8,
                                        backgroundColor: `${accentColor}15`,
                                        border: `1px solid ${accentColor}30`,
                                        cursor: 'pointer',
                                        opacity: loadingMore ? 0.6 : 1,
                                    }}
                                >
                                    {loadingMore ? (
                                        <ActivityIndicator size="small" color={accentColor} />
                                    ) : (
                                        <Text style={{ color: accentColor, fontSize: 11, fontWeight: 700 }}>{t('app.brandDashboard.loadMore')}</Text>
                                    )}
                                </TouchableOpacity>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// Entity Posts Modal
// ============================================================================

const EntityPostsModal = ({ entityName, category, orgId, onClose }) => {
    const { t } = useTranslation();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const PAGE_SIZE = 20;

    useEffect(() => {
        (async () => {
            setLoading(true);
            const result = await ListsManager.fetchEntityPosts(entityName, category, PAGE_SIZE, 0, orgId);
            setPosts(result || []);
            setHasMore(result && result.length >= PAGE_SIZE);
            setLoading(false);
        })();
    }, [entityName, category, orgId]);

    const loadMore = async () => {
        setLoadingMore(true);
        const result = await ListsManager.fetchEntityPosts(entityName, category, PAGE_SIZE, posts.length, orgId);
        if (result && result.length > 0) {
            setPosts(prev => [...prev, ...result]);
            if (result.length < PAGE_SIZE) setHasMore(false);
        } else {
            setHasMore(false);
        }
        setLoadingMore(false);
    };

    const catColor = ENTITY_CATEGORY_COLORS[category] || '#7eb5ff';
    const catLabel = category.charAt(0).toUpperCase() + category.slice(1);

    return ReactDOM.createPortal(
        <div
            onClick={onClose}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.85)',
                backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                zIndex: 9999,
                animation: 'fadeIn 0.2s ease',
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    backgroundColor: THEME.bgCard || '#121214',
                    borderRadius: 20, border: `1px solid ${THEME.cardBorder}`,
                    width: '90%', maxWidth: 680, maxHeight: '85vh',
                    display: 'flex', flexDirection: 'column',
                    boxShadow: '0 24px 60px rgba(0,0,0,0.8)',
                    overflow: 'hidden',
                    animation: 'slideUp 0.25s ease',
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '20px 24px 16px',
                    borderBottom: `1px solid ${THEME.cardBorder}`,
                    flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{
                                width: 36, height: 36, borderRadius: 10,
                                background: `${catColor}18`,
                                border: `1px solid ${catColor}30`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <FaSearch size={14} color={catColor} />
                            </div>
                            <div>
                                <div style={{ color: THEME.textMain, fontSize: 17, fontWeight: 700, letterSpacing: -0.3 }}>
                                    {entityName}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                    <div style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: catColor }} />
                                    <span style={{ color: THEME.textSecondary, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                        {catLabel}
                                    </span>
                                    {!loading && (
                                        <span style={{ color: THEME.textTertiary, fontSize: 11 }}>
                                            · {t('app.brandDashboard.postCount', { count: posts.length })}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <TouchableOpacity onPress={onClose} style={{
                            width: 32, height: 32, borderRadius: 8,
                            backgroundColor: THEME.surface,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer',
                        }}>
                            <FaTimes size={14} color={THEME.textSecondary} />
                        </TouchableOpacity>
                    </div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
                    {loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 12 }}>
                            <ActivityIndicator size="small" color={catColor} />
                            <span style={{ color: THEME.textSecondary, fontSize: 13 }}>{t('app.brandDashboard.searchingPosts')}</span>
                        </div>
                    ) : posts.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 10 }}>
                            <FaSearch size={24} color={THEME.textTertiary} />
                            <span style={{ color: THEME.textSecondary, fontSize: 14, fontWeight: 600 }}>{t('app.brandDashboard.noPostsFound')}</span>
                            <span style={{ color: THEME.textTertiary, fontSize: 12 }}>{t('app.brandDashboard.noRecentPostsMention', { entityName })}</span>
                        </div>
                    ) : (
                        <div style={{ paddingTop: 12, paddingBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {posts.map((post, i) => (
                                <PlatformPostCard key={post.id || i} post={post} />
                            ))}

                            {hasMore && (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 8px' }}>
                                    <TouchableOpacity
                                        onPress={loadMore}
                                        disabled={loadingMore}
                                        style={{
                                            paddingHorizontal: 20, paddingVertical: 10, borderRadius: 10,
                                            backgroundColor: `${catColor}15`,
                                            border: `1px solid ${catColor}30`,
                                            cursor: 'pointer',
                                            opacity: loadingMore ? 0.6 : 1,
                                        }}
                                    >
                                        {loadingMore ? (
                                            <ActivityIndicator size="small" color={catColor} />
                                        ) : (
                                            <Text style={{ color: catColor, fontSize: 12, fontWeight: 700 }}>{t('app.brandDashboard.loadMore')}</Text>
                                        )}
                                    </TouchableOpacity>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            `}} />
        </div>,
        document.body
    );
};

// ============================================================================
// Hashtag Posts Modal
// ============================================================================

const HashtagPostsModal = ({ tag, orgId, onClose }) => {
    const { t } = useTranslation();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const PAGE_SIZE = 20;
    const accentColor = '#ff0050';

    useEffect(() => {
        (async () => {
            setLoading(true);
            const result = await ListsManager.fetchHashtagPosts(tag, PAGE_SIZE, 0, orgId);
            setPosts(result || []);
            setHasMore(result && result.length >= PAGE_SIZE);
            setLoading(false);
        })();
    }, [tag, orgId]);

    const loadMore = async () => {
        setLoadingMore(true);
        const result = await ListsManager.fetchHashtagPosts(tag, PAGE_SIZE, posts.length, orgId);
        if (result && result.length > 0) {
            setPosts(prev => [...prev, ...result]);
            if (result.length < PAGE_SIZE) setHasMore(false);
        } else {
            setHasMore(false);
        }
        setLoadingMore(false);
    };

    return ReactDOM.createPortal(
        <div
            onClick={onClose}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.85)',
                backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                zIndex: 9999,
                animation: 'fadeIn 0.2s ease',
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    backgroundColor: THEME.bgCard || '#121214',
                    borderRadius: 20, border: `1px solid ${THEME.cardBorder}`,
                    width: '90%', maxWidth: 680, maxHeight: '85vh',
                    display: 'flex', flexDirection: 'column',
                    boxShadow: '0 24px 60px rgba(0,0,0,0.8)',
                    overflow: 'hidden',
                    animation: 'slideUp 0.25s ease',
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '20px 24px 16px',
                    borderBottom: `1px solid ${THEME.cardBorder}`,
                    flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{
                                width: 36, height: 36, borderRadius: 10,
                                background: `${accentColor}18`,
                                border: `1px solid ${accentColor}30`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <FaTiktok size={14} color={accentColor} />
                            </div>
                            <div>
                                <div style={{ color: THEME.textMain, fontSize: 17, fontWeight: 700, letterSpacing: -0.3 }}>
                                    #{tag}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                    <span style={{ color: THEME.textSecondary, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                        {t('app.brandDashboard.tiktokHashtag')}
                                    </span>
                                    {!loading && (
                                        <span style={{ color: THEME.textTertiary, fontSize: 11 }}>
                                            · {t('app.brandDashboard.postCount', { count: posts.length })}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <TouchableOpacity onPress={onClose} style={{
                            width: 32, height: 32, borderRadius: 8,
                            backgroundColor: THEME.surface,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer',
                        }}>
                            <FaTimes size={14} color={THEME.textSecondary} />
                        </TouchableOpacity>
                    </div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
                    {loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 12 }}>
                            <ActivityIndicator size="small" color={accentColor} />
                            <span style={{ color: THEME.textSecondary, fontSize: 13 }}>{t('app.brandDashboard.searchingPosts')}</span>
                        </div>
                    ) : posts.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 10 }}>
                            <FaTiktok size={24} color={THEME.textTertiary} />
                            <span style={{ color: THEME.textSecondary, fontSize: 14, fontWeight: 600 }}>{t('app.brandDashboard.noPostsFound')}</span>
                            <span style={{ color: THEME.textTertiary, fontSize: 12 }}>{t('app.brandDashboard.noRecentTikTokPostsWithTag', { tag })}</span>
                        </div>
                    ) : (
                        <div style={{ paddingTop: 12, paddingBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {posts.map((post, i) => (
                                <PlatformPostCard key={post.id || i} post={{ ...post, source: post.source || 'tiktok' }} />
                            ))}

                            {hasMore && (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 8px' }}>
                                    <TouchableOpacity
                                        onPress={loadMore}
                                        disabled={loadingMore}
                                        style={{
                                            paddingHorizontal: 20, paddingVertical: 10, borderRadius: 10,
                                            backgroundColor: `${accentColor}15`,
                                            border: `1px solid ${accentColor}30`,
                                            cursor: 'pointer',
                                            opacity: loadingMore ? 0.6 : 1,
                                        }}
                                    >
                                        {loadingMore ? (
                                            <ActivityIndicator size="small" color={accentColor} />
                                        ) : (
                                            <Text style={{ color: accentColor, fontSize: 12, fontWeight: 700 }}>{t('app.brandDashboard.loadMore')}</Text>
                                        )}
                                    </TouchableOpacity>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            `}} />
        </div>,
        document.body
    );
};

// ============================================================================
// Narratives Hub (3-tab toggle)
// ============================================================================

const EngagementBarList = ({ engagementData, navigate, onNarrativeClick }) => {
    const { t } = useTranslation();
    if (!engagementData || engagementData.length === 0) {
        return <Text style={styles.noDataText}>{t('app.brandDashboard.noEngagementData')}</Text>;
    }

    const maxEngagement = Math.max(...engagementData.map(e => (e.total_views || 0) + (e.total_likes || 0)), 1);

    return (
        <View style={{ gap: 8 }}>
            {engagementData.map((e) => {
                const total = (e.total_views || 0) + (e.total_likes || 0);
                const pct = (total / maxEngagement) * 100;
                const sentColor = (e.avg_sentiment || 0) > 0.1 ? '#4ade80' : (e.avg_sentiment || 0) < -0.1 ? '#f87171' : '#fbbf24';
                return (
                    <TouchableOpacity key={e.id} onPress={() => onNarrativeClick ? onNarrativeClick(e.id, e.title) : navigate('/trends', { state: { highlightNarrativeId: e.id } })}
                        style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 6 }}>
                        <Text style={{ color: THEME.textMain, fontSize: 13, fontWeight: '500', width: 150 }} numberOfLines={1}>{e.title}</Text>
                        <View style={{ flex: 1, height: 24, borderRadius: 8, backgroundColor: THEME.surface, overflow: 'hidden' }}>
                            <View style={{ width: `${pct}%`, height: '100%', borderRadius: 8, background: `linear-gradient(90deg, ${sentColor}88, ${sentColor}cc)` }} />
                        </View>
                        <View style={{ flexDirection: 'row', gap: 8, minWidth: 100, justifyContent: 'flex-end' }}>
                            {e.total_views > 0 && (
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                                    <FaEye size={10} color="#00d4ff" />
                                    <span className="mono-value" style={{ color: THEME.textSecondary, fontSize: 11 }}>{formatCompact(e.total_views)}</span>
                                </View>
                            )}
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                                <FaHeart size={10} color="#ff6b9d" />
                                <span className="mono-value" style={{ color: THEME.textSecondary, fontSize: 11 }}>{formatCompact(e.total_likes)}</span>
                            </View>
                        </View>
                    </TouchableOpacity>
                );
            })}
        </View>
    );
};

const NarrativesHub = ({ data, navigate }) => {
    const { t } = useTranslation();
    const [tab, setTab] = useState('hottest');

    const hasEngagement = data.narrative_engagement && data.narrative_engagement.length > 0;

    const tabs = [
        { key: 'hottest', label: t('app.brandDashboard.hottest'), activeStyle: styles.tabItemActiveBlue, color: '#0a84ff' },
        { key: 'threats', label: t('app.brandDashboard.threats'), activeStyle: styles.tabItemActiveNeg, color: '#ff453a' },
        { key: 'opportunities', label: t('app.brandDashboard.opportunities'), activeStyle: styles.tabItemActivePos, color: '#30d158' },
        ...(hasEngagement ? [{ key: 'engagement', label: t('app.brandDashboard.engagement'), activeStyle: { backgroundColor: '#ff005015', borderWidth: 1, borderColor: '#ff005030' }, color: '#ff0050' }] : []),
    ];

    const narratives = tab === 'hottest' ? data.hottest_narratives
        : tab === 'threats' ? data.top_threats
        : tab === 'opportunities' ? data.top_opportunities
        : null;

    const variant = tab === 'threats' ? 'threat' : tab === 'opportunities' ? 'opportunity' : undefined;

    if ((!data.hottest_narratives || data.hottest_narratives.length === 0) &&
        (!data.top_threats || data.top_threats.length === 0) &&
        (!data.top_opportunities || data.top_opportunities.length === 0) &&
        !hasEngagement) {
        return null;
    }

    return (
        <SectionCard title={t('app.brandDashboard.narratives')} icon={<FaFire size={16} color="#ff9f0a" />}>
            <View style={styles.tabContainer}>
                {tabs.map(({ key, label, activeStyle, color }) => (
                    <TouchableOpacity
                        key={key}
                        onPress={() => setTab(key)}
                        style={[styles.tabItem, tab === key && activeStyle]}
                    >
                        <Text style={[styles.tabText, tab === key && { color }]}>{label}</Text>
                    </TouchableOpacity>
                ))}
            </View>
            <View style={{ marginTop: 12 }}>
                {tab === 'engagement' ? (
                    <EngagementBarList engagementData={data.narrative_engagement} navigate={navigate} />
                ) : narratives && narratives.length > 0 ? (
                    narratives.filter(n => n.title || n.summary).map((n) => (
                        <NarrativeCard key={n.id} narrative={n} onPress={() => navigate('/trends', { state: { highlightNarrativeId: n.id } })} variant={variant} />
                    ))
                ) : (
                    <Text style={styles.noDataText}>{t('app.brandDashboard.noNarrativesTab', { tab: tab === 'hottest' ? t('app.brandDashboard.hot') : t(`app.brandDashboard.${tab}`) })}</Text>
                )}
            </View>
        </SectionCard>
    );
};

// ============================================================================
// Top Influencers Table (screenshot-style rich table)
// ============================================================================

const TopInfluencersTable = ({ sources, onSourceClick }) => {
    const { t } = useTranslation();
    const [sortBy, setSortBy] = useState('post_count');
    const [sortDir, setSortDir] = useState('desc');

    const handleSort = (field) => {
        if (sortBy === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
        else { setSortBy(field); setSortDir('desc'); }
    };

    const sorted = useMemo(() => {
        if (!sources) return [];
        const dir = sortDir === 'asc' ? -1 : 1;
        return [...sources].sort((a, b) => {
            if (sortBy === 'avg_sentiment') return ((b.avg_sentiment || 0) - (a.avg_sentiment || 0)) * dir;
            return ((b[sortBy] || 0) - (a[sortBy] || 0)) * dir;
        });
    }, [sources, sortBy, sortDir]);

    const SortHeader = ({ field, label, flex, textAlign }) => (
        <TouchableOpacity onPress={() => handleSort(field)} style={{ flex, flexDirection: 'row', alignItems: 'center', justifyContent: textAlign === 'center' ? 'center' : 'flex-start', gap: 3 }}>
            <Text style={{ color: sortBy === field ? THEME.primary : THEME.textTertiary, fontSize: 10, fontWeight: '700', textTransform: 'uppercase' }}>{label}</Text>
            {sortBy === field && <Text style={{ color: THEME.primary, fontSize: 8 }}>{sortDir === 'desc' ? '▼' : '▲'}</Text>}
        </TouchableOpacity>
    );

    return (
        <View style={{ gap: 8 }}>
            {/* Header row */}
            <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 8, borderBottomWidth: 1, borderBottomColor: THEME.cardBorder }}>
                <View style={{ flex: 3 }}><Text style={{ color: THEME.textTertiary, fontSize: 10, fontWeight: '700', textTransform: 'uppercase' }}>{t('app.brandDashboard.author')}</Text></View>
                <SortHeader field="post_count" label={t('app.brandDashboard.posts')} flex={1} textAlign="center" />
                <SortHeader field="follower_count" label={t('app.brandDashboard.followers')} flex={1.2} textAlign="center" />
                <SortHeader field="avg_sentiment" label={t('app.brandDashboard.sentiment')} flex={2.5} textAlign="center" />
            </View>
            {sorted.map((s, i) => {
                const Icon = PLATFORM_ICONS[s.source];
                const pos = s.positive_posts || 0;
                const neu = s.neutral_posts || 0;
                const neg = s.negative_posts || 0;
                const sentTotal = pos + neu + neg || 1;

                return (
                    <div key={i} className="table-row-hover" onClick={() => onSourceClick && onSourceClick(s.username, s.source)} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '8px', borderRadius: 8, cursor: onSourceClick ? 'pointer' : 'default' }}>
                        {/* Author */}
                        <div style={{ flex: 3, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <div style={{ position: 'relative' }}>
                                {s.profile_image_url ? (
                                    <img src={s.profile_image_url} alt="" style={{ width: 32, height: 32, borderRadius: 16, objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ width: 32, height: 32, borderRadius: 16, background: THEME.cardHover, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <FaUsers size={14} color={THEME.textTertiary} />
                                    </div>
                                )}
                                {Icon && (
                                    <div style={{ position: 'absolute', bottom: -2, right: -2, width: 14, height: 14, borderRadius: 7, background: THEME.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Icon size={9} color={PLATFORM_COLORS[s.source] || '#8e8e93'} />
                                    </div>
                                )}
                            </div>
                            <div style={{ overflow: 'hidden' }}>
                                <div style={{ color: THEME.textMain, fontSize: 12, fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {s.name || s.username}
                                </div>
                                <div style={{ color: THEME.textTertiary, fontSize: 10 }}>@{s.username}</div>
                            </div>
                        </div>
                        {/* Posts */}
                        <div style={{ flex: 1, textAlign: 'center' }}>
                            <span style={{ color: THEME.textMain, fontSize: 12, fontWeight: '600' }}>{s.post_count}</span>
                        </div>
                        {/* Followers */}
                        <div style={{ flex: 1.2, textAlign: 'center' }}>
                            <span style={{ color: THEME.textSecondary, fontSize: 11 }}>{formatCompact(s.follower_count)}</span>
                        </div>
                        {/* Sentiment bar */}
                        <div style={{ flex: 2.5, display: 'flex', flexDirection: 'column', gap: 2, padding: '0 6px' }}>
                            <div style={{ display: 'flex', flexDirection: 'row', height: 6, borderRadius: 3, overflow: 'hidden', gap: 1 }}>
                                {pos > 0 && <div style={{ width: `${(pos / sentTotal) * 100}%`, background: '#30d158' }} />}
                                {neu > 0 && <div style={{ width: `${(neu / sentTotal) * 100}%`, background: '#fbbf24' }} />}
                                {neg > 0 && <div style={{ width: `${(neg / sentTotal) * 100}%`, background: '#ff453a' }} />}
                            </div>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                <span style={{ color: '#30d158', fontSize: 9 }}>{Math.round((pos / sentTotal) * 100)}%</span>
                                <span style={{ color: '#fbbf24', fontSize: 9 }}>{Math.round((neu / sentTotal) * 100)}%</span>
                                <span style={{ color: '#ff453a', fontSize: 9 }}>{Math.round((neg / sentTotal) * 100)}%</span>
                            </div>
                        </div>
                    </div>
                );
            })}
        </View>
    );
};

// ============================================================================
// Most Engaging Posts Section
// ============================================================================

const MostEngagingPostsSection = ({ orgId }) => {
    const { t } = useTranslation();
    const [posts, setPosts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState('views');

    useEffect(() => {
        setLoading(true);
        ListsManager.fetchEngagementPosts(sortBy, 10, 0, orgId).then(result => {
            setPosts(result || []);
            setLoading(false);
        });
    }, [sortBy, orgId]);

    if (loading) return <SectionCard title={t('app.brandDashboard.mostEngagingPosts')} info={t('app.brandDashboard.mostEngagingPostsInfo')}><ActivityIndicator color={THEME.primary} /></SectionCard>;
    if (!posts || posts.length === 0) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noEngagementData')}</div>;

    return (
        <SectionCard title={t('app.brandDashboard.mostEngagingPosts')} icon={<FaBolt size={14} color="#ff0050" />} info={t('app.brandDashboard.mostEngagingPostsInfo')} headerRight={
            <View style={{ flexDirection: 'row', borderRadius: 8, overflow: 'hidden', borderWidth: 1, borderColor: THEME.cardBorder }}>
                {['views', 'likes', 'comments', 'shares'].map(s => (
                    <TouchableOpacity key={s} onPress={() => setSortBy(s)} style={{ paddingHorizontal: 10, paddingVertical: 4, backgroundColor: sortBy === s ? THEME.primary + '20' : 'transparent' }}>
                        <Text style={{ color: sortBy === s ? THEME.primary : THEME.textSecondary, fontSize: 10, fontWeight: '600' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</Text>
                    </TouchableOpacity>
                ))}
            </View>
        }>
            <View style={{ gap: 6 }}>
                {posts.map((post, i) => {
                    const Icon = PLATFORM_ICONS[post.source];
                    const sentInfo = formatSentiment(post.sentiment_score);
                    const postUrl = post.post_url;
                    return (
                        <TouchableOpacity key={post.id || i} onPress={() => postUrl && window.open(postUrl, '_blank')} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, padding: 10, borderRadius: 10, backgroundColor: THEME.surface }}>
                            <View style={{ flexShrink: 0, alignItems: 'center', gap: 4 }}>
                                <Text style={{ color: THEME.textTertiary, fontSize: 10, fontWeight: '700' }}>#{i + 1}</Text>
                                {Icon && <Icon size={14} color={PLATFORM_COLORS[post.source]} />}
                            </View>
                            <View style={{ flex: 1, gap: 4 }}>
                                <Text numberOfLines={2} style={{ color: THEME.textMain, fontSize: 12, lineHeight: 16 }}>
                                    {post.translated_content || post.content || '—'}
                                </Text>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <Text style={{ color: THEME.textTertiary, fontSize: 10 }}>@{post.author_username}</Text>
                                    <Text style={{ color: sentInfo.color, fontSize: 10, fontWeight: '600' }}>{sentInfo.pct}</Text>
                                </View>
                            </View>
                            <View style={{ flexShrink: 0, alignItems: 'flex-end', gap: 3 }}>
                                {post.views_count > 0 && <Text style={{ color: THEME.textSecondary, fontSize: 10 }}><FaEye size={9} /> {formatCompact(post.views_count)}</Text>}
                                {post.likes_count > 0 && <Text style={{ color: '#ff6b9d', fontSize: 10 }}><FaHeart size={9} /> {formatCompact(post.likes_count)}</Text>}
                                {post.comments_count > 0 && <Text style={{ color: '#64d2ff', fontSize: 10 }}><FaComment size={9} /> {formatCompact(post.comments_count)}</Text>}
                                {post.shares_count > 0 && <Text style={{ color: '#30d158', fontSize: 10 }}><FaShareAlt size={9} /> {formatCompact(post.shares_count)}</Text>}
                            </View>
                        </TouchableOpacity>
                    );
                })}
            </View>
        </SectionCard>
    );
};

// ============================================================================
// Sentiment Donut Chart (pie chart)
// ============================================================================

const SentimentDonutChart = ({ dist24h, dist7d, onSentimentClick }) => {
    const { t } = useTranslation();
    const [animated, setAnimated] = React.useState(false);
    React.useEffect(() => { const t = setTimeout(() => setAnimated(true), 50); return () => clearTimeout(t); }, []);
    const dist = dist7d || dist24h;
    if (!dist) return null;

    const total = (dist.positive || 0) + (dist.neutral || 0) + (dist.negative || 0);
    if (total === 0) return null;

    const pos = dist.positive || 0;
    const neu = dist.neutral || 0;
    const neg = dist.negative || 0;
    const posPct = Math.round((pos / total) * 100);
    const neuPct = Math.round((neu / total) * 100);
    const negPct = 100 - posPct - neuPct;

    const size = 180;
    const strokeWidth = 28;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const center = size / 2;

    const posArc = (pos / total) * circumference;
    const neuArc = (neu / total) * circumference;
    const negArc = (neg / total) * circumference;

    const posOffset = 0;
    const neuOffset = -(posArc);
    const negOffset = -(posArc + neuArc);

    return (
        <View style={{ gap: 16 }}>
            <View style={{ alignItems: 'center', gap: 20 }}>
                <View style={{ position: 'relative', width: size, height: size }}>
                    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
                        <circle cx={center} cy={center} r={radius} fill="none" stroke="#30d158" strokeWidth={strokeWidth}
                            strokeDasharray={animated ? `${posArc} ${circumference - posArc}` : `0 ${circumference}`} strokeDashoffset={posOffset}
                            style={{ cursor: onSentimentClick ? 'pointer' : 'default', transition: `stroke-dasharray 0.7s cubic-bezier(0.4, 0, 0.2, 1) 0s` }}
                            onClick={() => onSentimentClick && onSentimentClick('positive')} />
                        <circle cx={center} cy={center} r={radius} fill="none" stroke="#fbbf24" strokeWidth={strokeWidth}
                            strokeDasharray={animated ? `${neuArc} ${circumference - neuArc}` : `0 ${circumference}`} strokeDashoffset={neuOffset}
                            style={{ cursor: onSentimentClick ? 'pointer' : 'default', transition: `stroke-dasharray 0.7s cubic-bezier(0.4, 0, 0.2, 1) ${(pos / total) * 0.6}s` }}
                            onClick={() => onSentimentClick && onSentimentClick('neutral')} />
                        <circle cx={center} cy={center} r={radius} fill="none" stroke="#ff453a" strokeWidth={strokeWidth}
                            strokeDasharray={animated ? `${negArc} ${circumference - negArc}` : `0 ${circumference}`} strokeDashoffset={negOffset}
                            style={{ cursor: onSentimentClick ? 'pointer' : 'default', transition: `stroke-dasharray 0.7s cubic-bezier(0.4, 0, 0.2, 1) ${((pos + neu) / total) * 0.6}s` }}
                            onClick={() => onSentimentClick && onSentimentClick('negative')} />
                    </svg>
                    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, justifyContent: 'center', alignItems: 'center' }}>
                        <Text style={{ color: THEME.textMain, fontSize: 22, fontWeight: '700' }}>{total}</Text>
                        <Text style={{ color: THEME.textTertiary, fontSize: 10 }}>{t('app.brandDashboard.posts')}</Text>
                    </View>
                </View>
                <View style={{ flexDirection: 'row', gap: 24 }}>
                    <div onClick={() => onSentimentClick && onSentimentClick('positive')} style={{ cursor: onSentimentClick ? 'pointer' : 'default' }}>
                    <View style={{ alignItems: 'center', gap: 4 }}>
                        <View style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: '#30d158' }} />
                        <Text style={{ color: '#30d158', fontSize: 13, fontWeight: '700' }}>{posPct}%</Text>
                        <Text style={{ color: THEME.textTertiary, fontSize: 10 }}>{t('app.brandDashboard.positive')}</Text>
                        <Text style={{ color: THEME.textSecondary, fontSize: 11 }}>{pos}</Text>
                    </View>
                    </div>
                    <div onClick={() => onSentimentClick && onSentimentClick('neutral')} style={{ cursor: onSentimentClick ? 'pointer' : 'default' }}>
                    <View style={{ alignItems: 'center', gap: 4 }}>
                        <View style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: '#fbbf24' }} />
                        <Text style={{ color: '#fbbf24', fontSize: 13, fontWeight: '700' }}>{neuPct}%</Text>
                        <Text style={{ color: THEME.textTertiary, fontSize: 10 }}>{t('app.brandDashboard.neutral')}</Text>
                        <Text style={{ color: THEME.textSecondary, fontSize: 11 }}>{neu}</Text>
                    </View>
                    </div>
                    <div onClick={() => onSentimentClick && onSentimentClick('negative')} style={{ cursor: onSentimentClick ? 'pointer' : 'default' }}>
                    <View style={{ alignItems: 'center', gap: 4 }}>
                        <View style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: '#ff453a' }} />
                        <Text style={{ color: '#ff453a', fontSize: 13, fontWeight: '700' }}>{negPct}%</Text>
                        <Text style={{ color: THEME.textTertiary, fontSize: 10 }}>{t('app.brandDashboard.negative')}</Text>
                        <Text style={{ color: THEME.textSecondary, fontSize: 11 }}>{neg}</Text>
                    </View>
                    </div>
                </View>
            </View>
        </View>
    );
};

// ============================================================================
// Competitive Benchmarking Section
// ============================================================================

const CompetitiveBenchmarkingSection = ({ data }) => {
    const { t } = useTranslation();
    if (!data || data.total_analyzed === 0) return null;

    const gapColor = data.sentiment_gap > 0.15 ? THEME.error
        : data.sentiment_gap < -0.15 ? THEME.success : THEME.warning;
    const brandColor = (data.avg_brand_sentiment || 0) > 0 ? THEME.success : (data.avg_brand_sentiment || 0) < -0.1 ? THEME.error : THEME.warning;
    const contentColor = (data.avg_content_sentiment || 0) > 0 ? THEME.success : (data.avg_content_sentiment || 0) < -0.1 ? THEME.error : THEME.warning;

    return (
        <SectionCard title={t('app.brandDashboard.competitiveBenchmarking') || 'Competitive Benchmarking'} info={t('app.brandDashboard.competitiveBenchmarkingInfo') || 'Compares brand impact sentiment vs raw content tone. Positive gap = competitors dominating the conversation.'}>
            {/* KPI Row */}
            <View style={{ flexDirection: 'row', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <View style={{ flex: 1, minWidth: 120, padding: 14, borderRadius: 14, backgroundColor: THEME.surface, borderWidth: 1, borderColor: THEME.cardBorder }}>
                    <Text style={{ color: THEME.textTertiary, fontSize: 10, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                        {t('app.brandDashboard.brandSentiment') || 'Brand Impact'}
                    </Text>
                    <Text style={{ color: brandColor, fontSize: 22, fontWeight: '800' }}>
                        {data.avg_brand_sentiment != null ? `${data.avg_brand_sentiment > 0 ? '+' : ''}${Math.round(data.avg_brand_sentiment * 100)}%` : '—'}
                    </Text>
                </View>
                <View style={{ flex: 1, minWidth: 120, padding: 14, borderRadius: 14, backgroundColor: THEME.surface, borderWidth: 1, borderColor: THEME.cardBorder }}>
                    <Text style={{ color: THEME.textTertiary, fontSize: 10, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                        {t('app.brandDashboard.contentTone') || 'Content Tone'}
                    </Text>
                    <Text style={{ color: contentColor, fontSize: 22, fontWeight: '800' }}>
                        {data.avg_content_sentiment != null ? `${data.avg_content_sentiment > 0 ? '+' : ''}${Math.round(data.avg_content_sentiment * 100)}%` : '—'}
                    </Text>
                </View>
                <View style={{ flex: 1, minWidth: 120, padding: 14, borderRadius: 14, backgroundColor: THEME.surface, borderWidth: 1, borderColor: THEME.cardBorder }}>
                    <Text style={{ color: THEME.textTertiary, fontSize: 10, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                        {t('app.brandDashboard.competitiveGap') || 'Competitive Gap'}
                    </Text>
                    <Text style={{ color: gapColor, fontSize: 22, fontWeight: '800' }}>
                        {data.sentiment_gap != null ? `${data.sentiment_gap > 0 ? '+' : ''}${Math.round(data.sentiment_gap * 100)}%` : '—'}
                    </Text>
                </View>
            </View>

            {/* Threat / Aligned counts */}
            <View style={{ flexDirection: 'row', gap: 10, marginBottom: 16 }}>
                {data.competitive_threat_count > 0 && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, backgroundColor: THEME.error + '15', borderWidth: 1, borderColor: THEME.error + '30' }}>
                        <FaExclamationTriangle size={10} color={THEME.error} />
                        <Text style={{ color: THEME.error, fontSize: 11, fontWeight: '700' }}>
                            {data.competitive_threat_count} {t('app.brandDashboard.competitiveThreats') || 'threats'}
                        </Text>
                    </View>
                )}
                {data.brand_aligned_count > 0 && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, backgroundColor: THEME.success + '15', borderWidth: 1, borderColor: THEME.success + '30' }}>
                        <FaCheckCircle size={10} color={THEME.success} />
                        <Text style={{ color: THEME.success, fontSize: 11, fontWeight: '700' }}>
                            {data.brand_aligned_count} {t('app.brandDashboard.brandAligned') || 'aligned'}
                        </Text>
                    </View>
                )}
                <Text style={{ color: THEME.textTertiary, fontSize: 11, marginLeft: 'auto' }}>
                    {data.total_analyzed} {t('app.brandDashboard.postsAnalyzed') || 'posts analyzed'}
                </Text>
            </View>

            {/* Timeline */}
            {data.timeline && data.timeline.length > 1 && (
                <View style={{ marginTop: 8 }}>
                    <Text style={{ color: THEME.textTertiary, fontSize: 10, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                        {t('app.brandDashboard.sentimentGapTimeline') || 'Daily Gap Timeline'}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 4, height: 60 }}>
                        {data.timeline.map((point, i) => {
                            const gap = (point.content || 0) - (point.brand || 0);
                            const barHeight = Math.min(Math.abs(gap) * 100, 55);
                            const barColor = gap > 0.1 ? THEME.error : gap < -0.1 ? THEME.success : THEME.warning;
                            return (
                                <View key={i} style={{ flex: 1, alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                                    <div style={{ width: '100%', maxWidth: 24, height: Math.max(barHeight, 3), borderRadius: 4, background: barColor, opacity: 0.7 }} />
                                    <Text style={{ color: THEME.textTertiary, fontSize: 8, marginTop: 3 }}>
                                        {point.day ? new Date(point.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                                    </Text>
                                </View>
                            );
                        })}
                    </View>
                    <View style={{ flexDirection: 'row', gap: 14, marginTop: 8 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <div style={{ width: 8, height: 8, borderRadius: 4, background: THEME.error }} />
                            <Text style={{ color: THEME.textTertiary, fontSize: 9 }}>{t('app.brandDashboard.competitorsWinning') || 'Competitors winning'}</Text>
                        </View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <div style={{ width: 8, height: 8, borderRadius: 4, background: THEME.success }} />
                            <Text style={{ color: THEME.textTertiary, fontSize: 9 }}>{t('app.brandDashboard.brandWinning') || 'Brand winning'}</Text>
                        </View>
                    </View>
                </View>
            )}
        </SectionCard>
    );
};

// ============================================================================
// Share of Voice Section
// ============================================================================

const ROLE_COLORS = { brand: '#0a84ff', competitor: '#ff453a', general: '#8e8e93' };
const ROLE_LABELS = { brand: 'Brand', competitor: 'Competitor', general: 'General' };
const RoleBadge = ({ role }) => {
    if (!role || role === 'general') return null;
    const color = ROLE_COLORS[role] || '#8e8e93';
    return (
        <span style={{ fontSize: 8, fontWeight: 700, color, backgroundColor: color + '18', padding: '1px 5px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {ROLE_LABELS[role] || role}
        </span>
    );
};

const ShareOfVoiceSection = ({ data }) => {
    const { t } = useTranslation();
    const ca = data.competitive_analysis;
    if (!ca?.per_list_comparison?.length) return null;

    const totalPosts = ca.per_list_comparison.reduce((sum, l) => sum + l.post_count, 0);
    const timeline = ca.share_of_voice_timeline || [];
    const listNames = {};
    ca.per_list_comparison.forEach(l => { listNames[l.list_id] = l.list_name; });

    // Group timeline by day
    const days = {};
    timeline.forEach(t => {
        if (!days[t.day]) days[t.day] = {};
        days[t.day][t.list_id] = t.post_count;
    });
    const sortedDays = Object.keys(days).sort();

    return (
        <SectionCard title={t('app.brandDashboard.shareOfVoice') || 'Share of Voice'} info={t('app.brandDashboard.shareOfVoiceInfo') || 'Percentage of total posts per list — shows who dominates the conversation.'}>
            {/* Donut visualization */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                {ca.per_list_comparison.map((list, i) => {
                    const pct = totalPosts > 0 ? Math.round((list.post_count / totalPosts) * 100) : 0;
                    const color = LIST_COLORS[i % LIST_COLORS.length];
                    return (
                        <View key={list.list_id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 12, backgroundColor: color + '12', borderWidth: 1, borderColor: color + '30' }}>
                            <div style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: color }} />
                            <Text style={{ color: THEME.textMain, fontSize: 12, fontWeight: '600' }}>{list.list_name}</Text>
                            <RoleBadge role={list.list_role} />
                            <Text style={{ color, fontSize: 14, fontWeight: '800' }}>{pct}%</Text>
                            <Text style={{ color: THEME.textTertiary, fontSize: 10 }}>({list.post_count})</Text>
                        </View>
                    );
                })}
            </View>

            {/* Stacked bar timeline */}
            {sortedDays.length > 1 && (
                <View style={{ marginTop: 8 }}>
                    <Text style={{ color: THEME.textTertiary, fontSize: 10, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                        {t('app.brandDashboard.dailyBreakdown') || 'Daily Breakdown'}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 3, height: 80 }}>
                        {sortedDays.map((day, di) => {
                            const dayData = days[day];
                            const dayTotal = Object.values(dayData).reduce((s, v) => s + v, 0);
                            return (
                                <View key={di} style={{ flex: 1, height: '100%', justifyContent: 'flex-end' }}>
                                    {ca.per_list_comparison.map((list, li) => {
                                        const count = dayData[list.list_id] || 0;
                                        const height = dayTotal > 0 ? (count / dayTotal) * 70 : 0;
                                        return (
                                            <div key={list.list_id} style={{ width: '100%', height: Math.max(height, 0), backgroundColor: LIST_COLORS[li % LIST_COLORS.length], opacity: 0.8 }} />
                                        );
                                    })}
                                    <Text style={{ color: THEME.textTertiary, fontSize: 7, textAlign: 'center', marginTop: 2 }}>{day ? new Date(day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</Text>
                                </View>
                            );
                        })}
                    </View>
                </View>
            )}
        </SectionCard>
    );
};

// ============================================================================
// Head-to-Head Sentiment Section
// ============================================================================

const HeadToHeadSentimentSection = ({ data }) => {
    const { t } = useTranslation();
    const ca = data.competitive_analysis;
    if (!ca?.per_list_comparison?.length) return null;

    const roleOrder = { brand: 0, competitor: 1, general: 2 };
    const sorted = [...ca.per_list_comparison].sort((a, b) => (roleOrder[a.list_role] || 2) - (roleOrder[b.list_role] || 2) || (b.avg_content_sentiment || 0) - (a.avg_content_sentiment || 0));
    const maxAbs = Math.max(...sorted.map(l => Math.abs(l.avg_content_sentiment || 0)), 0.01);

    return (
        <SectionCard title={t('app.brandDashboard.headToHeadSentiment') || 'Head-to-Head Sentiment'} info={t('app.brandDashboard.headToHeadSentimentInfo') || 'Content sentiment comparison across all lists, ranked best to worst.'}>
            <View style={{ gap: 10 }}>
                {sorted.map((list, i) => {
                    const sentiment = list.avg_content_sentiment || 0;
                    const barWidth = Math.abs(sentiment) / maxAbs * 100;
                    const color = sentiment > 0.1 ? THEME.success : sentiment < -0.1 ? THEME.error : THEME.warning;
                    return (
                        <View key={list.list_id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                            <View style={{ width: 120, flexShrink: 0, flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                <Text style={{ color: ROLE_COLORS[list.list_role] || LIST_COLORS[i % LIST_COLORS.length], fontSize: 11, fontWeight: '600' }} numberOfLines={1}>{list.list_name}</Text>
                                <RoleBadge role={list.list_role} />
                            </View>
                            <View style={{ flex: 1, height: 20, borderRadius: 6, backgroundColor: THEME.surface, overflow: 'hidden', justifyContent: 'center' }}>
                                <div style={{ width: `${Math.max(barWidth, 3)}%`, height: '100%', borderRadius: 6, backgroundColor: color, opacity: 0.8 }} />
                            </View>
                            <Text style={{ color, fontSize: 12, fontWeight: '700', width: 50, textAlign: 'right' }}>
                                {formatSentiment(sentiment).pct}
                            </Text>
                        </View>
                    );
                })}
            </View>
        </SectionCard>
    );
};

// ============================================================================
// Engagement Benchmarking Section
// ============================================================================

const EngagementBenchmarkingSection = ({ data }) => {
    const { t } = useTranslation();
    const ca = data.competitive_analysis;
    if (!ca?.engagement_benchmarking?.length) return null;

    const metrics = ['avg_likes', 'avg_comments', 'avg_views', 'avg_shares'];
    const metricLabels = { avg_likes: 'Likes', avg_comments: 'Comments', avg_views: 'Views', avg_shares: 'Shares' };
    const metricColors = { avg_likes: '#ff453a', avg_comments: '#0a84ff', avg_views: '#30d158', avg_shares: '#bf5af2' };

    return (
        <SectionCard title={t('app.brandDashboard.engagementBenchmarking') || 'Engagement Benchmarking'} info={t('app.brandDashboard.engagementBenchmarkingInfo') || 'Average engagement metrics per list. Based on TikTok, Instagram, and Facebook data.'}>
            <View style={{ gap: 16 }}>
                {ca.engagement_benchmarking.map((list, li) => (
                    <View key={list.list_id} style={{ padding: 12, borderRadius: 12, backgroundColor: THEME.surface, borderWidth: 1, borderColor: THEME.cardBorder }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: ROLE_COLORS[list.list_role] || LIST_COLORS[li % LIST_COLORS.length] }} />
                            <Text style={{ color: THEME.textMain, fontSize: 12, fontWeight: '700' }}>{list.list_name}</Text>
                            <RoleBadge role={list.list_role} />
                            <Text style={{ color: THEME.textTertiary, fontSize: 10, marginLeft: 'auto' }}>{list.post_count} posts</Text>
                        </View>
                        <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                            {metrics.map(m => {
                                const val = list[m];
                                if (val == null || val === 0) return null;
                                return (
                                    <View key={m} style={{ padding: '6px 10px', borderRadius: 8, backgroundColor: metricColors[m] + '12' }}>
                                        <Text style={{ color: THEME.textTertiary, fontSize: 9, fontWeight: '600', textTransform: 'uppercase' }}>{metricLabels[m]}</Text>
                                        <Text style={{ color: metricColors[m], fontSize: 14, fontWeight: '800' }}>{val >= 1000 ? `${(val / 1000).toFixed(1)}K` : Math.round(val)}</Text>
                                    </View>
                                );
                            })}
                        </View>
                    </View>
                ))}
            </View>
            <Text style={{ color: THEME.textTertiary, fontSize: 9, marginTop: 10, fontStyle: 'italic' }}>Based on TikTok, Instagram, and Facebook data</Text>
        </SectionCard>
    );
};

// ============================================================================
// Competitor Mentions Section
// ============================================================================

const CompetitorMentionsSection = ({ data, onCompetitorClick }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = React.useState(null);
    const ca = data.competitive_analysis;
    if (!ca?.competitor_mentions?.length) return null;

    return (
        <SectionCard title={t('app.brandDashboard.competitorMentions') || 'Competitor Mentions'} info={t('app.brandDashboard.competitorMentionsInfo') || 'How often tracked competitors are mentioned across your monitored content.'}>
            <View style={{ gap: 10 }}>
                {ca.competitor_mentions.map((comp, i) => (
                    <TouchableOpacity key={comp.competitor_id || comp.competitor} onPress={() => onCompetitorClick ? onCompetitorClick(comp.competitor, comp.competitor_id) : setExpanded(expanded === i ? null : i)} style={{ padding: 12, borderRadius: 12, backgroundColor: THEME.surface, borderWidth: 1, borderColor: THEME.cardBorder, cursor: 'pointer' }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                                {comp.logo_url ? (
                                    <Image source={{ uri: comp.logo_url }} style={{ width: 28, height: 28, borderRadius: 6 }} />
                                ) : (
                                    <View style={{ width: 28, height: 28, borderRadius: 6, backgroundColor: '#ff453a14', justifyContent: 'center', alignItems: 'center' }}>
                                        <Text style={{ color: '#ff453a', fontWeight: '700', fontSize: 13 }}>{(comp.competitor || '?')[0].toUpperCase()}</Text>
                                    </View>
                                )}
                                <View>
                                    <Text style={{ color: THEME.textMain, fontSize: 13, fontWeight: '700' }}>{comp.competitor}</Text>
                                    {comp.description ? <Text style={{ color: THEME.textTertiary, fontSize: 10, marginTop: 1 }} numberOfLines={1}>{comp.description}</Text> : null}
                                </View>
                            </View>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                <View style={{ padding: '3px 8px', borderRadius: 10, backgroundColor: THEME.primary + '20' }}>
                                    <Text style={{ color: THEME.primary, fontSize: 11, fontWeight: '700' }}>{comp.mention_count}</Text>
                                </View>
                                <FaChevronDown size={10} color={THEME.textTertiary} style={{ transform: expanded === i ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                            </View>
                        </View>
                        {expanded === i && comp.examples && comp.examples.length > 0 && (
                            <View style={{ marginTop: 10, gap: 6 }}>
                                {comp.examples.map((ex, j) => (
                                    <Text key={j} style={{ color: THEME.textSecondary, fontSize: 11, padding: 8, borderRadius: 8, backgroundColor: THEME.cardBorder, lineHeight: 16 }} numberOfLines={3}>{ex}</Text>
                                ))}
                            </View>
                        )}
                    </TouchableOpacity>
                ))}
            </View>
            {ca.tracked_competitors?.length > 0 && (
                <Text style={{ color: THEME.textTertiary, fontSize: 9, marginTop: 10, fontStyle: 'italic' }}>
                    Tracking: {ca.tracked_competitors.join(', ')}
                </Text>
            )}
        </SectionCard>
    );
};

// ============================================================================
// Platform Strongholds Section
// ============================================================================

const PlatformStrongholdsSection = ({ data, onPlatformClick }) => {
    const { t } = useTranslation();
    const ca = data.competitive_analysis;
    if (!ca?.platform_strongholds || Object.keys(ca.platform_strongholds).length === 0) return null;

    const lists = Object.entries(ca.platform_strongholds);

    // Consistent platform ordering across all lists so bars are easy to compare.
    // Build a stable order from all platforms seen, then sort each list's bar by it.
    const platformOrder = (() => {
        const seen = [];
        lists.forEach(([, d]) => d.platforms.forEach(p => { if (!seen.includes(p.source)) seen.push(p.source); }));
        return seen.sort();
    })();
    const orderIndex = (src) => {
        const i = platformOrder.indexOf(src);
        return i === -1 ? platformOrder.length : i;
    };

    return (
        <SectionCard title={t('app.brandDashboard.platformStrongholds') || 'Platform Strongholds'} info={t('app.brandDashboard.platformStrongholdsInfo') || 'Platform distribution per list — reveals where each entity is most active.'}>
            <View style={{ gap: 14 }}>
                {lists.map(([listId, listData], li) => {
                    const platforms = [...listData.platforms].sort((a, b) => orderIndex(a.source) - orderIndex(b.source));
                    const total = platforms.reduce((s, p) => s + p.post_count, 0);
                    return (
                        <View key={listId}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: LIST_COLORS[li % LIST_COLORS.length] }} />
                                <Text style={{ color: THEME.textMain, fontSize: 11, fontWeight: '700' }}>{listData.list_name}</Text>
                                <Text style={{ color: THEME.textTertiary, fontSize: 10 }}>({total})</Text>
                            </View>
                            {/* Stacked horizontal bar */}
                            <View style={{ flexDirection: 'row', height: 20, borderRadius: 6, overflow: 'hidden' }}>
                                {platforms.map(p => {
                                    const pct = total > 0 ? (p.post_count / total) * 100 : 0;
                                    const color = PLATFORM_COLORS[p.source] || PLATFORM_COLORS[p.source + '_group'] || '#8e8e93';
                                    return (
                                        <div key={p.source} title={`${p.source}: ${p.post_count} (${Math.round(pct)}%)`} onClick={() => onPlatformClick && onPlatformClick(p.source, listId)} style={{ width: `${pct}%`, height: '100%', backgroundColor: color, opacity: 0.85, cursor: onPlatformClick ? 'pointer' : 'default' }} />
                                    );
                                })}
                            </View>
                            {/* Legend */}
                            <View style={{ flexDirection: 'row', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                                {platforms.map(p => {
                                    const color = PLATFORM_COLORS[p.source] || PLATFORM_COLORS[p.source + '_group'] || '#8e8e93';
                                    return (
                                        <div key={p.source} onClick={() => onPlatformClick && onPlatformClick(p.source, listId)} style={{ cursor: onPlatformClick ? 'pointer' : 'default', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                                            <div style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: color }} />
                                            <Text style={{ color: THEME.textTertiary, fontSize: 9 }}>{p.source} {Math.round((p.post_count / total) * 100)}%</Text>
                                        </div>
                                    );
                                })}
                            </View>
                        </View>
                    );
                })}
            </View>
        </SectionCard>
    );
};

// ============================================================================
// Emotion Fingerprint Section
// ============================================================================

const EmotionFingerprintSection = ({ data, onEmotionClick }) => {
    const { t } = useTranslation();
    const ca = data.competitive_analysis;
    if (!ca?.emotion_fingerprints || Object.keys(ca.emotion_fingerprints).length === 0) return null;

    const lists = Object.entries(ca.emotion_fingerprints);
    // Collect all unique emotions
    const allEmotions = new Set();
    lists.forEach(([, d]) => Object.keys(d.emotions).forEach(e => allEmotions.add(e)));
    const emotions = [...allEmotions].sort();

    // Find max for scaling
    const maxCount = Math.max(...lists.flatMap(([, d]) => Object.values(d.emotions)), 1);

    return (
        <SectionCard title={t('app.brandDashboard.emotionFingerprint') || 'Emotion Fingerprint'} info={t('app.brandDashboard.emotionFingerprintInfo') || 'Dominant emotion distribution per list — reveals the emotional tone each entity generates.'}>
            <View style={{ gap: 12 }}>
                {emotions.map(emotion => (
                    <View key={emotion}>
                        <div onClick={() => onEmotionClick && onEmotionClick(emotion)} style={{ cursor: onEmotionClick ? 'pointer' : 'default' }}>
                            <Text style={{ color: EMOTION_COLORS[emotion] || THEME.textSecondary, fontSize: 10, fontWeight: '700', textTransform: 'capitalize', marginBottom: 4 }}>{emotion}</Text>
                        </div>
                        <View style={{ gap: 4 }}>
                            {lists.map(([listId, listData], li) => {
                                const count = listData.emotions[emotion] || 0;
                                const barWidth = (count / maxCount) * 100;
                                return (
                                    <div key={listId} onClick={(e) => { e.stopPropagation(); onEmotionClick && onEmotionClick(emotion, listId); }} style={{ cursor: onEmotionClick ? 'pointer' : 'default' }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                        <Text style={{ color: LIST_COLORS[li % LIST_COLORS.length], fontSize: 9, width: 70, fontWeight: '600' }} numberOfLines={1}>{listData.list_name}</Text>
                                        <View style={{ flex: 1, height: 12, borderRadius: 4, backgroundColor: THEME.surface, overflow: 'hidden' }}>
                                            <div style={{ width: `${Math.max(barWidth, 1)}%`, height: '100%', borderRadius: 4, backgroundColor: EMOTION_COLORS[emotion] || LIST_COLORS[li % LIST_COLORS.length], opacity: 0.75 }} />
                                        </View>
                                        <Text style={{ color: THEME.textTertiary, fontSize: 9, width: 25, textAlign: 'right' }}>{count}</Text>
                                    </View>
                                    </div>
                                );
                            })}
                        </View>
                    </View>
                ))}
            </View>
        </SectionCard>
    );
};

// ============================================================================
// Platform Sentiment Chart
// ============================================================================

const PlatformSentimentSection = ({ data, onPlatformClick }) => {
    const { t } = useTranslation();
    const platforms = data.platform_sentiment_7d || data.platform_sentiment;
    if (!platforms || platforms.length === 0) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: THEME.textMuted }}>{t('app.brandDashboard.noPlatformSentimentData')}</div>;

    return (
        <SectionCard title={t('app.brandDashboard.platformSentiment')} info={t('app.brandDashboard.platformSentimentInfo')}>
            <PlatformSentimentChart platforms={platforms} onPlatformClick={onPlatformClick} />
        </SectionCard>
    );
};

const PlatformSentimentChart = ({ platforms, onPlatformClick }) => (
    <View style={{ gap: 16, marginTop: 10 }}>
        {platforms.map(({ source, avg_sentiment, post_count, positive, neutral, negative }) => {
            const Icon = PLATFORM_ICONS[source];
            const sentiment = avg_sentiment || 0;
            const fillPct = ((sentiment + 1) / 2) * 100;
            const color = sentiment > 0.1 ? THEME.success : sentiment < -0.1 ? THEME.error : THEME.warning;
            const hasDist = (positive || 0) + (neutral || 0) + (negative || 0) > 0;
            const distTotal = (positive || 0) + (neutral || 0) + (negative || 0);
            return (
                <div key={source} onClick={() => onPlatformClick && onPlatformClick(source)} style={{ cursor: onPlatformClick ? 'pointer' : 'default' }}>
                <View style={styles.platformSentimentCard}>
                    <View style={styles.platformSentimentHeader}>
                        <View style={styles.platformSentimentInfo}>
                            <View style={[styles.platformIconBox, { backgroundColor: (PLATFORM_COLORS[source] || '#8e8e93') + '15' }]}>
                                {Icon && <Icon size={16} color={PLATFORM_COLORS[source] || '#8e8e93'} />}
                            </View>
                            <Text style={styles.platformNameText}>{PLATFORM_LABELS[source] || source} ({post_count} posts)</Text>
                        </View>
                        <View style={styles.platformSentimentMetrics}>
                            <Text style={[styles.platformSentimentValue, { color: formatSentiment(sentiment).color }]}>
                                {formatSentiment(sentiment).pct} · {formatSentiment(sentiment).label}
                            </Text>
                            <View style={styles.platformCountBadge}>
                                <Text style={styles.platformCountText}>{post_count}</Text>
                            </View>
                        </View>
                    </View>
                    {/* Stacked distribution bar only — position indicator removed */}
                    {hasDist && (
                        <View style={{ marginTop: 6, gap: 3 }}>
                            <View style={{ flexDirection: 'row', height: 5, borderRadius: 3, overflow: 'hidden', gap: 1 }}>
                                {positive > 0 && <div style={{ width: `${(positive / distTotal) * 100}%`, background: '#30d158' }} />}
                                {neutral > 0 && <div style={{ width: `${(neutral / distTotal) * 100}%`, background: '#fbbf24' }} />}
                                {negative > 0 && <div style={{ width: `${(negative / distTotal) * 100}%`, background: '#ff453a' }} />}
                            </View>
                            <View style={{ flexDirection: 'row', gap: 10 }}>
                                <Text style={{ color: '#30d158', fontSize: 9, fontWeight: '600' }}>Pos {Math.round((positive / distTotal) * 100)}%</Text>
                                <Text style={{ color: '#fbbf24', fontSize: 9, fontWeight: '600' }}>Neu {Math.round((neutral / distTotal) * 100)}%</Text>
                                <Text style={{ color: '#ff453a', fontSize: 9, fontWeight: '600' }}>Neg {Math.round((negative / distTotal) * 100)}%</Text>
                            </View>
                        </View>
                    )}
                </View>
                </div>
            );
        })}
    </View>
);

// ============================================================================
// Extreme Posts List with Action Advisor
// ============================================================================

const ACTION_COLORS = {
    report: '#ff453a',
    counter_narrative: '#ff9f0a',
    react: '#0a84ff',
    monitor: '#8e8e93',
    amplify: '#30d158',
    share: '#64d2ff',
    ignore: '#636366',
};

const SEVERITY_COLORS = {
    critical: '#ff453a',
    high: '#ff9f0a',
    medium: '#ffd60a',
    low: '#8e8e93',
};

const formatFollowers = (count) => {
    if (!count) return '—';
    if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
    if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
    return count.toString();
};

const SORT_FIELD_KEYS = [
    { key: 'source', labelKey: 'app.brandDashboard.platform', flex: 0.8 },
    { key: 'content', labelKey: 'app.brandDashboard.capturedContent', flex: 3 },
    { key: 'author_username', labelKey: 'app.brandDashboard.author', flex: 1.2 },
    { key: 'author_follower_count', labelKey: 'app.brandDashboard.followers', flex: 1 },
    { key: 'sentiment_score', labelKey: 'app.brandDashboard.sentiment', flex: 0.8 },
    { key: 'action', labelKey: 'app.brandDashboard.status', flex: 1.2 },
];

const SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
const STATUS_ORDER = { in_progress: 0, new: 1, completed: 2 };


const PlatformPostCard = ({ post }) => {
    const src = post.source || post.source_type || 'unknown';
    const content = post.translated_content || post.content || '';
    const author = post.author_username || post.author_name || '';
    const authorName = post.author_name || post.author_username || '';
    const profileImg = post.author_profile_image_url;
    const followers = post.author_follower_count || 0;
    const postUrl = post.post_url || null;
    const sentScore = post.sentiment_score || 0;
    const sentColor = sentScore > 0.1 ? '#4ade80' : sentScore < -0.1 ? '#f87171' : '#fbbf24';
    const postDate = post.post_date ? new Date(post.post_date) : null;
    const timeAgo = postDate ? _timeAgo(postDate) : '';
    const viewsCount = post.views_count ?? post.views ?? 0;
    const likesCount = post.likes_count ?? post.likes ?? 0;
    const commentsCount = post.comments_count ?? post.comments ?? 0;
    const sharesCount = post.shares_count ?? post.shares ?? 0;

    const sentBadge = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <div style={{ padding: '2px 7px', borderRadius: 6, backgroundColor: `${sentColor}15`, border: `1px solid ${sentColor}30` }}>
                <span style={{ color: formatSentiment(sentScore).color, fontSize: 10, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>
                    {formatSentiment(sentScore).pct}
                </span>
            </div>
            {post.dominant_emotion && post.dominant_emotion !== 'neutral' && (
                <span style={{ padding: '2px 6px', borderRadius: 4, backgroundColor: (EMOTION_COLORS[post.dominant_emotion] || '#8e8e93') + '15', color: EMOTION_COLORS[post.dominant_emotion] || '#8e8e93', fontSize: 9, fontWeight: 600, textTransform: 'capitalize' }}>
                    {post.dominant_emotion}
                </span>
            )}
            {timeAgo && <span style={{ color: THEME.textTertiary, fontSize: 10 }}>{timeAgo}</span>}
        </div>
    );

    const engagementRow = (viewsCount > 0 || likesCount > 0 || commentsCount > 0 || sharesCount > 0) ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 8 }}>
            {viewsCount > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: THEME.textTertiary, fontSize: 11 }}><FaEye size={10} /> {formatFollowers(viewsCount)}</span>}
            {likesCount > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: THEME.textTertiary, fontSize: 11 }}><FaHeart size={10} /> {formatFollowers(likesCount)}</span>}
            {commentsCount > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: THEME.textTertiary, fontSize: 11 }}><FaComment size={10} /> {formatFollowers(commentsCount)}</span>}
            {sharesCount > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: THEME.textTertiary, fontSize: 11 }}><FaShareAlt size={10} /> {formatFollowers(sharesCount)}</span>}
        </div>
    ) : null;

    if (src === 'twitter') {
        const tweetId = post.post_url?.match(/status\/(\d+)/)?.[1] || null;
        return (
            <div style={{ borderRadius: 16, background: THEME.bgCard, border: `1px solid ${THEME.cardBorder}`, borderLeft: '3px solid #1da1f2', overflow: 'hidden' }}>
                <div style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        {profileImg ? (
                            <img src={profileImg} alt="" style={{ width: 36, height: 36, borderRadius: 18, objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                        ) : (
                            <div style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(29,161,242,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaTwitter size={16} color="#1da1f2" /></div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: THEME.textMain, fontSize: 13, fontWeight: 700 }}>@{author}</div>
                            {followers > 0 && <div style={{ color: THEME.textTertiary, fontSize: 11 }}>{formatFollowers(followers)} followers</div>}
                        </div>
                        <FaTwitter size={14} color="#1da1f2" />
                    </div>
                    <p style={{ margin: '0 0 10px', color: THEME.textMain, fontSize: 13, lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{content}</p>
                    {sentBadge}
                    {engagementRow}
                    <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                        {postUrl && <a href={postUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#1da1f2', fontSize: 11, fontWeight: 600, textDecoration: 'none', padding: '5px 10px', borderRadius: 16, background: 'rgba(29,161,242,0.1)', border: '1px solid rgba(29,161,242,0.25)' }}><FaExternalLinkAlt size={8} />View on X</a>}
                        {tweetId && <a href={`https://twitter.com/intent/reply?in_reply_to=${tweetId}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: THEME.textSecondary, fontSize: 11, fontWeight: 600, textDecoration: 'none', padding: '5px 10px', borderRadius: 16, background: THEME.surface, border: `1px solid ${THEME.cardBorder}` }}><FaReply size={8} />Reply</a>}
                        {tweetId && <a href={`https://twitter.com/intent/retweet?tweet_id=${tweetId}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#30d158', fontSize: 11, fontWeight: 600, textDecoration: 'none', padding: '5px 10px', borderRadius: 16, background: 'rgba(48,209,88,0.1)', border: '1px solid rgba(48,209,88,0.25)' }}><FaRetweet size={8} />Repost</a>}
                    </div>
                </div>
            </div>
        );
    }

    if (src === 'telegram') {
        return (
            <div style={{ borderRadius: 18, borderBottomRightRadius: 4, background: 'rgba(33,47,61,0.6)', border: '1px solid rgba(51,144,236,0.2)', overflow: 'hidden' }}>
                <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(51,144,236,0.15)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 17, background: 'rgba(0,136,204,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FaTelegram size={16} color="#0088cc" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: '#3390ec', fontSize: 13, fontWeight: 700 }}>{authorName}</div>
                            {followers > 0 && <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{formatFollowers(followers)} subscribers</div>}
                        </div>
                    </div>
                </div>
                <div style={{ padding: '10px 12px' }}>
                    <p style={{ margin: '0 0 10px', color: THEME.textMain, fontSize: 13, lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{content}</p>
                    {sentBadge}
                    {postUrl && <a href={postUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, color: '#0088cc', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}><FaExternalLinkAlt size={9} />View on Telegram</a>}
                </div>
            </div>
        );
    }

    if (src === 'tv') {
        return (
            <div style={{ borderRadius: 14, overflow: 'hidden', background: '#000', border: '1px solid rgba(159,18,57,0.3)' }}>
                {post.video_url && (
                    <video src={post.video_url} controls playsInline style={{ width: '100%', aspectRatio: '16/9', display: 'block', background: '#000' }} />
                )}
                <div style={{ padding: '10px 12px', borderTop: post.video_url ? '1px solid rgba(159,18,57,0.2)' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 8, background: 'rgba(159,18,57,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaTv size={16} color="#9f1239" /></div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: THEME.textMain, fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{authorName}</div>
                        </div>
                        <FaTv size={14} color="#9f1239" />
                    </div>
                    {content && <p style={{ margin: '0 0 8px', color: THEME.textMain, fontSize: 13, lineHeight: 1.6, wordBreak: 'break-word' }}>{content}</p>}
                    {sentBadge}
                    {engagementRow}
                    {postUrl && (
                        <a href={postUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, color: '#9f1239', fontSize: 11, fontWeight: 600, textDecoration: 'none', padding: '5px 12px', borderRadius: 16, background: 'rgba(159,18,57,0.12)', border: '1px solid rgba(159,18,57,0.25)' }}>
                            <FaExternalLinkAlt size={8} />Watch clip
                        </a>
                    )}
                </div>
            </div>
        );
    }

    if (src === 'tiktok') {
        const tiktokId = post.post_url?.match(/video\/(\d+)/)?.[1] || null;
        return (
            <div style={{ borderRadius: 14, overflow: 'hidden', background: '#111', border: '1px solid rgba(255,0,80,0.2)' }}>
                {tiktokId && (
                    <iframe
                        src={`https://www.tiktok.com/embed/v2/${tiktokId}`}
                        style={{ width: '100%', height: 400, border: 'none', display: 'block' }}
                        allowFullScreen
                        allow="encrypted-media; autoplay"
                        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    />
                )}
                <div style={{ padding: '10px 12px', borderTop: tiktokId ? '1px solid rgba(255,0,80,0.15)' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        {profileImg ? (
                            <img src={profileImg} alt="" style={{ width: 34, height: 34, borderRadius: 17, objectFit: 'cover', border: '2px solid rgba(255,0,80,0.4)' }} onError={e => { e.target.style.display = 'none'; }} />
                        ) : (
                            <div style={{ width: 34, height: 34, borderRadius: 17, background: 'rgba(255,0,80,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaTiktok size={16} color="#ff0050" /></div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>@{author}</div>
                            {followers > 0 && <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{formatFollowers(followers)} followers</div>}
                        </div>
                        <FaTiktok size={14} color="#ff0050" />
                    </div>
                    {!tiktokId && <p style={{ margin: '0 0 8px', color: 'rgba(255,255,255,0.85)', fontSize: 13, lineHeight: 1.6, wordBreak: 'break-word' }}>{content}</p>}
                    {sentBadge}
                    {engagementRow}
                    {postUrl && (
                        <a href={postUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, color: '#ff0050', fontSize: 11, fontWeight: 600, textDecoration: 'none', padding: '5px 12px', borderRadius: 16, background: 'rgba(255,0,80,0.12)', border: '1px solid rgba(255,0,80,0.25)' }}>
                            <FaExternalLinkAlt size={8} />Watch on TikTok
                        </a>
                    )}
                </div>
            </div>
        );
    }

    if (src === 'instagram') {
        const shortcode = (postUrl || '').match(/instagram\.com\/(?:p|reel|tv)\/([A-Za-z0-9_-]+)/)?.[1] || null;
        const igGradient = 'linear-gradient(135deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)';
        return (
            <div style={{ borderRadius: 14, overflow: 'hidden', background: THEME.bgCard, border: '1px solid rgba(225,48,108,0.25)' }}>
                {shortcode && (
                    <iframe
                        src={`https://www.instagram.com/p/${shortcode}/embed/`}
                        style={{ width: '100%', height: 400, border: 'none', display: 'block' }}
                        allowFullScreen
                        scrolling="no"
                        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    />
                )}
                {!shortcode && (
                    <>
                        <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(225,48,108,0.15)', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 34, height: 34, borderRadius: 17, padding: 2, background: igGradient, flexShrink: 0 }}>
                                <div style={{ width: '100%', height: '100%', borderRadius: 15, background: THEME.bgCard, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FaInstagram size={14} color="#e1306c" />
                                </div>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ color: THEME.textMain, fontSize: 13, fontWeight: 700 }}>@{author}</div>
                                {followers > 0 && <div style={{ color: THEME.textTertiary, fontSize: 11 }}>{formatFollowers(followers)} followers</div>}
                            </div>
                            <FaInstagram size={14} color="#e1306c" />
                        </div>
                        <div style={{ padding: '10px 12px' }}>
                            <p style={{ margin: '0 0 8px', color: THEME.textMain, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{content}</p>
                            {sentBadge}
                            {engagementRow}
                            {postUrl && <a href={postUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, color: '#e1306c', fontSize: 11, fontWeight: 600, textDecoration: 'none', padding: '5px 12px', borderRadius: 16, background: 'rgba(225,48,108,0.1)', border: '1px solid rgba(225,48,108,0.25)' }}><FaExternalLinkAlt size={8} />View on Instagram</a>}
                        </div>
                    </>
                )}
            </div>
        );
    }

    if (src === 'rss') {
        return (
            <div style={{ borderRadius: 12, background: THEME.bgCard, border: `1px solid ${THEME.cardBorder}`, borderLeft: '3px solid #f59e0b', overflow: 'hidden' }}>
                <div style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FaGlobe size={14} color="#f59e0b" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: THEME.textMain, fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{authorName}</div>
                        </div>
                    </div>
                    <p style={{ margin: '0 0 10px', color: THEME.textMain, fontSize: 13, lineHeight: 1.6, wordBreak: 'break-word' }}>{content}</p>
                    {sentBadge}
                    {postUrl && <a href={postUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, color: '#f59e0b', fontSize: 11, fontWeight: 600, textDecoration: 'none', padding: '5px 12px', borderRadius: 16, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)' }}><FaExternalLinkAlt size={8} />Read Article</a>}
                </div>
            </div>
        );
    }

    // Generic fallback
    const platformColor = PLATFORM_COLORS[src] || '#8e8e93';
    const PlatformIcon = PLATFORM_ICONS[src] || FaGlobe;
    return (
        <div style={{ borderRadius: 12, background: THEME.bgCard, border: `1px solid ${THEME.cardBorder}`, borderLeft: `3px solid ${platformColor}`, overflow: 'hidden' }}>
            <div style={{ padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    {profileImg ? (
                        <img src={profileImg} alt="" style={{ width: 30, height: 30, borderRadius: 15, objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                        <div style={{ width: 30, height: 30, borderRadius: 15, background: `${platformColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><PlatformIcon size={13} color={platformColor} /></div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: THEME.textMain, fontSize: 13, fontWeight: 600 }}>@{author}</div>
                        {followers > 0 && <div style={{ color: THEME.textTertiary, fontSize: 11 }}>{formatFollowers(followers)} followers</div>}
                    </div>
                    <PlatformIcon size={13} color={platformColor} />
                </div>
                <p style={{ margin: '0 0 8px', color: THEME.textMain, fontSize: 13, lineHeight: 1.6, wordBreak: 'break-word' }}>{content}</p>
                {sentBadge}
                {engagementRow}
                {postUrl && <a href={postUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, color: platformColor, fontSize: 11, fontWeight: 600, textDecoration: 'none' }}><FaExternalLinkAlt size={9} />View Original</a>}
            </div>
        </div>
    );
};

const ExtremePostsList = ({ negativePosts, positivePosts, orgId }) => {
    const { t } = useTranslation();
    const SORT_FIELDS = useMemo(() => SORT_FIELD_KEYS.map(f => ({ ...f, label: t(f.labelKey) })), [t]);
    const [tab, setTab] = useState('negative');
    const [expandedId, setExpandedId] = useState(null);
    const [actionResults, setActionResults] = useState({});
    const [loadingIds, setLoadingIds] = useState({});
    const [batchLoading, setBatchLoading] = useState(false);
    const [updatingStatusId, setUpdatingStatusId] = useState(null);

    // Pagination
    const [extraNegative, setExtraNegative] = useState([]);
    const [extraPositive, setExtraPositive] = useState([]);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMoreNeg, setHasMoreNeg] = useState(true);
    const [hasMorePos, setHasMorePos] = useState(true);

    // Sorting
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState('asc');

    // Load existing post_actions on mount so statuses persist across refreshes
    useEffect(() => {
        (async () => {
            const existing = await ListsManager.fetchPostActions();
            if (existing && existing.length > 0) {
                const map = {};
                existing.forEach(a => {
                    map[a.source_post_id] = a;
                });
                setActionResults(map);
            }
        })();
    }, []);

    const rawNegative = [...negativePosts, ...extraNegative];
    const rawPositive = [...positivePosts, ...extraPositive];
    const rawPosts = tab === 'negative' ? rawNegative : rawPositive;

    const seenIds = new Set();
    const dedupedPosts = rawPosts.filter(p => {
        if (seenIds.has(p.id)) return false;
        seenIds.add(p.id);
        return true;
    });

    const posts = sortKey ? [...dedupedPosts].sort((a, b) => {
        let valA, valB;
        if (sortKey === 'action') {
            valA = (actionResults[a.id]?.status || 'zzz');
            valB = (actionResults[b.id]?.status || 'zzz');
        } else if (sortKey === 'sentiment_score' || sortKey === 'author_follower_count') {
            valA = a[sortKey] ?? 0;
            valB = b[sortKey] ?? 0;
            return sortDir === 'asc' ? valA - valB : valB - valA;
        } else if (sortKey === 'content') {
            valA = (a.content || '').toLowerCase();
            valB = (b.content || '').toLowerCase();
        } else {
            valA = (a[sortKey] || '').toString().toLowerCase();
            valB = (b[sortKey] || '').toString().toLowerCase();
        }
        if (typeof valA === 'string') {
            const cmp = valA.localeCompare(valB);
            return sortDir === 'asc' ? cmp : -cmp;
        }
        return sortDir === 'asc' ? valA - valB : valB - valA;
    }) : dedupedPosts;

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir(key === 'sentiment_score' ? (tab === 'negative' ? 'asc' : 'desc') : 'asc');
        }
    };

    const loadMore = async () => {
        setLoadingMore(true);
        const currentCount = tab === 'negative' ? rawNegative.length : rawPositive.length;
        const result = await ListsManager.fetchExtremePosts(tab, 10, currentCount, orgId);
        if (result && result.length > 0) {
            if (tab === 'negative') {
                setExtraNegative(prev => [...prev, ...result]);
                if (result.length < 10) setHasMoreNeg(false);
            } else {
                setExtraPositive(prev => [...prev, ...result]);
                if (result.length < 10) setHasMorePos(false);
            }
        } else {
            if (tab === 'negative') setHasMoreNeg(false);
            else setHasMorePos(false);
        }
        setLoadingMore(false);
    };

    const hasMore = tab === 'negative' ? hasMoreNeg : hasMorePos;

    const analyzePost = async (post) => {
        setLoadingIds(prev => ({ ...prev, [post.id]: true }));
        const result = await ListsManager.analyzePostAction(post.source, post.id);
        setLoadingIds(prev => ({ ...prev, [post.id]: false }));
        if (result.success) {
            setActionResults(prev => ({ ...prev, [post.id]: result }));
        }
    };

    const analyzeAll = async () => {
        setBatchLoading(true);
        const toAnalyze = posts
            .filter(p => !actionResults[p.id])
            .map(p => ({ source_type: p.source, source_post_id: p.id }));

        if (toAnalyze.length > 0) {
            const result = await ListsManager.analyzePostActionBatch(toAnalyze);
            if (result.success && result.results) {
                const newResults = {};
                result.results.forEach(r => {
                    if (r.id && !r.skipped) {
                        newResults[r.source_post_id] = r;
                    }
                });
                setActionResults(prev => ({ ...prev, ...newResults }));
            }
        }
        setBatchLoading(false);
    };

    const updateStatus = async (actionId, postId, newStatus) => {
        setUpdatingStatusId(postId);
        const result = await ListsManager.updatePostActionStatus(actionId, newStatus);
        if (result.success) {
            setActionResults(prev => ({
                ...prev,
                [postId]: { ...prev[postId], status: newStatus },
            }));
        }
        setUpdatingStatusId(null);
    };

    const [showOriginal, setShowOriginal] = useState({});
    const getDisplayContent = (post) => showOriginal[post.id] ? post.content : (post.translated_content || post.content);
    const hasTranslation = (post) => !!post.translated_content && post.translated_content !== post.content;
    const toggleTranslation = (postId) => setShowOriginal(prev => ({ ...prev, [postId]: !prev[postId] }));

    const unanalyzedCount = posts.filter(p => !actionResults[p.id]).length;

    return (
        <SectionCard title={t('app.brandDashboard.extremePosts7d')}>
            {/* Header Controls */}
            <View style={styles.tableHeaderControls}>
                <View style={styles.tabContainer}>
                    <TouchableOpacity
                        onPress={() => { setTab('negative'); setExpandedId(null); setSortKey(null); }}
                        style={[styles.tabItem, tab === 'negative' && styles.tabItemActiveNeg]}
                    >
                        <Text style={[styles.tabText, tab === 'negative' && { color: '#ff453a' }]}>{t('app.brandDashboard.mostNegative')}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        onPress={() => { setTab('positive'); setExpandedId(null); setSortKey(null); }}
                        style={[styles.tabItem, tab === 'positive' && styles.tabItemActivePos]}
                    >
                        <Text style={[styles.tabText, tab === 'positive' && { color: '#30d158' }]}>{t('app.brandDashboard.mostPositive')}</Text>
                    </TouchableOpacity>
                </View>
                {unanalyzedCount > 0 && (
                    <TouchableOpacity
                        onPress={analyzeAll}
                        disabled={batchLoading}
                        style={[styles.batchAnalyzeBtn, batchLoading && { opacity: 0.6 }]}
                    >
                        {batchLoading ? (
                            <ActivityIndicator size="small" color="#fff" />
                        ) : (
                            <>
                                <FaBrain size={12} color="#fff" />
                                <Text style={styles.batchAnalyzeBtnText}>{t('app.brandDashboard.analyzeAll', { count: unanalyzedCount })}</Text>
                            </>
                        )}
                    </TouchableOpacity>
                )}
            </View>

            {/* Table Header */}
            <View style={styles.tableHead}>
                {SORT_FIELDS.map(({ key, label, flex }) => {
                    const isActive = sortKey === key;
                    return (
                        <TouchableOpacity
                            key={key}
                            onPress={() => toggleSort(key)}
                            style={[styles.tableHeadCell, { flex }]}
                        >
                            <Text style={[styles.tableHeadText, isActive && { color: '#fff' }]}>{label}</Text>
                            <View style={styles.sortIconContainer}>
                                {isActive ? (
                                    sortDir === 'asc' ? <FaSortUp size={10} color="#0a84ff" /> : <FaSortDown size={10} color="#0a84ff" />
                                ) : (
                                    <FaSort size={10} color={THEME.textMuted} />
                                )}
                            </View>
                        </TouchableOpacity>
                    );
                })}
            </View>

            {/* Table Body */}
            {posts.map((post) => {
                const isExpanded = expandedId === post.id;
                const action = actionResults[post.id];
                const isLoading = loadingIds[post.id];
                const Icon = PLATFORM_ICONS[post.source];
                const sentimentColor = (post.sentiment_score || 0) < 0 ? '#ff453a' : '#30d158';

                return (
                    <View key={post.id} style={styles.rowWrapper}>
                        <TouchableOpacity
                            className={!isExpanded ? 'table-row-hover' : undefined}
                            style={[styles.tableRow, isExpanded && styles.tableRowExpanded]}
                            onPress={() => setExpandedId(isExpanded ? null : post.id)}
                        >
                            {/* Platform */}
                            <View style={[styles.tableCell, { flex: 0.8, alignItems: 'center' }]}>
                                {Icon && <Icon size={14} color={PLATFORM_COLORS[post.source] || '#8e8e93'} />}
                            </View>

                            {/* Content Snippet */}
                            <View style={[styles.tableCell, { flex: 3 }]}>
                                <Text style={styles.rowContentText} numberOfLines={1}>
                                    {getDisplayContent(post)}
                                </Text>
                                {hasTranslation(post) && !showOriginal[post.id] && (
                                    <FaLanguage size={12} color="#0a84ff" style={{ marginLeft: 6 }} title="Translated" />
                                )}
                            </View>

                            {/* Author */}
                            <View style={[styles.tableCell, { flex: 1.2 }]}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                    <Text style={styles.rowAuthorText} numberOfLines={1}>@{post.author_username}</Text>
                                    {post.author_verified && post.author_verified !== 'none' && (
                                        <View style={[styles.verifiedDot, { backgroundColor: post.author_verified === 'blue' ? '#1DA1F2' : '#FFD700' }]} />
                                    )}
                                </View>
                            </View>

                            {/* Followers */}
                            <View style={[styles.tableCell, { flex: 1 }]}>
                                <Text style={styles.rowFollowerText}>
                                    {formatFollowers(post.author_follower_count)}
                                </Text>
                            </View>

                            {/* Sentiment */}
                            <View style={[styles.tableCell, { flex: 0.8 }]}>
                                <View style={[styles.sentimentPill, { backgroundColor: formatSentiment(post.sentiment_score).color + '15', borderColor: formatSentiment(post.sentiment_score).color + '33' }]}>
                                    <Text style={[styles.sentimentPillText, { color: formatSentiment(post.sentiment_score).color }]}>
                                        {formatSentiment(post.sentiment_score).pct}
                                    </Text>
                                </View>
                            </View>

                            {/* Action/Status */}
                            <View style={[styles.tableCell, { flex: 1.2, alignItems: 'center' }]}>
                                {action ? (
                                    <View style={[styles.statusMiniBadge, { backgroundColor: (ACTION_COLORS[action.recommended_action] || '#8e8e93') + '22' }]}>
                                        <Text style={[styles.statusMiniBadgeText, { color: ACTION_COLORS[action.recommended_action] || '#8e8e93' }]}>
                                            {(action.status || 'new').replace('_', ' ')}
                                        </Text>
                                    </View>
                                ) : (
                                    <TouchableOpacity
                                        onPress={(e) => { e.stopPropagation(); analyzePost(post); }}
                                        disabled={isLoading}
                                        style={styles.rowAdviseBtn}
                                    >
                                        {isLoading ? <ActivityIndicator size="small" color="#0a84ff" /> : <Text style={styles.rowAdviseBtnText}>{t('app.brandDashboard.advise')}</Text>}
                                    </TouchableOpacity>
                                )}
                            </View>
                        </TouchableOpacity>

                        {/* Expanded Content */}
                        {isExpanded && (
                            <View style={styles.detailDrawer}>
                                <View style={styles.detailGrid}>
                                    {/* Left: Original Post Info */}
                                    <View style={styles.detailSectionLeft}>
                                        <View style={styles.detailUserRow}>
                                            {post.author_profile_image_url && (
                                                <img src={post.author_profile_image_url} alt="" style={styles.detailAvatar} />
                                            )}
                                            <View>
                                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                                    <Text style={styles.detailUserName}>{post.author_name || post.author_username}</Text>
                                                    {post.author_verified && post.author_verified !== 'none' && (
                                                        <View style={[styles.verifiedDot, { backgroundColor: post.author_verified === 'blue' ? '#1DA1F2' : '#FFD700' }]} />
                                                    )}
                                                </View>
                                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                                    <Text style={styles.detailUserHandle}>@{post.author_username} • {post.source}</Text>
                                                    {post.author_follower_count > 0 && (
                                                        <Text style={styles.detailFollowerCount}>{formatFollowers(post.author_follower_count)} {t('app.brandDashboard.followers')}</Text>
                                                    )}
                                                </View>
                                            </View>
                                        </View>
                                        <Text style={styles.detailFullText}>{getDisplayContent(post)}</Text>
                                        {hasTranslation(post) && (
                                            <TouchableOpacity onPress={() => toggleTranslation(post.id)} style={styles.detailTranslateBtn}>
                                                {showOriginal[post.id] ? <FaLanguage size={14} color="#0a84ff" /> : <FaUndo size={12} color="#0a84ff" />}
                                                <Text style={styles.detailTranslateBtnText}>{showOriginal[post.id] ? t('app.brandDashboard.showTranslated') : t('app.brandDashboard.showOriginal')}</Text>
                                            </TouchableOpacity>
                                        )}
                                        {post.author_bio && (
                                            <View style={styles.authorBioBox}>
                                                <Text style={styles.authorBioLabel}>{t('app.brandDashboard.authorBio')}</Text>
                                                <Text style={styles.authorBioText}>{post.author_bio}</Text>
                                            </View>
                                        )}
                                    </View>

                                    {/* Right: AI Analysis & Actions */}
                                    <View style={styles.detailSectionRight}>
                                        {action ? (
                                            <View style={styles.analysisCard}>
                                                <View style={styles.analysisHeader}>
                                                    <View style={[styles.analysisActionBadge, { backgroundColor: (ACTION_COLORS[action.recommended_action] || '#8e8e93') + '22' }]}>
                                                        <Text style={[styles.analysisActionText, { color: ACTION_COLORS[action.recommended_action] || '#8e8e93' }]}>
                                                            {action.recommended_action.replace('_', ' ')}
                                                        </Text>
                                                    </View>
                                                    <View style={[styles.analysisSeverityBadge, { backgroundColor: (SEVERITY_COLORS[action.severity] || '#8e8e93') + '22' }]}>
                                                        <Text style={[styles.analysisSeverityText, { color: SEVERITY_COLORS[action.severity] || '#8e8e93' }]}>
                                                            {action.severity} {t('app.brandDashboard.severity')}
                                                        </Text>
                                                    </View>
                                                </View>

                                                <Text style={styles.analysisReasoning}>{action.reasoning}</Text>

                                                {action.suggested_steps && action.suggested_steps.length > 0 && (
                                                    <View style={styles.stepsContainer}>
                                                        {action.suggested_steps.map((step, i) => (
                                                            <View key={i} style={styles.stepRow}>
                                                                <View style={styles.stepDot} />
                                                                <Text style={styles.stepText}>{step}</Text>
                                                            </View>
                                                        ))}
                                                    </View>
                                                )}

                                                {/* Status Dropdown/Selector */}
                                                <View style={styles.statusPickerContainer}>
                                                    <Text style={styles.statusPickerLabel}>{t('app.brandDashboard.updateStatus')}</Text>
                                                    <View style={styles.statusOptions}>
                                                        {['new', 'in_progress', 'completed'].map((st) => (
                                                            <TouchableOpacity
                                                                key={st}
                                                                onPress={() => updateStatus(action.id, post.id, st)}
                                                                disabled={updatingStatusId === post.id}
                                                                style={[
                                                                    styles.statusOptionBtn,
                                                                    action.status === st && styles.statusOptionBtnActive,
                                                                    action.status === st && st === 'completed' && { borderColor: '#30d158', backgroundColor: 'rgba(48,209,88,0.1)' },
                                                                    action.status === st && st === 'in_progress' && { borderColor: '#0a84ff', backgroundColor: 'rgba(10,132,255,0.1)' },
                                                                ]}
                                                            >
                                                                {updatingStatusId === post.id && action.status !== st ? (
                                                                     null
                                                                ) : (
                                                                    <Text style={[
                                                                        styles.statusOptionText,
                                                                        action.status === st && { color: st === 'completed' ? '#30d158' : st === 'in_progress' ? '#0a84ff' : '#fff', fontWeight: '700' }
                                                                    ]}>
                                                                        {st.replace('_', ' ')}
                                                                    </Text>
                                                                )}
                                                            </TouchableOpacity>
                                                        ))}
                                                    </View>
                                                </View>
                                            </View>
                                        ) : (
                                            <View style={styles.pendingAnalysis}>
                                                <FaBrain size={24} color={THEME.textMuted} />
                                                <Text style={styles.pendingAnalysisText}>{t('app.brandDashboard.aiAdvisorNotRequested')}</Text>
                                                <TouchableOpacity
                                                    onPress={() => analyzePost(post)}
                                                    disabled={isLoading}
                                                    style={styles.detailAnalyzeBtn}
                                                >
                                                    {isLoading ? (
                                                        <ActivityIndicator size="small" color="#fff" />
                                                    ) : (
                                                        <>
                                                            <FaSearch size={14} color="#fff" />
                                                            <Text style={styles.detailAnalyzeBtnText}>{t('app.brandDashboard.analyzePostNow')}</Text>
                                                        </>
                                                    )}
                                                </TouchableOpacity>
                                            </View>
                                        )}
                                    </View>
                                </View>
                            </View>
                        )}
                    </View>
                );
            })}

            {/* Pagination / Footer */}
            <View style={styles.tableFooter}>
                {posts.length === 0 ? (
                    <Text style={styles.noDataText}>{t('app.brandDashboard.noPostsWithSentiment')}</Text>
                ) : hasMore ? (
                    <TouchableOpacity
                        onPress={loadMore}
                        disabled={loadingMore}
                        style={styles.loadMoreBtn}
                    >
                        {loadingMore ? <ActivityIndicator size="small" color="#0a84ff" /> : <Text style={styles.loadMoreBtnText}>{t('app.brandDashboard.loadMorePosts')}</Text>}
                    </TouchableOpacity>
                ) : (
                    <Text style={styles.allLoadedText}>{t('app.brandDashboard.showingMostPosts', { count: posts.length, tab: t(`app.brandDashboard.${tab}`) })}</Text>
                )}
            </View>
        </SectionCard>
    );
};

// ============================================================================
// Styles
// ============================================================================

const getStyles = () => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: THEME.bg,
    },
    contentContainer: {
        maxWidth: 1280,
        width: '100%',
        alignSelf: 'center',
        padding: 28,
        paddingBottom: 100,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: THEME.bg,
    },
    loadingText: {
        color: THEME.textSecondary,
        fontSize: 14,
        fontWeight: '500',
        letterSpacing: 1,
        textTransform: 'uppercase',
    },
    pageHeader: {
        marginBottom: 36,
        position: 'relative',
        overflow: 'visible',
    },
    headerAmbientGlow: {
        position: 'absolute',
        top: -80,
        left: -60,
        width: 300,
        height: 200,
        borderRadius: 100,
        backgroundColor: 'rgba(10, 132, 255, 0.06)',
        filter: 'blur(60px)',
    },
    pageTitle: {
        color: THEME.textMain,
        fontSize: 30,
        fontWeight: '800',
        letterSpacing: -1,
    },
    pageSubtitle: {
        color: THEME.textTertiary,
        fontSize: 13,
        marginTop: 6,
        fontWeight: '500',
        letterSpacing: 0.5,
        textTransform: 'uppercase',
    },

    // Stats Row
    statsRow: {
        flexDirection: 'row',
        gap: 14,
        marginBottom: 28,
    },
    statCard: {
        backgroundColor: THEME.card,
        borderRadius: 20,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        position: 'relative',
        minHeight: 130,
        backdropFilter: 'blur(20px)',
    },
    statContent: {
        padding: 22,
        zIndex: 1,
    },
    statHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginBottom: 14,
    },
    statIconCircle: {
        width: 30,
        height: 30,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
    },
    statLabel: {
        color: THEME.textTertiary,
        fontSize: 11,
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: 1.2,
    },
    statValueContainer: {
        flexDirection: 'row',
        alignItems: 'baseline',
        gap: 6,
    },
    statSuffix: {
        color: THEME.textTertiary,
        fontSize: 13,
        fontWeight: '500',
    },
    statSecondaryContainer: {
        flexDirection: 'row',
        alignItems: 'baseline',
        gap: 4,
        marginTop: 6,
    },

    // Section Card
    sectionCard: {
        backgroundColor: THEME.card,
        borderRadius: 20,
        marginBottom: 20,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        overflow: 'hidden',
        backdropFilter: 'blur(16px)',
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 24,
        paddingTop: 22,
        paddingBottom: 14,
    },
    sectionTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    sectionTitle: {
        color: THEME.textMain,
        fontSize: 16,
        fontWeight: '700',
        letterSpacing: -0.2,
    },
    sectionContent: {
        paddingHorizontal: 24,
        paddingBottom: 24,
    },

    // Narrative Card
    narrativeCard: {
        backgroundColor: 'transparent',
        borderRadius: 14,
        marginBottom: 8,
        flexDirection: 'row',
        overflow: 'hidden',
    },
    narrativeContent: {
        flex: 1,
        padding: 14,
        paddingLeft: 16,
    },
    narrativeTop: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
        marginBottom: 10,
    },
    narrativeTitle: {
        color: THEME.textMain,
        fontSize: 14,
        fontWeight: '600',
        lineHeight: 20,
        flex: 1,
        letterSpacing: -0.1,
    },
    narrativeMeta: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    metaLeft: {
        flexDirection: 'row',
        gap: 6,
    },
    metaBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        backgroundColor: THEME.bgSurface,
        paddingHorizontal: 9,
        paddingVertical: 4,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: THEME.border,
    },
    metaRight: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    emotionBadge: {
        paddingHorizontal: 9,
        paddingVertical: 3,
        borderRadius: 6,
    },
    emotionBadgeText: {
        fontSize: 9,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 0.8,
    },

    // Warning Card
    warningCard: {
        backgroundColor: 'rgba(251, 191, 36, 0.04)',
        borderRadius: 14,
        padding: 16,
        marginBottom: 10,
        borderWidth: 1,
        borderColor: 'rgba(251, 191, 36, 0.08)',
    },

    // Hourly Chart
    hourlyChart: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        height: 120,
        gap: 4,
        marginTop: 10,
    },
    hourlyBarContainer: {
        flex: 1,
        alignItems: 'center',
        height: '100%',
        justifyContent: 'flex-end',
    },
    hourlyBarTrack: {
        width: '100%',
        height: '100%',
        justifyContent: 'flex-end',
        position: 'relative',
        borderRadius: 4,
        overflow: 'hidden',
    },
    hourlyBarIrrelevant: {
        width: '100%',
        backgroundColor: THEME.bgSurface,
        borderRadius: 4,
        position: 'absolute',
        bottom: 0,
    },
    hourlyBarRelevant: {
        width: '100%',
        backgroundColor: THEME.primary,
        borderRadius: 4,
        position: 'absolute',
        bottom: 0,
    },
    hourlyLabel: {
        color: THEME.textTertiary,
        fontSize: 10,
        marginTop: 8,
        fontWeight: '600',
    },
    sentimentDayLabel: {
        color: THEME.textTertiary,
        fontSize: 10,
        fontWeight: '600',
    },

    // Gauges
    gaugeTrack: {
        height: 6,
        backgroundColor: THEME.bgSurface,
        borderRadius: 3,
        overflow: 'hidden',
    },
    gaugeLabel: {
        color: THEME.textSecondary,
        fontSize: 13,
        fontWeight: '500',
        letterSpacing: -0.1,
    },
    subLabel: {
        color: THEME.textTertiary,
        fontSize: 11,
        fontWeight: '600',
        marginBottom: 12,
        marginTop: 6,
        textTransform: 'uppercase',
        letterSpacing: 1,
    },

    // Emotion Bars
    emotionRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 6,
    },
    emotionLabel: {
        width: 85,
        fontSize: 11,
        fontWeight: '600',
        textTransform: 'capitalize',
        letterSpacing: -0.1,
    },
    emotionBarTrack: {
        flex: 1,
        height: 6,
        backgroundColor: THEME.bgSurface,
        borderRadius: 3,
    },
    emotionBarFill: {
        height: '100%',
        borderRadius: 3,
    },
    emotionCount: {
        color: THEME.textTertiary,
        fontSize: 11,
        fontWeight: '600',
        width: 35,
        textAlign: 'right',
    },

    // Platform Sentiment
    platformSentimentCard: {
        backgroundColor: THEME.bgSurface,
        borderRadius: 16,
        padding: 16,
        marginBottom: 12,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
    },
    platformSentimentTrack: {
        height: 8,
        backgroundColor: THEME.bgSurface,
        borderRadius: 4,
        overflow: 'hidden',
        position: 'relative',
        marginTop: 4,
    },
    platformPostCountBadge: {
        backgroundColor: THEME.bgSurface,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 10,
    },
    platformPostCountText: {
        color: THEME.textSecondary,
        fontSize: 11,
        fontWeight: '700',
    },

    // Safety Alerts
    safetyAlert: {
        backgroundColor: 'rgba(255, 69, 58, 0.05)',
        borderRadius: 16,
        padding: 16,
        marginBottom: 12,
        borderWidth: 1,
        borderColor: 'rgba(255, 69, 58, 0.1)',
    },
    safetyAlertTitle: {
        color: THEME.error,
        fontSize: 15,
        fontWeight: '700',
    },
    safetyAlertReason: {
        color: THEME.textSecondary,
        fontSize: 13,
        marginTop: 6,
        lineHeight: 18,
    },
    // Platform Bars
    platformRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    platformLabel: {
        color: THEME.textSecondary,
        fontSize: 12,
        fontWeight: '500',
        textTransform: 'capitalize',
    },
    platformBarTrack: {
        flex: 1,
        height: 6,
        backgroundColor: THEME.bgSurface,
        borderRadius: 3,
        overflow: 'hidden',
    },
    platformBarFill: {
        height: '100%',
        borderRadius: 3,
    },
    platformPct: {
        color: THEME.textTertiary,
        fontSize: 12,
        fontWeight: '600',
        width: 40,
        textAlign: 'right',
    },

    // Sources List
    sourceRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 5,
    },
    sourceUsername: {
        color: THEME.textMain,
        fontSize: 13,
        fontWeight: '500',
        flex: 1,
    },
    sourceCount: {
        color: THEME.textTertiary,
        fontSize: 12,
        fontWeight: '500',
    },
    sourceFollowers: {
        color: THEME.textTertiary,
        fontSize: 11,
    },
    sourcePlatformLabel: {
        color: THEME.textSecondary,
        fontSize: 13,
        fontWeight: '600',
        textTransform: 'capitalize',
    },
    noDataText: {
        color: THEME.textTertiary,
        fontSize: 13,
        textAlign: 'center',
        paddingVertical: 20,
    },

    // Entity Chips
    entityCategoryLabel: {
        color: THEME.textTertiary,
        fontSize: 10,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 1.2,
        marginBottom: 8,
    },
    chipsContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
        marginBottom: 12,
    },
    chip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: THEME.bgSurface,
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: THEME.border,
    },
    chipText: {
        color: THEME.textMain,
        fontSize: 12,
        fontWeight: '500',
    },
    chipCount: {
        color: THEME.textTertiary,
        fontSize: 10,
        fontWeight: '600',
    },

    // Safety Alerts
    safetySection: {
        marginTop: 4,
    },

    // Stage Distribution
    stageBarContainer: {
        flexDirection: 'row',
        height: 6,
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: THEME.bgSurface,
    },
    stageSegment: {
        height: '100%',
    },
    stageLegend: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 14,
        marginTop: 10,
    },
    stageLegendItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
    },
    stageDot: {
        width: 7,
        height: 7,
        borderRadius: 4,
    },
    stageLegendText: {
        color: THEME.textTertiary,
        fontSize: 11,
        fontWeight: '500',
        textTransform: 'capitalize',
    },

    // Platform Sentiment Cards
    platformSentimentCard: {
        backgroundColor: THEME.surface,
        borderRadius: 14,
        padding: 16,
        marginBottom: 8,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
    },
    platformSentimentHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    platformSentimentInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    platformIconBox: {
        width: 30,
        height: 30,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
    },
    platformNameText: {
        color: THEME.textMain,
        fontSize: 13,
        fontWeight: '600',
        textTransform: 'capitalize',
        letterSpacing: 0.2,
    },
    platformSentimentMetrics: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    platformSentimentValue: {
        fontSize: 17,
        fontWeight: '700',
        fontVariant: ['tabular-nums'],
    },
    platformCountBadge: {
        backgroundColor: THEME.bgSurface,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: THEME.border,
    },
    platformCountText: {
        color: THEME.textTertiary,
        fontSize: 10,
        fontWeight: '700',
    },
    platformSentimentTrack: {
        height: 5,
        backgroundColor: THEME.bgSurface,
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
    },
    platformSentimentCenterLine: {
        position: 'absolute',
        left: '50%',
        top: 0,
        bottom: 0,
        width: 1,
        backgroundColor: 'rgba(255,255,255,0.12)',
    },
    platformSentimentFill: {
        height: '100%',
        borderRadius: 3,
    },

    // Extreme Posts Table
    tableHeaderControls: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        gap: 12,
    },
    tabContainer: {
        flexDirection: 'row',
        backgroundColor: THEME.bgSurface,
        padding: 3,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: THEME.border,
    },
    tabItem: {
        paddingHorizontal: 16,
        paddingVertical: 7,
        borderRadius: 8,
    },
    tabItemActiveNeg: {
        backgroundColor: 'rgba(248,113,113,0.12)',
        borderWidth: 1,
        borderColor: 'rgba(248,113,113,0.25)',
    },
    tabItemActivePos: {
        backgroundColor: 'rgba(52,211,153,0.12)',
        borderWidth: 1,
        borderColor: 'rgba(52,211,153,0.25)',
    },
    tabItemActiveBlue: {
        backgroundColor: 'rgba(10,132,255,0.12)',
        borderWidth: 1,
        borderColor: 'rgba(10,132,255,0.25)',
    },
    tabText: {
        color: THEME.textTertiary,
        fontSize: 13,
        fontWeight: '600',
    },
    batchAnalyzeBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: THEME.primary,
        paddingHorizontal: 18,
        paddingVertical: 10,
        borderRadius: 10,
        shadowColor: THEME.primary,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 12,
    },
    batchAnalyzeBtnText: {
        color: '#fff',
        fontSize: 13,
        fontWeight: '600',
    },
    tableHead: {
        flexDirection: 'row',
        paddingHorizontal: 12,
        paddingVertical: 11,
        backgroundColor: THEME.bgSurface,
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255,255,255,0.06)',
    },
    tableHeadCell: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
    },
    tableHeadText: {
        color: THEME.textTertiary,
        fontSize: 10,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 1,
    },
    sortIconContainer: {
        width: 14,
        alignItems: 'center',
    },
    rowWrapper: {
        borderBottomWidth: 1,
        borderBottomColor: THEME.border,
    },
    tableRow: {
        flexDirection: 'row',
        paddingHorizontal: 12,
        paddingVertical: 13,
        alignItems: 'center',
        backgroundColor: 'transparent',
    },
    tableRowExpanded: {
        backgroundColor: 'rgba(10,132,255,0.03)',
    },
    tableCell: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    rowContentText: {
        color: THEME.textMain,
        fontSize: 13,
        fontWeight: '400',
        flex: 1,
        opacity: 0.85,
    },
    rowAuthorText: {
        color: THEME.textSecondary,
        fontSize: 12,
        fontWeight: '500',
    },
    sentimentPill: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
        borderWidth: 1,
        minWidth: 50,
        alignItems: 'center',
    },
    sentimentPillText: {
        fontSize: 11,
        fontWeight: '700',
    },
    statusMiniBadge: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        minWidth: 80,
        alignItems: 'center',
    },
    statusMiniBadgeText: {
        fontSize: 10,
        fontWeight: '800',
        textTransform: 'uppercase',
    },
    rowAdviseBtn: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#0a84ff',
        minWidth: 70,
        alignItems: 'center',
    },
    rowAdviseBtnText: {
        color: '#0a84ff',
        fontSize: 11,
        fontWeight: '700',
    },
    detailDrawer: {
        backgroundColor: 'rgba(0,0,0,0.25)',
        padding: 24,
        borderLeftWidth: 2,
        borderLeftColor: THEME.primary,
        backdropFilter: 'blur(8px)',
    },
    detailGrid: {
        flexDirection: 'row',
        gap: 24,
    },
    detailSectionLeft: {
        flex: 1.2,
    },
    detailSectionRight: {
        flex: 1,
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255,255,255,0.08)',
        paddingLeft: 24,
    },
    detailUserRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16,
    },
    detailAvatar: {
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: 'rgba(255,255,255,0.1)',
    },
    detailUserName: {
        color: '#fff',
        fontSize: 16,
        fontWeight: '700',
    },
    detailUserHandle: {
        color: '#8e8e93',
        fontSize: 13,
    },
    detailFullText: {
        color: '#fff',
        fontSize: 15,
        lineHeight: 24,
        marginBottom: 16,
    },
    detailTranslateBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 20,
    },
    detailTranslateBtnText: {
        color: '#0a84ff',
        fontSize: 13,
        fontWeight: '600',
    },
    authorBioBox: {
        backgroundColor: THEME.bgSurface,
        padding: 12,
        borderRadius: 8,
    },
    authorBioLabel: {
        color: '#8e8e93',
        fontSize: 11,
        fontWeight: '700',
        textTransform: 'uppercase',
        marginBottom: 4,
    },
    authorBioText: {
        color: '#ccc',
        fontSize: 13,
        lineHeight: 18,
    },
    analysisCard: {
        backgroundColor: THEME.bgSurface,
        borderRadius: 14,
        padding: 18,
        borderWidth: 1,
        borderColor: THEME.border,
        backdropFilter: 'blur(8px)',
    },
    analysisHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    analysisActionBadge: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
    },
    analysisActionText: {
        fontSize: 12,
        fontWeight: '800',
        textTransform: 'uppercase',
    },
    analysisSeverityBadge: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 6,
    },
    analysisSeverityText: {
        fontSize: 10,
        fontWeight: '700',
        textTransform: 'uppercase',
    },
    analysisReasoning: {
        color: '#eee',
        fontSize: 14,
        lineHeight: 20,
        marginBottom: 16,
    },
    stepsContainer: {
        gap: 8,
        marginBottom: 20,
    },
    stepRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
    },
    stepDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: '#0a84ff',
        marginTop: 7,
    },
    stepText: {
        color: '#ccc',
        fontSize: 13,
        flex: 1,
    },
    statusPickerContainer: {
        marginTop: 'auto',
        paddingTop: 16,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.08)',
    },
    statusPickerLabel: {
        color: '#8e8e93',
        fontSize: 12,
        fontWeight: '700',
        marginBottom: 12,
    },
    statusOptions: {
        flexDirection: 'row',
        gap: 8,
    },
    statusOptionBtn: {
        flex: 1,
        paddingVertical: 8,
        alignItems: 'center',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.1)',
        backgroundColor: THEME.bgSurface,
    },
    statusOptionBtnActive: {
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderColor: '#fff',
    },
    statusOptionText: {
        color: '#8e8e93',
        fontSize: 11,
        fontWeight: '600',
        textTransform: 'capitalize',
    },
    pendingAnalysis: {
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        gap: 16,
    },
    pendingAnalysisText: {
        color: '#8e8e93',
        fontSize: 13,
        textAlign: 'center',
    },
    detailAnalyzeBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        backgroundColor: '#0a84ff',
        paddingHorizontal: 20,
        paddingVertical: 12,
        borderRadius: 10,
    },
    detailAnalyzeBtnText: {
        color: '#fff',
        fontSize: 14,
        fontWeight: '700',
    },
    tableFooter: {
        paddingVertical: 16,
        alignItems: 'center',
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.05)',
    },
    loadMoreBtn: {
        paddingHorizontal: 24,
        paddingVertical: 11,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: 'rgba(10,132,255,0.25)',
        backgroundColor: 'rgba(10,132,255,0.04)',
    },
    loadMoreBtnText: {
        color: THEME.primary,
        fontSize: 13,
        fontWeight: '600',
    },
    allLoadedText: {
        color: THEME.textTertiary,
        fontSize: 11,
        fontWeight: '500',
    },

    // Verified dot
    verifiedDot: { width: 8, height: 8, borderRadius: 4 },

    // Follower count in table row
    rowFollowerText: { color: THEME.textSecondary, fontSize: 12, fontWeight: '600' },

    // Detail follower count
    detailFollowerCount: { color: THEME.textSecondary, fontSize: 12, fontWeight: '600' },

    // Top Authors
    authorRow: {
        flexDirection: 'row', alignItems: 'center', gap: 12,
        paddingVertical: 10, borderBottomWidth: 1,
        borderBottomColor: 'rgba(255,255,255,0.05)',
    },
    authorAvatarFallback: {
        width: 40, height: 40, borderRadius: 20,
        backgroundColor: 'rgba(255,255,255,0.1)',
        alignItems: 'center', justifyContent: 'center',
    },
    authorAvatarLetter: { color: '#fff', fontSize: 16, fontWeight: '700' },
    authorName: { color: THEME.textMain, fontSize: 14, fontWeight: '600' },
    authorHandle: { color: THEME.textTertiary, fontSize: 12 },
    authorFollowers: { color: THEME.textSecondary, fontSize: 12 },
    authorPostCount: { color: THEME.textMain, fontSize: 13, fontWeight: '700', marginBottom: 4 },

    // Empty State
    emptyState: {
        alignItems: 'center',
        padding: 60,
    },
    emptyTitle: {
        color: THEME.textMain,
        fontSize: 20,
        fontWeight: '700',
        marginBottom: 10,
        letterSpacing: -0.3,
    },
    emptySubtitle: {
        color: '#8e8e93',
        fontSize: 14,
        textAlign: 'center',
        marginBottom: 20,
    },
    emptyButton: {
        backgroundColor: THEME.primary,
        paddingHorizontal: 24,
        paddingVertical: 11,
        borderRadius: 10,
    },
    emptyButtonText: {
        color: '#fff',
        fontWeight: '600',
        fontSize: 14,
    },

    // Error state
    errorText: {
        color: THEME.error,
        fontSize: 15,
        fontWeight: '500',
        marginBottom: 16,
    },
    retryButton: {
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 10,
        backgroundColor: 'rgba(10,132,255,0.12)',
        borderWidth: 1,
        borderColor: 'rgba(10,132,255,0.25)',
    },
    retryText: {
        color: THEME.primary,
        fontSize: 14,
        fontWeight: '600',
    },

    // Badge (Warning Card)
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        backgroundColor: THEME.bgSurface,
        paddingHorizontal: 9,
        paddingVertical: 4,
        borderRadius: 8,
    },
    badgeText: {
        color: THEME.textSecondary,
        fontSize: 11,
        fontWeight: '600',
    },
    narrativeHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
        marginBottom: 10,
    },

    // Layout
    twoColumn: {
        flexDirection: 'row',
        gap: 16,
    },
    column: {
        flex: 1,
        width: '50%',
    },
});

let styles = getStyles();

export default BrandDashboardScreen;
